// src/rng.ts
import { bytesToLanes } from "@mj/protocol";
var rotl = (x, k) => (x << k | x >>> 32 - k) >>> 0;
function makeRngFromLanes(init) {
  let [s0, s1, s2, s3] = init;
  const nextU32 = () => {
    const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
    const t = s1 << 9 >>> 0;
    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotl(s3, 11);
    return result;
  };
  const nextBelow = (n) => {
    if (n <= 0) throw new Error("n must be > 0");
    const limit = 4294967296 - 4294967296 % n;
    let x = nextU32();
    while (x >= limit) x = nextU32();
    return x % n;
  };
  return { nextU32, nextBelow, state: () => [s0, s1, s2, s3] };
}
function makeRng(seed) {
  return makeRngFromLanes(bytesToLanes(seed));
}

// src/wall.ts
function shuffle(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.nextBelow(i + 1);
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

// src/determinism.ts
import { hexToSeed, fnv1a32, stableStringify, ALL_TILE_IDS } from "@mj/protocol";
var DETERMINISM_SEED_HEX = "0123456789abcdef".repeat(4);
function determinismDigest() {
  const rng = makeRng(hexToSeed(DETERMINISM_SEED_HEX));
  const stream = Array.from({ length: 64 }, () => rng.nextU32());
  const wall = shuffle(
    Array.from({ length: 136 }, (_, i) => i),
    rng
  );
  const coreTileCount = ALL_TILE_IDS.filter((t) => t[0] !== "f").length;
  return fnv1a32(stableStringify({ tiles: coreTileCount, stream, wall }));
}

// src/gamelength.ts
function effectiveGameLength(rs) {
  return rs.gameLength ?? "infinite";
}
function scheduledKyoku(rs) {
  const n = rs.players;
  switch (effectiveGameLength(rs)) {
    case "single":
      return 1;
    case "tonpuu":
      return n;
    case "hanchan":
      return 2 * n;
    case "infinite":
      return Infinity;
  }
}
function roundWindForKyoku(kyoku, players) {
  const WINDS2 = ["z1", "z2", "z3", "z4"];
  const roundIndex = Math.floor(kyoku / players);
  return WINDS2[roundIndex % 4];
}
function kyokuRemaining(rs, kyoku) {
  const total = scheduledKyoku(rs);
  if (total === Infinity) return Infinity;
  return Math.max(0, total - kyoku - 1);
}
function isAllLast(rs, kyoku) {
  const total = scheduledKyoku(rs);
  if (total === Infinity) return false;
  return kyoku === total - 1;
}

// src/aiscalar.ts
function pushScalar(inp) {
  const { scores, seat, kyoku, rs } = inp;
  if (scores.length <= 1) return 0;
  const own = scores[seat] ?? 0;
  let bestOther = -Infinity;
  for (let i = 0; i < scores.length; i++) {
    if (i === seat) continue;
    const s = scores[i] ?? 0;
    if (s > bestOther) bestOther = s;
  }
  if (bestOther === -Infinity) {
    return 0;
  }
  const margin = own - bestOther;
  const REF = 8e3;
  const scoreFactor = -margin / REF;
  const gl = effectiveGameLength(rs);
  let urgency;
  if (gl === "infinite") {
    urgency = 0;
  } else {
    const rem = kyokuRemaining(rs, kyoku);
    urgency = 1 / (rem + 1);
  }
  const raw = scoreFactor * urgency;
  const clamped = Math.max(-1, Math.min(1, raw));
  return clamped === 0 ? 0 : clamped;
}

// src/defense.ts
import { baseOfAka as baseOfAka2, isHonor as isHonor2 } from "@mj/protocol";

// src/tiles.ts
import { baseOfAka, compareTiles, isHonor } from "@mj/protocol";
function tileKey(t) {
  const base = baseOfAka(t);
  const s = base[0];
  const suit = s === "m" ? 0 : s === "p" ? 1 : s === "s" ? 2 : 3;
  return { suit, rank: Number(base.slice(1)) };
}
function handCounts(tiles) {
  const m = /* @__PURE__ */ new Map();
  for (const t of tiles) {
    const base = baseOfAka(t);
    m.set(base, (m.get(base) ?? 0) + 1);
  }
  const sorted = [...m.entries()].sort((a, b) => compareTiles(a[0], b[0]));
  return new Map(sorted);
}
function visibleCounts(tiles) {
  const c = new Array(34).fill(0);
  for (const t of tiles) {
    if (t[0] === "f") continue;
    c[tile34(baseOfAka(t))]++;
  }
  return c;
}
function sortTiles(tiles) {
  return [...tiles].sort(compareTiles);
}
var YAOCHUU = ["m1", "m9", "p1", "p9", "s1", "s9", "z1", "z2", "z3", "z4", "z5", "z6", "z7"];
function isTerminal(t) {
  const k = tileKey(t);
  return k.suit !== 3 && (k.rank === 1 || k.rank === 9);
}
function isYaochuu(t) {
  return isHonor(t) || isTerminal(t);
}
function tile34(t) {
  const k = tileKey(t);
  return k.suit * 9 + (k.rank - 1);
}
var BASE_BY_34 = (() => {
  const out = [];
  for (const s of ["m", "p", "s"]) for (let r = 1; r <= 9; r++) out.push(`${s}${r}`);
  for (let r = 1; r <= 7; r++) out.push(`z${r}`);
  return out;
})();
function from34(i) {
  const t = BASE_BY_34[i];
  if (t === void 0) throw new Error(`bad tile34 index ${i}`);
  return t;
}

// src/defense.ts
function isGenbutsu(tile, river) {
  const base = baseOfAka2(tile);
  return river.some((e) => baseOfAka2(e.tile) === base);
}
function safeTilesVs(river) {
  const s = /* @__PURE__ */ new Set();
  for (const e of river) {
    s.add(baseOfAka2(e.tile));
  }
  return s;
}
function isSuji(tile, river) {
  if (isHonor2(tile)) return false;
  const base = baseOfAka2(tile);
  const suit = base[0];
  const rank = Number(base.slice(1));
  const riverBases = /* @__PURE__ */ new Set();
  for (const e of river) {
    const b = baseOfAka2(e.tile);
    riverBases.add(b);
  }
  const lowerPartner = rank - 3;
  const upperPartner = rank + 3;
  if (lowerPartner >= 1 && riverBases.has(`${suit}${lowerPartner}`)) return true;
  if (upperPartner <= 9 && riverBases.has(`${suit}${upperPartner}`)) return true;
  return false;
}
function isKabe(tile, seen) {
  if (isHonor2(tile)) return false;
  const base = baseOfAka2(tile);
  const suit = base[0];
  const rank = Number(base.slice(1));
  function adjWalled(adjRank) {
    if (adjRank < 1 || adjRank > 9) return false;
    const adjTile = `${suit}${adjRank}`;
    const idx = tile34(adjTile);
    return (seen[idx] ?? 0) >= 4;
  }
  return adjWalled(rank - 1) || adjWalled(rank + 1);
}
function classifySafety(tile, river, seen) {
  if (isGenbutsu(tile, river)) return "genbutsu";
  if (isKabe(tile, seen)) return "kabe";
  if (isSuji(tile, river)) return "suji";
  return "unknown";
}

// src/parser.ts
import { baseOfAka as baseOfAka3, compareTiles as compareTiles2 } from "@mj/protocol";
function toCounts(tiles) {
  const c = new Array(34).fill(0);
  for (const t of tiles) c[tile34(baseOfAka3(t))]++;
  return c;
}
function countsKey(c) {
  let s = "";
  for (let i = 0; i < 34; i++) s += String(c[i]);
  return s;
}
function meldToGroup(meld) {
  const open = meld.kind !== "ankan";
  const kind = meld.kind === "chii" ? "sequence" : meld.kind === "pon" ? "triplet" : "quad";
  return { kind, tiles: meld.tiles.map(baseOfAka3), open };
}
function groupMelds(melds) {
  return melds.filter((mm) => mm.kind !== "kita");
}
function decomposeStandard(counts, needGroups) {
  const results = [];
  const c = counts.slice();
  for (let p = 0; p < 34; p++) {
    if (c[p] < 2) continue;
    c[p] -= 2;
    const groupsList = decomposeGroups(c, needGroups);
    for (const groups of groupsList) results.push({ groups, pair: from34(p) });
    c[p] += 2;
  }
  return dedupeDecompositions(results);
}
function decomposeGroups(c, need) {
  if (need === 0) {
    for (let i2 = 0; i2 < 34; i2++) if (c[i2] !== 0) return [];
    return [[]];
  }
  let i = 0;
  while (i < 34 && c[i] === 0) i++;
  if (i === 34) return [];
  const out = [];
  if (c[i] >= 3) {
    c[i] -= 3;
    for (const rest of decomposeGroups(c, need - 1)) {
      out.push([{ kind: "triplet", tiles: [from34(i), from34(i), from34(i)], open: false }, ...rest]);
    }
    c[i] += 3;
  }
  const suit = Math.floor(i / 9);
  const rank = i % 9;
  if (suit < 3 && rank <= 6 && c[i] >= 1 && c[i + 1] >= 1 && c[i + 2] >= 1) {
    c[i]--;
    c[i + 1]--;
    c[i + 2]--;
    for (const rest of decomposeGroups(c, need - 1)) {
      out.push([{ kind: "sequence", tiles: [from34(i), from34(i + 1), from34(i + 2)], open: false }, ...rest]);
    }
    c[i]++;
    c[i + 1]++;
    c[i + 2]++;
  }
  return out;
}
function groupSignature(g) {
  return g.kind + ":" + g.tiles.join(",");
}
function dedupeDecompositions(list) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const d of list) {
    const sig = d.pair + "|" + d.groups.map(groupSignature).slice().sort().join("|");
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push(d);
  }
  out.sort((a, b) => {
    const ka = a.pair + "|" + a.groups.map(groupSignature).sort().join("|");
    const kb = b.pair + "|" + b.groups.map(groupSignature).sort().join("|");
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  return out;
}
function classifyWait(groups, pair, winTile) {
  const w = baseOfAka3(winTile);
  const wIdx = tile34(w);
  const candidates = [];
  if (groups.some((g) => g.kind === "triplet" && tile34(g.tiles[0]) === wIdx)) {
    candidates.push("shanpon");
  }
  for (const g of groups) {
    if (g.kind !== "sequence") continue;
    const idxs = g.tiles.map((t) => tile34(t));
    if (!idxs.includes(wIdx)) continue;
    const lo = idxs[0];
    const rank = lo % 9;
    if (wIdx === idxs[1]) {
      candidates.push("kanchan");
    } else if (wIdx === lo) {
      candidates.push(rank === 6 ? "penchan" : "ryanmen");
    } else {
      candidates.push(rank === 0 ? "penchan" : "ryanmen");
    }
  }
  if (pair !== void 0 && tile34(pair) === wIdx) candidates.push("tanki");
  if (candidates.length === 0) return "none";
  const order = ["ryanmen", "shanpon", "kanchan", "penchan", "tanki", "none"];
  for (const o of order) if (candidates.includes(o)) return o;
  return "none";
}
function isChiitoi(counts) {
  let pairs = 0;
  for (let i = 0; i < 34; i++) {
    if (counts[i] === 0) continue;
    if (counts[i] === 2) {
      pairs++;
      continue;
    }
    return false;
  }
  return pairs === 7;
}
function isKokushi(counts) {
  let distinct = 0;
  let pairIdx = -1;
  for (let i = 0; i < 34; i++) {
    if (counts[i] === 0) continue;
    if (!isYaochuu(from34(i))) return { ok: false, pairIdx: -1 };
    distinct++;
    if (counts[i] >= 2) {
      if (pairIdx !== -1 || counts[i] > 2) return { ok: false, pairIdx: -1 };
      pairIdx = i;
    }
  }
  return { ok: distinct === 13 && pairIdx !== -1, pairIdx };
}
var parseMemo = /* @__PURE__ */ new Map();
function parseAgari(concealed, melds, winTile, winBy) {
  concealed = concealed.filter((t) => t[0] !== "f");
  const gMelds = groupMelds(melds);
  const counts = toCounts(concealed);
  const memoKey = countsKey(counts) + "|" + gMelds.map((mm) => mm.kind + mm.tiles.map(baseOfAka3).join("")).join(",") + "|" + baseOfAka3(winTile) + "|" + winBy;
  const cached = parseMemo.get(memoKey);
  if (cached) return cached.map((p) => ({ ...p, melds }));
  const concealedNonMeld = concealed;
  const meldGroups = gMelds.map(meldToGroup);
  const concealedConcealed = gMelds.every((mm) => mm.kind === "ankan");
  const concealedFlag = gMelds.length === 0 || concealedConcealed;
  const out = [];
  if (gMelds.length === 0 && concealedNonMeld.length === 14 && isChiitoi(counts)) {
    out.push({
      form: "chiitoi",
      groups: [],
      pair: void 0,
      melds,
      winTile,
      winBy,
      concealed: true,
      wait: "tanki"
      // chiitoi is always a single (pair) wait
    });
  }
  if (gMelds.length === 0 && concealedNonMeld.length === 14) {
    const k = isKokushi(counts);
    if (k.ok) {
      out.push({
        form: "kokushi",
        groups: [],
        pair: { tiles: [from34(k.pairIdx), from34(k.pairIdx)] },
        melds,
        winTile,
        winBy,
        concealed: true,
        // 13-wait (juusan-men) when winTile is the singleton-completed one i.e. the hand had 13
        // distinct before the win; otherwise a single (tanki) wait. We classify as tanki here and
        // let the yaku engine (E2) detect the 13-sided variant from context.
        wait: "tanki"
      });
    }
  }
  const needGroups = 4 - gMelds.length;
  if (needGroups >= 0 && needGroups <= 4) {
    const decomps = decomposeStandard(counts, needGroups);
    for (const d of decomps) {
      const concealedGroups = d.groups;
      const groups = [...meldGroups, ...concealedGroups];
      const pair = { tiles: [d.pair, d.pair] };
      out.push({
        form: "standard",
        groups,
        pair,
        melds,
        winTile,
        winBy,
        concealed: concealedFlag,
        wait: classifyWait(concealedGroups, d.pair, winTile)
      });
    }
  }
  out.sort((a, b) => {
    const ka = parseSortKey(a);
    const kb = parseSortKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  parseMemo.set(memoKey, out.map((p) => ({ ...p, melds: [] })));
  return out;
}
function parseSortKey(p) {
  const groups = p.groups.map(groupSignature).slice().sort().join("|");
  const pair = p.pair ? p.pair.tiles.join(",") : "";
  return p.form + "#" + pair + "#" + groups + "#" + p.wait;
}
function shanten(concealed, melds) {
  const gMelds = groupMelds(melds);
  const counts = toCounts(concealed.filter((t) => t[0] !== "f"));
  const meldCount = gMelds.length;
  let best = standardShanten(counts, meldCount);
  if (meldCount === 0) {
    best = Math.min(best, chiitoiShanten(counts), kokushiShanten(counts));
  }
  return best;
}
var standardShantenMemo = /* @__PURE__ */ new Map();
function standardShanten(counts, meldCount) {
  const key = countsKey(counts) + "|" + meldCount;
  const cached = standardShantenMemo.get(key);
  if (cached !== void 0) return cached;
  const need = 4 - meldCount;
  const c = counts.slice();
  let best = 8;
  const search = (i, sets, partials, pairs) => {
    while (i < 34 && c[i] === 0) i++;
    if (i === 34) {
      best = Math.min(best, evalShanten(need, sets, partials, pairs));
      return;
    }
    const suit = Math.floor(i / 9);
    const rank = i % 9;
    if (c[i] >= 3) {
      c[i] -= 3;
      search(i, sets + 1, partials, pairs);
      c[i] += 3;
    }
    if (suit < 3 && rank <= 6 && c[i] >= 1 && c[i + 1] >= 1 && c[i + 2] >= 1) {
      c[i]--;
      c[i + 1]--;
      c[i + 2]--;
      search(i, sets + 1, partials, pairs);
      c[i]++;
      c[i + 1]++;
      c[i + 2]++;
    }
    if (c[i] >= 2) {
      c[i] -= 2;
      search(i, sets, partials, pairs + 1);
      c[i] += 2;
    }
    if (suit < 3 && rank <= 7 && c[i] >= 1 && c[i + 1] >= 1) {
      c[i]--;
      c[i + 1]--;
      search(i, sets, partials + 1, pairs);
      c[i]++;
      c[i + 1]++;
    }
    if (suit < 3 && rank <= 6 && c[i] >= 1 && c[i + 2] >= 1) {
      c[i]--;
      c[i + 2]--;
      search(i, sets, partials + 1, pairs);
      c[i]++;
      c[i + 2]++;
    }
    c[i]--;
    search(i, sets, partials, pairs);
    c[i]++;
  };
  search(0, 0, 0, 0);
  standardShantenMemo.set(key, best);
  return best;
}
function evalShanten(need, sets, partials, pairs) {
  let hasPair = pairs > 0 ? 1 : 0;
  let extraPairBlocks = pairs - hasPair;
  let blocks = partials + extraPairBlocks;
  if (sets + blocks > need) blocks = need - sets;
  if (blocks < 0) blocks = 0;
  return need * 2 - sets * 2 - blocks - hasPair;
}
function chiitoiShanten(counts) {
  let pairs = 0;
  let kinds = 0;
  for (let i = 0; i < 34; i++) {
    if (counts[i] >= 1) kinds++;
    if (counts[i] >= 2) pairs++;
  }
  return 6 - pairs + Math.max(0, 7 - kinds);
}
function kokushiShanten(counts) {
  let kinds = 0;
  let hasPair = 0;
  for (const t of YAOCHUU) {
    const n = counts[tile34(t)];
    if (n >= 1) kinds++;
    if (n >= 2) hasPair = 1;
  }
  return 13 - kinds - hasPair;
}
function tenpaiWaits(concealed, melds) {
  if (shanten(concealed, melds) !== 0) return [];
  const out = [];
  const counts = toCounts(concealed);
  for (const m of melds) for (const t of m.tiles) counts[tile34(baseOfAka3(t))]++;
  for (let i = 0; i < 34; i++) {
    if (counts[i] >= 4) continue;
    const t = from34(i);
    if (shanten([...concealed, t], melds) === -1) out.push(t);
  }
  return out.sort(compareTiles2);
}

// src/ukeire.ts
import { baseOfAka as baseOfAka4, compareTiles as compareTiles3 } from "@mj/protocol";
function ukeire(concealed, melds = [], seen) {
  const base = concealed.filter((t) => t[0] !== "f");
  const seenVec = seen ?? visibleCounts(base);
  const distinct = /* @__PURE__ */ new Map();
  for (const t of base) {
    const b = baseOfAka4(t);
    distinct.set(tile34(b), b);
  }
  const rows = [];
  for (const [, d] of distinct) {
    const remainder = removeOne(base, d);
    const rs = shanten(remainder, melds);
    const remCounts = new Array(34).fill(0);
    for (const t of remainder) remCounts[tile34(baseOfAka4(t))]++;
    const accept = [];
    let count = 0;
    for (let i = 0; i < 34; i++) {
      if (remCounts[i] >= 4) continue;
      const t = from34(i);
      if (shanten([...remainder, t], melds) < rs) {
        accept.push(t);
        count += Math.max(0, 4 - (seenVec[i] ?? 0));
      }
    }
    accept.sort(compareTiles3);
    rows.push({ discard: d, shanten: rs, accept, count });
  }
  rows.sort((a, b) => a.shanten - b.shanten || b.count - a.count || compareTiles3(a.discard, b.discard));
  return rows;
}
function removeOne(tiles, d) {
  let dropped = false;
  return tiles.filter((t) => !dropped && baseOfAka4(t) === d ? (dropped = true, false) : true);
}

// src/settle.ts
import { nSymmetricUma } from "@mj/protocol";
function finishOrder(scores) {
  return scores.map((score2, seat) => ({ score: score2, seat })).sort((a, b) => b.score - a.score || a.seat - b.seat).map((x) => x.seat);
}
function placementRanks(scores) {
  const order = finishOrder(scores);
  const rank = new Array(scores.length).fill(0);
  order.forEach((seat, r) => {
    rank[seat] = r;
  });
  return rank;
}
function settleUma(scores, rs) {
  const n = scores.length;
  const table = rs.points.uma.length === n ? rs.points.uma : nSymmetricUma(n);
  const ranks = placementRanks(scores);
  return ranks.map((r) => table[r] ?? 0);
}
var FUKUROKU_RANK_INDEPENDENT = true;
var DAN_FLOOR = { jakuho: 5, jakudo: 5 };
var DAN_PT_4 = {
  jakuho: [60, 30, 0, 0],
  jakudo: [60, 30, 0, -10],
  jakusho: [50, 20, -10, -40],
  jakuryu: [50, 10, -20, -60],
  jakuou: [40, 0, -30, -80],
  tenjaku: [30, 0, -40, -120]
};
function danColumn3(tier) {
  const c = DAN_PT_4[tier];
  return [c[0], c[1], c[3]];
}
function danPointDelta(rank, players, tier) {
  let d;
  if (players === 4) d = DAN_PT_4[tier][rank] ?? 0;
  else if (players === 3) d = danColumn3(tier)[rank] ?? 0;
  else {
    const c = DAN_PT_4[tier];
    const top = c[0], bottom = c[3];
    d = Math.round(top + (bottom - top) * rank / (players - 1));
  }
  return Math.max(d, DAN_FLOOR[tier] ?? -Infinity);
}
var PAYOUT_WEIGHTS = {
  3: [3, 1, 0],
  4: [20, 7, 3, 0],
  5: [20, 9, 4, 2, 0],
  6: [20, 10, 6, 3, 1, 0],
  7: [20, 11, 7, 4, 2, 1, 0]
};
function defaultWeights(players) {
  return Array.from({ length: players }, (_, r) => players - 1 - r);
}
function rankedPayoutMultipliers(players, holdPct) {
  const w = PAYOUT_WEIGHTS[players] ?? defaultWeights(players);
  const target = players * (1 - holdPct);
  const wsum = w.reduce((a, b) => a + b, 0);
  return w.map((x) => Math.round(x / wsum * target * 1e9) / 1e9);
}
function rankedEntryPayout(entryFee, rank, players, holdPct) {
  return Math.round(entryFee * (rankedPayoutMultipliers(players, holdPct)[rank] ?? 0));
}
function houseHold(entryFee, players, holdPct) {
  const sum = rankedPayoutMultipliers(players, holdPct).reduce((a, b) => a + b, 0);
  return Math.round(entryFee * (players - sum));
}
function practiceSettleMode() {
  return { mode: "casual" };
}
function settleMatch(finalScores, rs, opts) {
  const ranks = placementRanks(finalScores);
  const uma = settleUma(finalScores, rs);
  const n = finalScores.length;
  const seats = [];
  for (let seat = 0; seat < n; seat++) {
    const rank = ranks[seat];
    const base = { seat, rank, umaPts: uma[seat], enDelta: 0 };
    if (opts.mode === "ranked") {
      base.danPtDelta = danPointDelta(rank, n, opts.tier);
      base.enDelta = rankedEntryPayout(opts.entryFee, rank, n, opts.holdPct) - opts.entryFee;
    } else if (opts.mode === "friendlyWager") {
      const avg = finalScores.reduce((a, b) => a + b, 0) / n;
      base.enDelta = Math.round((finalScores[seat] - avg) * opts.enPerPoint);
    }
    seats.push(base);
  }
  return { mode: opts.mode, seats };
}

// src/eventfuda.ts
function defaultEventFudaCaps() {
  return {
    perGrantMax: 3,
    annualMax: 12,
    annualWindowMs: 365 * 24 * 60 * 60 * 1e3,
    channelPolicy: {
      event: { countsAgainstAnnual: true },
      anniversary: { countsAgainstAnnual: true },
      apology: { countsAgainstAnnual: false }
    }
  };
}
function eventFudaGrantedInWindow(ledger, channel, nowMs, windowMs) {
  const cutoff = nowMs - windowMs;
  return ledger.grants.filter((g) => g.channel === channel && g.atMs > cutoff).reduce((sum, g) => sum + g.fuda, 0);
}
function clampEventFudaGrant(proposed, ledger, caps, nowMs) {
  let granted = proposed.fuda;
  let reason = "ok";
  if (granted > caps.perGrantMax) {
    granted = caps.perGrantMax;
    reason = "perGrantCapped";
  }
  const isDuplicate = ledger.grants.some((g) => g.campaignId === proposed.campaignId);
  if (isDuplicate) {
    return { granted: 0, reason: "duplicateCampaign" };
  }
  const policy = caps.channelPolicy[proposed.channel];
  const countsAgainstAnnual = policy ? policy.countsAgainstAnnual : true;
  if (countsAgainstAnnual) {
    const alreadyUsed = eventFudaGrantedInWindow(
      ledger,
      proposed.channel,
      nowMs,
      caps.annualWindowMs
    );
    const remaining = Math.max(0, caps.annualMax - alreadyUsed);
    if (granted > remaining) {
      granted = remaining;
      if (reason === "ok") reason = "annualCapped";
    }
  }
  return { granted, reason };
}
function applyEventFudaGrant(ledger, proposed, caps, nowMs) {
  const { granted, reason } = clampEventFudaGrant(proposed, ledger, caps, nowMs);
  if (granted <= 0) {
    return { ledger, granted: 0, reason };
  }
  const newGrant = { ...proposed, fuda: granted };
  return {
    ledger: { grants: [...ledger.grants, newGrant] },
    granted,
    reason
  };
}

// src/relief.ts
var DAN_LADDER = ["jakuho", "jakudo", "jakusho", "jakuryu", "jakuou", "tenjaku"];
var RUNG_INDEX = Object.fromEntries(
  DAN_LADDER.map((r, i) => [r, i])
);
var BOTTOM_FLOOR_RUNGS = /* @__PURE__ */ new Set(["jakuho", "jakudo"]);
var RUNG_START_PT = {
  jakuho: 0,
  jakudo: 0,
  jakusho: 30,
  jakuryu: 30,
  jakuou: 30,
  tenjaku: 30
};
function reliefCostFor(rung, _currentPt) {
  if (BOTTOM_FLOOR_RUNGS.has(rung)) {
    return { kind: "none-bottom-floor" };
  }
  const idx = RUNG_INDEX[rung];
  const toRung = DAN_LADDER[idx - 1];
  return {
    kind: "demotion",
    fromRung: rung,
    toRung,
    ptResetTo: RUNG_START_PT[toRung]
  };
}
function reliefEligibility(balanceEn, rung, state, bounds, nowMs) {
  if (balanceEn >= 0) {
    return { eligible: false, reason: "balance-nonnegative" };
  }
  if (state.lastGrantAtMs !== null && nowMs - state.lastGrantAtMs < bounds.cooldownMs) {
    return { eligible: false, reason: "cooldown" };
  }
  const isBottomFloor = BOTTOM_FLOOR_RUNGS.has(rung);
  if (isBottomFloor) {
    const windowActive = state.windowStartMs !== null && nowMs - state.windowStartMs < bounds.bottomFloorWindowMs;
    const claimsInWindow = windowActive ? state.bottomFloorClaimsInWindow : 0;
    if (claimsInWindow >= bounds.bottomFloorMaxClaims) {
      return { eligible: false, reason: "bottom-floor-exhausted" };
    }
  }
  const debt = -balanceEn;
  const grantEn = Math.min(debt, bounds.grantCap);
  const cost = reliefCostFor(rung);
  const nextState = {
    lastGrantAtMs: nowMs,
    bottomFloorClaimsInWindow: state.bottomFloorClaimsInWindow,
    windowStartMs: state.windowStartMs
  };
  if (isBottomFloor) {
    const windowActive = state.windowStartMs !== null && nowMs - state.windowStartMs < bounds.bottomFloorWindowMs;
    if (windowActive) {
      nextState.bottomFloorClaimsInWindow = state.bottomFloorClaimsInWindow + 1;
      nextState.windowStartMs = state.windowStartMs;
    } else {
      nextState.bottomFloorClaimsInWindow = 1;
      nextState.windowStartMs = nowMs;
    }
  }
  return { eligible: true, grantEn, cost, nextState };
}
function defaultReliefBounds(_rs) {
  return {
    grantCap: 500,
    cooldownMs: 7 * 24 * 60 * 60 * 1e3,
    bottomFloorMaxClaims: 3,
    bottomFloorWindowMs: 30 * 24 * 60 * 60 * 1e3
  };
}

// src/hounou.ts
var HOUNOU_MAX = 3;
var HOUNOU_COST_MULT = [3, 8, 20];
function expectedSatsuToPortrait(deckSize, daikichiCount = 1) {
  return (deckSize + 1) / (daikichiCount + 1);
}
function hounouCost(pastBestEntryFee, level) {
  const mult = HOUNOU_COST_MULT[level - 1];
  if (mult === void 0) throw new RangeError(`level must be 1..${HOUNOU_MAX}, got ${level}`);
  return Math.round(pastBestEntryFee * mult);
}
function hounouCostCumulative(pastBestEntryFee, levels) {
  let total = 0;
  for (let l = 1; l <= levels; l++) {
    total += hounouCost(pastBestEntryFee, l);
  }
  return total;
}
function hounouPlan(startDeck, daikichiCount = 1, pastBestEntryFee = 0) {
  const rows = [];
  let prevExpected = expectedSatsuToPortrait(startDeck, daikichiCount);
  for (let level = 0; level <= HOUNOU_MAX; level++) {
    const deckSize = startDeck - level;
    const expectedSatsu = expectedSatsuToPortrait(deckSize, daikichiCount);
    const deltaSatsu = level === 0 ? 0 : prevExpected - expectedSatsu;
    const cost = level === 0 ? 0 : hounouCost(pastBestEntryFee, level);
    const cumulativeCost = level === 0 ? 0 : hounouCostCumulative(pastBestEntryFee, level);
    rows.push({ level, deckSize, expectedSatsu, deltaSatsu, cost, cumulativeCost });
    prevExpected = expectedSatsu;
  }
  return rows;
}
function formatSatsuToPortrait(expectedSatsu) {
  return `\u3042\u3068\u7D04${expectedSatsu.toFixed(1)}\u672D\u3067\u7ACB\u3061\u7D75`;
}
function formatDeltaSatsu(deltaSatsu) {
  return `\u2212${deltaSatsu.toFixed(1)}\u672D`;
}

// src/yaku/dsl.ts
import { baseOfAka as baseOfAka5, isHonor as isHonor3 } from "@mj/protocol";
function allTilesOf(ph) {
  const out = [];
  for (const g of ph.groups) for (const t of g.tiles) out.push(baseOfAka5(t));
  if (ph.pair) for (const t of ph.pair.tiles) out.push(baseOfAka5(t));
  for (const mm of ph.melds) for (const t of mm.tiles) out.push(baseOfAka5(t));
  return out;
}
function honorClass(t) {
  if (!isHonor3(t)) return void 0;
  return Number(t.slice(1)) <= 4 ? "wind" : "dragon";
}
function numSuit(t) {
  if (isHonor3(t)) return void 0;
  return t[0];
}
function tileMatchesAllTilesIn(t, spec) {
  const honor = isHonor3(t);
  if (spec.honors !== void 0 && honor !== spec.honors) return false;
  if (spec.isYaochuu !== void 0 && isYaochuu(t) !== spec.isYaochuu) return false;
  if (spec.suit !== void 0) {
    if (honor) return false;
    const s = t[0];
    if (s !== spec.suit) return false;
  }
  if (spec.rankRange !== void 0) {
    if (honor) return false;
    const { rank } = tileKey(t);
    const [lo, hi] = spec.rankRange;
    if (rank < lo || rank > hi) return false;
  }
  return true;
}
function evalCtx(spec, ctx) {
  if (spec.firstTurn !== void 0 && ctx.firstTurn !== spec.firstTurn) return false;
  if (spec.tsumoOnly === true && ctx.winBy !== "tsumo") return false;
  if (spec.ronOnly === true && ctx.winBy !== "ron") return false;
  if (spec.menzenOnly === true && ctx.menzen !== true) return false;
  if (spec.afterKan !== void 0 && ctx.afterKan !== spec.afterKan) return false;
  if (spec.lastTile !== void 0 && ctx.lastTile !== spec.lastTile) return false;
  if (spec.nonDealer === true && ctx.isDealer !== false) return false;
  return true;
}
function evalConstraint(c, ph, ctx, tiles) {
  if ("form" in c) return ph.form === c.form;
  if ("allTilesIn" in c) {
    const ts = (tiles ?? allTilesOf(ph)).map(baseOfAka5);
    return ts.every((t) => tileMatchesAllTilesIn(t, c.allTilesIn));
  }
  if ("ctx" in c) return evalCtx(c.ctx, ctx);
  if ("groups" in c) {
    const f = c.groups;
    let count = 0;
    for (const g of ph.groups) {
      if (f.kind !== void 0) {
        if (f.kind === "set") {
          if (g.kind !== "triplet" && g.kind !== "quad") continue;
        } else if (g.kind !== f.kind) continue;
      }
      if (f.open !== void 0 && g.open !== f.open) continue;
      const rep = baseOfAka5(g.tiles[0]);
      if (f.suit !== void 0 && numSuit(rep) !== f.suit) continue;
      if (f.honor !== void 0) {
        if (g.kind === "sequence" || honorClass(rep) !== f.honor) continue;
      }
      count++;
    }
    if (count < (f.atLeast ?? 1)) return false;
    if (f.atMost !== void 0 && count > f.atMost) return false;
    return true;
  }
  if ("identicalSequences" in c) {
    const f = c.identicalSequences;
    const sigs = /* @__PURE__ */ new Map();
    for (const g of ph.groups) {
      if (g.kind !== "sequence") continue;
      const sig = g.tiles.map(baseOfAka5).join(",");
      sigs.set(sig, (sigs.get(sig) ?? 0) + 1);
    }
    let maxCount = 0;
    let pairs = 0;
    for (const n of sigs.values()) {
      if (n > maxCount) maxCount = n;
      pairs += Math.floor(n / 2);
    }
    if (f.atLeast !== void 0 && maxCount < f.atLeast) return false;
    if (f.pairsAtLeast !== void 0 && pairs < f.pairsAtLeast) return false;
    return true;
  }
  if ("consecutiveTriplets" in c) {
    const bySuit = { m: /* @__PURE__ */ new Set(), p: /* @__PURE__ */ new Set(), s: /* @__PURE__ */ new Set() };
    for (const g of ph.groups) {
      if (g.kind !== "triplet" && g.kind !== "quad") continue;
      const s = numSuit(baseOfAka5(g.tiles[0]));
      if (s === void 0) continue;
      bySuit[s].add(tileKey(baseOfAka5(g.tiles[0])).rank);
    }
    let best = 0;
    for (const s of ["m", "p", "s"]) {
      for (let start = 1; start <= 9; start++) {
        let run = 0;
        for (let r = start; r <= 9 && bySuit[s].has(r); r++) run++;
        if (run > best) best = run;
      }
    }
    return best >= c.consecutiveTriplets.atLeast;
  }
  if ("sameSequenceSuits" in c) {
    const byRank = /* @__PURE__ */ new Map();
    for (const g of ph.groups) {
      if (g.kind !== "sequence") continue;
      const lo = baseOfAka5(g.tiles[0]);
      const s = numSuit(lo);
      if (s === void 0) continue;
      const rank = tileKey(lo).rank;
      let set = byRank.get(rank);
      if (!set) byRank.set(rank, set = /* @__PURE__ */ new Set());
      set.add(s);
    }
    let best = 0;
    for (const set of byRank.values()) if (set.size > best) best = set.size;
    return best >= c.sameSequenceSuits.atLeast;
  }
  if ("consecutiveSequences" in c) {
    const bySuit = { m: /* @__PURE__ */ new Set(), p: /* @__PURE__ */ new Set(), s: /* @__PURE__ */ new Set() };
    for (const g of ph.groups) {
      if (g.kind !== "sequence") continue;
      const lo = baseOfAka5(g.tiles[0]);
      const s = numSuit(lo);
      if (s === void 0) continue;
      bySuit[s].add(tileKey(lo).rank);
    }
    let best = 0;
    for (const s of ["m", "p", "s"]) {
      for (let start = 1; start <= 7; start++) {
        let run = 0;
        for (let r = start; bySuit[s].has(r); r += 3) run++;
        if (run > best) best = run;
      }
    }
    return best >= c.consecutiveSequences.atLeast;
  }
  if ("everyGroupTouches" in c) {
    const f = c.everyGroupTouches;
    if (ph.groups.length === 0) return false;
    const tileOk = (t) => {
      if (f.terminal === true && !(numSuit(t) !== void 0 && (tileKey(t).rank === 1 || tileKey(t).rank === 9))) return false;
      if (f.honor === true && !isHonor3(t)) return false;
      if (f.yaochuu === true && !isYaochuu(t)) return false;
      return true;
    };
    return ph.groups.every((g) => g.tiles.some((raw) => tileOk(baseOfAka5(raw))));
  }
  if ("pair" in c) {
    if (!ph.pair) return false;
    const t = baseOfAka5(ph.pair.tiles[0]);
    const f = c.pair;
    if (f.honor !== void 0 && honorClass(t) !== f.honor) return false;
    if (f.isYaochuu !== void 0 && isYaochuu(t) !== f.isYaochuu) return false;
    if (f.suit !== void 0 && numSuit(t) !== f.suit) return false;
    if (f.rankRange !== void 0) {
      if (isHonor3(t)) return false;
      const { rank } = tileKey(t);
      if (rank < f.rankRange[0] || rank > f.rankRange[1]) return false;
    }
    return true;
  }
  if ("winTile" in c) {
    const t = baseOfAka5(ph.winTile);
    const f = c.winTile;
    if (f.in !== void 0 && !f.in.map(baseOfAka5).includes(t)) return false;
    if (f.isYaochuu !== void 0 && isYaochuu(t) !== f.isYaochuu) return false;
    if (f.suit !== void 0 && numSuit(t) !== f.suit) return false;
    if (f.rankRange !== void 0) {
      if (isHonor3(t)) return false;
      const { rank } = tileKey(t);
      if (rank < f.rankRange[0] || rank > f.rankRange[1]) return false;
    }
    return true;
  }
  if ("wait" in c) {
    return c.wait.in.includes(ph.wait);
  }
  if ("tilesSubsetOf" in c) {
    const allowed = new Set(c.tilesSubsetOf.tiles.map(baseOfAka5));
    const ts = (tiles ?? allTilesOf(ph)).map(baseOfAka5);
    return ts.length > 0 && ts.every((t) => allowed.has(t));
  }
  if ("suitsUsed" in c) {
    const f = c.suitsUsed;
    const ts = (tiles ?? allTilesOf(ph)).map(baseOfAka5);
    let m = false, p = false, s = false, wind = false, dragon = false;
    for (const t of ts) {
      const hc = honorClass(t);
      if (hc === "wind") wind = true;
      else if (hc === "dragon") dragon = true;
      else {
        const su = numSuit(t);
        if (su === "m") m = true;
        else if (su === "p") p = true;
        else if (su === "s") s = true;
      }
    }
    const numCount = (m ? 1 : 0) + (p ? 1 : 0) + (s ? 1 : 0);
    const hasHonor2 = wind || dragon;
    if (f.numberSuits === "one" && numCount !== 1) return false;
    if (f.honors === "required" && !hasHonor2) return false;
    if (f.honors === "forbidden" && hasHonor2) return false;
    if (f.gates === true && !(m && p && s && wind && dragon)) return false;
    return true;
  }
  if ("all" in c) return c.all.every((sub) => evalConstraint(sub, ph, ctx, tiles));
  if ("any" in c) return c.any.some((sub) => evalConstraint(sub, ph, ctx, tiles));
  if ("none" in c) return c.none.every((sub) => !evalConstraint(sub, ph, ctx, tiles));
  return false;
}

// src/yaku/builtin.ts
import { baseOfAka as baseOfAka6, isHonor as isHonor4 } from "@mj/protocol";
function isSeq(g) {
  return g.kind === "sequence";
}
function isSet(g) {
  return g.kind === "triplet" || g.kind === "quad";
}
function setRankTile(g) {
  return baseOfAka6(g.tiles[0]);
}
function seqLowTile(g) {
  return baseOfAka6(g.tiles[0]);
}
function allTiles(ph) {
  const out = [];
  for (const g of ph.groups) for (const t of g.tiles) out.push(baseOfAka6(t));
  if (ph.pair) for (const t of ph.pair.tiles) out.push(baseOfAka6(t));
  return out;
}
var DRAGONS = /* @__PURE__ */ new Set(["z5", "z6", "z7"]);
function isDragon(t) {
  return DRAGONS.has(baseOfAka6(t));
}
function groupTouchesYaochuu(g) {
  return g.tiles.some((t) => isYaochuu(baseOfAka6(t)));
}
function groupTouchesTerminal(g) {
  return g.tiles.some((t) => {
    const b = baseOfAka6(t);
    return !isHonor4(b) && (tileKey(b).rank === 1 || tileKey(b).rank === 9);
  });
}
function pairTile(ph) {
  return ph.pair ? baseOfAka6(ph.pair.tiles[0]) : void 0;
}
function isYakuhaiPairTile(t, ctx) {
  if (isDragon(t)) return true;
  if (ctx.roundWind && baseOfAka6(t) === baseOfAka6(ctx.roundWind)) return true;
  if (ctx.seatWind && baseOfAka6(t) === baseOfAka6(ctx.seatWind)) return true;
  return false;
}
var riichi = (_ph, ctx) => ctx.riichi ? 1 : 0;
var doubleRiichi = (_ph, ctx) => ctx.doubleRiichi ? 2 : 0;
var openRiichi = (_ph, ctx) => ctx.riichi && ctx.openRiichi ? 2 : 0;
var openDoubleRiichi = (_ph, ctx) => ctx.doubleRiichi && ctx.openRiichi ? 3 : 0;
var ippatsu = (_ph, ctx) => ctx.ippatsu ? 1 : 0;
var menzenTsumo = (ph, ctx) => ph.concealed && ctx.winBy === "tsumo" ? 1 : 0;
var haitei = (_ph, ctx) => ctx.lastTile === "haitei" && ctx.winBy === "tsumo" ? 1 : 0;
var houtei = (_ph, ctx) => ctx.lastTile === "houtei" && ctx.winBy === "ron" ? 1 : 0;
var rinshan = (_ph, ctx) => ctx.afterKan && ctx.winBy === "tsumo" ? 1 : 0;
var chankan = (_ph, ctx) => ctx.afterKan && ctx.winBy === "ron" ? 1 : 0;
var pinfu = (ph, ctx) => {
  if (ph.form !== "standard" || !ph.concealed) return 0;
  if (!ph.groups.every(isSeq)) return 0;
  if (ph.wait !== "ryanmen") return 0;
  const pt = pairTile(ph);
  if (pt === void 0 || isYakuhaiPairTile(pt, ctx)) return 0;
  return 1;
};
var tanyao = (ph, _ctx, rs, tiles) => {
  if (!ph.concealed && rs.flags.kuitan === false) return 0;
  const ts = tiles ?? allTiles(ph);
  return ts.length > 0 && ts.every((t) => !isYaochuu(t)) ? 1 : 0;
};
function identicalSeqPairs(ph) {
  const sigs = /* @__PURE__ */ new Map();
  for (const g of ph.groups) {
    if (!isSeq(g)) continue;
    const sig = g.tiles.map(baseOfAka6).join(",");
    sigs.set(sig, (sigs.get(sig) ?? 0) + 1);
  }
  let pairs = 0;
  for (const n of sigs.values()) pairs += Math.floor(n / 2);
  return pairs;
}
var iipeikou = (ph) => {
  if (ph.form !== "standard" || !ph.concealed) return 0;
  return identicalSeqPairs(ph) === 1 ? 1 : 0;
};
var ryanpeikou = (ph) => {
  if (ph.form !== "standard" || !ph.concealed) return 0;
  return identicalSeqPairs(ph) >= 2 ? 3 : 0;
};
function hasSetOf(ph, tile) {
  if (!tile) return false;
  const b = baseOfAka6(tile);
  return ph.groups.some((g) => isSet(g) && setRankTile(g) === b);
}
var yakuhaiRound = (ph, ctx) => hasSetOf(ph, ctx.roundWind) ? 1 : 0;
var yakuhaiSeat = (ph, ctx, rs) => {
  if (!hasSetOf(ph, ctx.seatWind)) return 0;
  if (rs.scoring.doubleWindTripletHan === 1 && ctx.roundWind && ctx.seatWind && baseOfAka6(ctx.roundWind) === baseOfAka6(ctx.seatWind) && hasSetOf(ph, ctx.roundWind)) {
    return 0;
  }
  return 1;
};
var yakuhaiHaku = (ph) => hasSetOf(ph, "z5") ? 1 : 0;
var yakuhaiHatsu = (ph) => hasSetOf(ph, "z6") ? 1 : 0;
var yakuhaiChun = (ph) => hasSetOf(ph, "z7") ? 1 : 0;
var yakuhaiJigen = (ph, ctx) => (ctx.seatDragon ?? null) && hasSetOf(ph, ctx.seatDragon ?? null) ? 1 : 0;
var sanshokuDoujun = (ph) => {
  const bySuit = { m: /* @__PURE__ */ new Set(), p: /* @__PURE__ */ new Set(), s: /* @__PURE__ */ new Set() };
  for (const g of ph.groups) {
    if (!isSeq(g)) continue;
    const lo = seqLowTile(g);
    const k = tileKey(lo);
    const suit = lo[0];
    if (suit === "m" || suit === "p" || suit === "s") bySuit[suit].add(k.rank);
  }
  for (let r = 1; r <= 7; r++) {
    if (bySuit["m"].has(r) && bySuit["p"].has(r) && bySuit["s"].has(r)) {
      return ph.concealed ? 2 : 1;
    }
  }
  return 0;
};
var sanshokuDoukou = (ph) => {
  const bySuit = { m: /* @__PURE__ */ new Set(), p: /* @__PURE__ */ new Set(), s: /* @__PURE__ */ new Set() };
  for (const g of ph.groups) {
    if (!isSet(g)) continue;
    const t = setRankTile(g);
    if (isHonor4(t)) continue;
    const suit = t[0];
    if (suit === "m" || suit === "p" || suit === "s") bySuit[suit].add(tileKey(t).rank);
  }
  for (let r = 1; r <= 9; r++) {
    if (bySuit["m"].has(r) && bySuit["p"].has(r) && bySuit["s"].has(r)) return 2;
  }
  return 0;
};
var ittsuu = (ph) => {
  const bySuit = { m: /* @__PURE__ */ new Set(), p: /* @__PURE__ */ new Set(), s: /* @__PURE__ */ new Set() };
  for (const g of ph.groups) {
    if (!isSeq(g)) continue;
    const lo = seqLowTile(g);
    const suit = lo[0];
    if (suit === "m" || suit === "p" || suit === "s") bySuit[suit].add(tileKey(lo).rank);
  }
  for (const suit of ["m", "p", "s"]) {
    const s = bySuit[suit];
    if (s.has(1) && s.has(4) && s.has(7)) return ph.concealed ? 2 : 1;
  }
  return 0;
};
var chanta = (ph) => {
  if (ph.form !== "standard") return 0;
  if (!ph.groups.some(isSeq)) return 0;
  if (!ph.groups.every(groupTouchesYaochuu)) return 0;
  const pt = pairTile(ph);
  if (pt === void 0 || !isYaochuu(pt)) return 0;
  return ph.concealed ? 2 : 1;
};
var junchan = (ph) => {
  if (ph.form !== "standard") return 0;
  if (!ph.groups.some(isSeq)) return 0;
  if (allTiles(ph).some((t) => isHonor4(t))) return 0;
  if (!ph.groups.every(groupTouchesTerminal)) return 0;
  const pt = pairTile(ph);
  if (pt === void 0 || isHonor4(pt) || !(tileKey(pt).rank === 1 || tileKey(pt).rank === 9)) return 0;
  return ph.concealed ? 3 : 2;
};
var honroutou = (ph, _ctx, _rs, tiles) => {
  if (ph.form === "kokushi") return 0;
  const ts = tiles ?? allTiles(ph);
  return ts.length > 0 && ts.every((t) => isYaochuu(t)) ? 2 : 0;
};
var toitoi = (ph) => {
  if (ph.form !== "standard") return 0;
  return ph.groups.every(isSet) ? 2 : 0;
};
var sanankou = (ph, ctx) => {
  if (ph.form !== "standard") return 0;
  let concealedSets = ph.groups.filter((g) => isSet(g) && !g.open).length;
  if (ctx.winBy === "ron" && ph.wait === "shanpon") {
    const w = baseOfAka6(ph.winTile);
    const completesConcealedTriplet = ph.groups.some(
      (g) => isSet(g) && !g.open && setRankTile(g) === w
    );
    if (completesConcealedTriplet) concealedSets -= 1;
  }
  return concealedSets >= 3 ? 2 : 0;
};
var sankantsu = (ph) => {
  const kans = ph.groups.filter((g) => g.kind === "quad").length;
  return kans >= 3 ? 2 : 0;
};
var shousangen = (ph) => {
  if (ph.form !== "standard") return 0;
  const dragonSets = ph.groups.filter((g) => isSet(g) && isDragon(setRankTile(g))).length;
  const pt = pairTile(ph);
  const dragonPair = pt !== void 0 && isDragon(pt);
  return dragonSets === 2 && dragonPair ? 2 : 0;
};
function numberSuits(ph, tiles) {
  const suits = /* @__PURE__ */ new Set();
  for (const t of tiles ?? allTiles(ph)) if (!isHonor4(t)) suits.add(t[0]);
  return suits;
}
function hasHonor(ph, tiles) {
  return (tiles ?? allTiles(ph)).some((t) => isHonor4(t));
}
var honitsu = (ph, _ctx, _rs, tiles) => {
  const suits = numberSuits(ph, tiles);
  if (suits.size !== 1) return 0;
  if (!hasHonor(ph, tiles)) return 0;
  return ph.concealed ? 3 : 2;
};
var chinitsu = (ph, _ctx, _rs, tiles) => {
  const suits = numberSuits(ph, tiles);
  if (suits.size !== 1) return 0;
  if (hasHonor(ph, tiles)) return 0;
  return ph.concealed ? 6 : 5;
};
var chiitoitsu = (ph) => ph.form === "chiitoi" ? 2 : 0;
function handTiles(ph, tiles) {
  if (tiles) return tiles;
  const out = [];
  for (const g of ph.groups) for (const t of g.tiles) out.push(t);
  if (ph.pair) for (const t of ph.pair.tiles) out.push(t);
  for (const mm of ph.melds) for (const t of mm.tiles) out.push(t);
  return out;
}
var akaSanshoku = (ph, _ctx, _rs, tiles) => {
  const ts = handTiles(ph, tiles);
  return ts.includes("m5r") && ts.includes("p5r") && ts.includes("s5r") ? 2 : 0;
};
var hasAll = (set, have) => {
  const s = new Set(have ?? []);
  return set.every((t) => s.has(t));
};
var seasonsYaku = (_ph, ctx) => hasAll(["f1", "f2", "f3", "f4"], ctx.flowers) ? 2 : 0;
var fourGentlemen = (_ph, ctx) => hasAll(["f5", "f6", "f7", "f8"], ctx.flowers) ? 2 : 0;
var windSet = (ph) => ph.melds.some((m) => m.kind === "ankan" && new Set(m.tiles.map(baseOfAka6)).size === 4 && ["z1", "z2", "z3", "z4"].every((w) => m.tiles.map(baseOfAka6).includes(w))) ? 2 : 0;
var BUILTIN_DETECTORS = {
  "flower-seasons": seasonsYaku,
  "four-gentlemen": fourGentlemen,
  "wind-set": windSet,
  riichi,
  "double-riichi": doubleRiichi,
  "open-riichi": openRiichi,
  "open-double-riichi": openDoubleRiichi,
  ippatsu,
  "menzen-tsumo": menzenTsumo,
  pinfu,
  tanyao,
  iipeikou,
  ryanpeikou,
  "yakuhai-round": yakuhaiRound,
  "yakuhai-seat": yakuhaiSeat,
  "yakuhai-haku": yakuhaiHaku,
  "yakuhai-hatsu": yakuhaiHatsu,
  "yakuhai-chun": yakuhaiChun,
  "yakuhai-jigen": yakuhaiJigen,
  "sanshoku-doujun": sanshokuDoujun,
  "sanshoku-doukou": sanshokuDoukou,
  ittsuu,
  chanta,
  junchan,
  honroutou,
  toitoi,
  sanankou,
  sankantsu,
  shousangen,
  honitsu,
  chinitsu,
  chiitoitsu,
  haitei,
  houtei,
  rinshan,
  chankan,
  "aka-sanshoku": akaSanshoku
};

// src/yaku/yakuman.ts
import { baseOfAka as baseOfAka7, isHonor as isHonor5 } from "@mj/protocol";
function baseTiles(tiles) {
  return tiles.map(baseOfAka7);
}
function isSet2(g) {
  return g.kind === "triplet" || g.kind === "quad";
}
function setRankTile2(g) {
  return baseOfAka7(g.tiles[0]);
}
var DRAGONS2 = /* @__PURE__ */ new Set(["z5", "z6", "z7"]);
var WINDS = /* @__PURE__ */ new Set(["z1", "z2", "z3", "z4"]);
function isDragon2(t) {
  return DRAGONS2.has(baseOfAka7(t));
}
function isWind(t) {
  return WINDS.has(baseOfAka7(t));
}
function dbl(rs) {
  return rs.scoring.doubleYakuman ? 2 : 1;
}
var kokushi = (ph, _ctx, rs) => {
  if (ph.form !== "kokushi") return 0;
  const wonOnPair = ph.pair !== void 0 && baseOfAka7(ph.pair.tiles[0]) === baseOfAka7(ph.winTile);
  return wonOnPair ? dbl(rs) : 1;
};
var suuankou = (ph, ctx, rs) => {
  if (ph.form !== "standard" || !ph.concealed) return 0;
  const sets = ph.groups.filter(isSet2);
  if (sets.length !== 4) return 0;
  if (sets.some((g) => g.open)) return 0;
  if (ctx.winBy === "ron" && ph.wait === "shanpon") return 0;
  return ph.wait === "tanki" ? dbl(rs) : 1;
};
var daisangen = (ph) => {
  if (ph.form !== "standard") return 0;
  const dragonSets = ph.groups.filter((g) => isSet2(g) && isDragon2(setRankTile2(g))).length;
  return dragonSets >= 3 ? 1 : 0;
};
var shousuushi = (ph) => {
  if (ph.form !== "standard") return 0;
  const windSets = ph.groups.filter((g) => isSet2(g) && isWind(setRankTile2(g))).length;
  const pt = ph.pair ? baseOfAka7(ph.pair.tiles[0]) : void 0;
  return windSets === 3 && pt !== void 0 && isWind(pt) ? 1 : 0;
};
var daisuushi = (ph, _ctx, rs) => {
  if (ph.form !== "standard") return 0;
  const windSets = ph.groups.filter((g) => isSet2(g) && isWind(setRankTile2(g))).length;
  return windSets >= 4 ? dbl(rs) : 0;
};
var tsuuiisou = (_ph, _ctx, _rs, tiles) => {
  const ts = baseTiles(tiles);
  return ts.length > 0 && ts.every((t) => isHonor5(t)) ? 1 : 0;
};
var chinroutou = (_ph, _ctx, _rs, tiles) => {
  const ts = baseTiles(tiles);
  return ts.length > 0 && ts.every((t) => !isHonor5(t) && isTerminal(t)) ? 1 : 0;
};
var GREEN = /* @__PURE__ */ new Set(["s2", "s3", "s4", "s6", "s8", "z6"]);
var ryuuiisou = (_ph, _ctx, _rs, tiles) => {
  const ts = baseTiles(tiles);
  return ts.length > 0 && ts.every((t) => GREEN.has(t)) ? 1 : 0;
};
var chuuren = (ph, _ctx, rs, tiles) => {
  if (ph.form !== "standard" || !ph.concealed) return 0;
  if (ph.melds.some((mm) => mm.kind !== "kita")) return 0;
  const ts = baseTiles(tiles);
  if (ts.length !== 14) return 0;
  let suit;
  for (const t of ts) {
    if (isHonor5(t)) return 0;
    const s = t[0];
    if (suit === void 0) suit = s;
    else if (s !== suit) return 0;
  }
  if (suit === void 0) return 0;
  const byRank = new Array(10).fill(0);
  for (const t of ts) byRank[tileKey(t).rank]++;
  if (byRank[1] < 3 || byRank[9] < 3) return 0;
  for (let r = 2; r <= 8; r++) if (byRank[r] < 1) return 0;
  const w = tileKey(baseOfAka7(ph.winTile)).rank;
  const minus = byRank.slice();
  minus[w]--;
  const isNineGatesBase = minus[1] === 3 && minus[9] === 3 && [2, 3, 4, 5, 6, 7, 8].every((r) => minus[r] === 1);
  return isNineGatesBase ? dbl(rs) : 1;
};
var suukantsu = (ph) => {
  const kans = ph.groups.filter((g) => g.kind === "quad").length;
  return kans >= 4 ? 1 : 0;
};
var tenhou = (_ph, ctx) => ctx.isDealer && ctx.firstTurn && ctx.winBy === "tsumo" ? 1 : 0;
var chiihou = (_ph, ctx) => !ctx.isDealer && ctx.firstTurn && ctx.winBy === "tsumo" ? 1 : 0;
var hachirenchan = (_ph, ctx) => ctx.dealerWinStreak >= 8 ? 1 : 0;
var juusanbuttou = (ph, ctx, _rs, tiles) => {
  if (!ctx.firstTurn || ctx.winBy !== "tsumo") return 0;
  const ts = baseTiles(tiles);
  if (ts.length !== 14) return 0;
  const counts = /* @__PURE__ */ new Map();
  for (const t of ts) counts.set(t, (counts.get(t) ?? 0) + 1);
  let pairTile2;
  for (const [t, n] of counts) {
    if (n >= 3) return 0;
    if (n === 2) {
      if (pairTile2 !== void 0) return 0;
      pairTile2 = t;
    }
  }
  if (pairTile2 === void 0) return 0;
  if (baseOfAka7(ph.winTile) !== pairTile2) return 0;
  const bySuit = { m: [], p: [], s: [] };
  for (const t of counts.keys()) {
    if (isHonor5(t)) continue;
    const s = t[0];
    bySuit[s].push(tileKey(t).rank);
  }
  for (const s of ["m", "p", "s"]) {
    const ranks = bySuit[s].slice().sort((a, b) => a - b);
    for (let i = 1; i < ranks.length; i++) {
      if (ranks[i] - ranks[i - 1] <= 2) return 0;
    }
  }
  return 1;
};
var YAKUMAN_DETECTORS = {
  kokushi,
  suuankou,
  daisangen,
  shousuushi,
  daisuushi,
  tsuuiisou,
  chinroutou,
  ryuuiisou,
  chuuren,
  suukantsu,
  tenhou,
  chiihou,
  hachirenchan,
  juusanbuttou
};

// src/yaku/catalog.ts
function builtin(key) {
  return { type: "builtin", key };
}
function dsl(constraint) {
  return { type: "dsl", constraint };
}
function entry(e) {
  const base = {
    id: e.id,
    name: e.name,
    kind: e.kind ?? "normal",
    defaultHan: e.defaultHan,
    tunable: e.tunable,
    category: e.category ?? "standard",
    detector: e.detector,
    exampleHand: e.exampleHand,
    description: e.description
  };
  if (e.yakumanMult !== void 0) base.yakumanMult = e.yakumanMult;
  if (e.source !== void 0) base.source = e.source;
  if (e.doubleName !== void 0) base.doubleName = e.doubleName;
  if (e.openHan !== void 0) base.openHan = e.openHan;
  if (e.excludes !== void 0) base.excludes = e.excludes;
  return base;
}
var EX = {
  tanyao: ["m2", "m3", "m4", "p2", "p3", "p4", "s5", "s6", "s7", "s3", "s4", "s5", "p8", "p8"],
  pinfu: ["m2", "m3", "m4", "p2", "p3", "p4", "s5", "s6", "s7", "s7", "s8", "s9", "p3", "p3"]
};
var YAKU_CATALOG = [
  // ---------------------------------------------------------------------------
  // Standard NORMAL yaku (builtin detectors). `tunable:'tunable'` => room may override han;
  // shape/luck yaku whose han is intrinsic are `structural`.
  // ---------------------------------------------------------------------------
  entry({ id: "riichi", name: { ja: "\u30EA\u30FC\u30C1", en: "Riichi" }, description: { ja: "\u9580\u524D\u3067\u7ACB\u76F4\u5BA3\u8A00", en: "Declared ready while concealed" }, detector: builtin("riichi"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "double-riichi", name: { ja: "\u30C0\u30D6\u30EB\u30EA\u30FC\u30C1", en: "Double Riichi" }, description: { ja: "\u7B2C\u4E00\u6253\u3067\u7ACB\u76F4", en: "Riichi on the first discard" }, detector: builtin("double-riichi"), defaultHan: 2, tunable: "tunable", excludes: ["riichi"], exampleHand: EX.tanyao }),
  entry({ id: "open-riichi", name: { ja: "\u30AA\u30FC\u30D7\u30F3\u7ACB\u76F4", en: "Open Riichi" }, description: { ja: "\u624B\u724C\u3092\u516C\u958B\u3057\u3066\u7ACB\u76F4\u3059\u308B\u7ACB\u76F4\u306E\u4E0A\u4F4D\u5F79\uFF082\u7FFB\u30FB\u7ACB\u76F4\u3068\u306F\u8907\u5408\u3057\u306A\u3044\uFF09", en: "Open-hand riichi; the superior of riichi (2 han, does not stack with riichi)" }, detector: builtin("open-riichi"), defaultHan: 2, tunable: "tunable", excludes: ["riichi"], exampleHand: EX.tanyao }),
  entry({ id: "open-double-riichi", name: { ja: "\u30AA\u30FC\u30D7\u30F3\u30C0\u30D6\u30EB\u7ACB\u76F4", en: "Open Double Riichi" }, description: { ja: "\u7B2C\u4E00\u6253\u306E\u30AA\u30FC\u30D7\u30F3\u7ACB\u76F4\uFF083\u7FFB\u30FB\u7ACB\u76F4/\u30C0\u30D6\u30EB\u7ACB\u76F4/\u30AA\u30FC\u30D7\u30F3\u7ACB\u76F4\u3068\u8907\u5408\u3057\u306A\u3044\uFF09", en: "Open riichi on the first discard (3 han; supersedes riichi/double-riichi/open-riichi)" }, detector: builtin("open-double-riichi"), defaultHan: 3, tunable: "tunable", excludes: ["riichi", "double-riichi", "open-riichi"], exampleHand: EX.tanyao }),
  entry({ id: "ippatsu", name: { ja: "\u4E00\u767A", en: "Ippatsu" }, description: { ja: "\u7ACB\u76F4\u5F8C\u4E00\u5DE1\u4EE5\u5185\u306B\u548C\u4E86", en: "Win within one go-around of riichi" }, detector: builtin("ippatsu"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "menzen-tsumo", name: { ja: "\u9580\u524D\u6E05\u81EA\u6478\u548C", en: "Menzen Tsumo" }, description: { ja: "\u9580\u524D\u3067\u30C4\u30E2\u548C\u4E86", en: "Self-draw win while concealed" }, detector: builtin("menzen-tsumo"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "pinfu", name: { ja: "\u5E73\u548C", en: "Pinfu" }, description: { ja: "\u5168\u9806\u5B50\u30FB\u975E\u5F79\u724C\u306E\u96C0\u982D\u30FB\u4E21\u9762\u5F85\u3061", en: "All sequences, non-yakuhai pair, ryanmen wait" }, detector: builtin("pinfu"), defaultHan: 1, tunable: "structural", exampleHand: EX.pinfu }),
  entry({ id: "tanyao", name: { ja: "\u65AD\u4E48\u4E5D", en: "Tanyao" }, description: { ja: "\u4E48\u4E5D\u724C\u3092\u542B\u307E\u306A\u3044", en: "No terminals or honors" }, detector: builtin("tanyao"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "iipeikou", name: { ja: "\u4E00\u76C3\u53E3", en: "Iipeikou" }, description: { ja: "\u540C\u4E00\u9806\u5B50\u304C\u4E00\u7D44\uFF08\u9580\u524D\uFF09", en: "One identical sequence pair (concealed)" }, detector: builtin("iipeikou"), defaultHan: 1, tunable: "structural", exampleHand: ["p2", "p3", "p4", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "z1", "z1"] }),
  entry({ id: "ryanpeikou", name: { ja: "\u4E8C\u76C3\u53E3", en: "Ryanpeikou" }, description: { ja: "\u540C\u4E00\u9806\u5B50\u304C\u4E8C\u7D44\uFF08\u9580\u524D\uFF09", en: "Two identical sequence pairs (concealed)" }, detector: builtin("ryanpeikou"), defaultHan: 3, tunable: "structural", excludes: ["iipeikou", "chiitoitsu"], exampleHand: ["p2", "p3", "p4", "p2", "p3", "p4", "s5", "s6", "s7", "s5", "s6", "s7", "m1", "m1"] }),
  entry({ id: "yakuhai-round", name: { ja: "\u5F79\u724C\uFF08\u5834\u98A8\uFF09", en: "Yakuhai (round wind)" }, description: { ja: "\u5834\u98A8\u724C\u306E\u523B\u5B50", en: "Triplet of the round wind" }, detector: builtin("yakuhai-round"), defaultHan: 1, tunable: "tunable", exampleHand: ["z1", "z1", "z1", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  entry({ id: "yakuhai-seat", name: { ja: "\u5F79\u724C\uFF08\u81EA\u98A8\uFF09", en: "Yakuhai (seat wind)" }, description: { ja: "\u81EA\u98A8\u724C\u306E\u523B\u5B50", en: "Triplet of the seat wind" }, detector: builtin("yakuhai-seat"), defaultHan: 1, tunable: "tunable", exampleHand: ["z2", "z2", "z2", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  entry({ id: "yakuhai-haku", name: { ja: "\u5F79\u724C\uFF08\u767D\uFF09", en: "Yakuhai (White)" }, description: { ja: "\u767D\u306E\u523B\u5B50", en: "Triplet of White dragon" }, detector: builtin("yakuhai-haku"), defaultHan: 1, tunable: "tunable", exampleHand: ["z5", "z5", "z5", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  entry({ id: "yakuhai-hatsu", name: { ja: "\u5F79\u724C\uFF08\u767C\uFF09", en: "Yakuhai (Green)" }, description: { ja: "\u767C\u306E\u523B\u5B50", en: "Triplet of Green dragon" }, detector: builtin("yakuhai-hatsu"), defaultHan: 1, tunable: "tunable", exampleHand: ["z6", "z6", "z6", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  entry({ id: "yakuhai-chun", name: { ja: "\u5F79\u724C\uFF08\u4E2D\uFF09", en: "Yakuhai (Red)" }, description: { ja: "\u4E2D\u306E\u523B\u5B50", en: "Triplet of Red dragon" }, detector: builtin("yakuhai-chun"), defaultHan: 1, tunable: "tunable", exampleHand: ["z7", "z7", "z7", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  // 自元（席ドラゴン）: +1 ADDITIVE han for a triplet of YOUR assigned seat-dragon (ctx.seatDragon, set by
  // flags.seatDragon for seats >=4). Inert unless the flag is ON and this seat has a dragon assigned.
  // Stacks with the always-on dragon yakuhai (your dragon = 2 han total). This is additive (Option B).
  entry({ id: "yakuhai-jigen", name: { ja: "\u81EA\u5143\uFF08\u5E2D\u30C9\u30E9\u30B4\u30F3\uFF09", en: "Jigen (seat dragon)" }, description: { ja: "\u81EA\u5206\u306E\u81EA\u5143\u30C9\u30E9\u30B4\u30F3\u306E\u523B\u5B50\u3067+1\u7FFB\uFF08\u4E09\u5143\u724C\u3068\u52A0\u7B97\uFF09", en: "+1 han for a triplet of your assigned seat dragon (adds to the dragon yakuhai)" }, detector: builtin("yakuhai-jigen"), defaultHan: 1, tunable: "tunable", exampleHand: ["z5", "z5", "z5", "p2", "p3", "p4", "s5", "s6", "s7", "m4", "m5", "m6", "p8", "p8"] }),
  entry({ id: "sanshoku-doujun", name: { ja: "\u4E09\u8272\u540C\u9806", en: "Sanshoku Doujun" }, description: { ja: "\u4E09\u8272\u3067\u540C\u3058\u9806\u5B50", en: "Same sequence in all three suits" }, detector: builtin("sanshoku-doujun"), defaultHan: 2, tunable: "tunable", excludes: ["nishoku-doujun"], exampleHand: ["m2", "m3", "m4", "p2", "p3", "p4", "s2", "s3", "s4", "s6", "s7", "s8", "z1", "z1"] }),
  // 二色同順: 三色同順の2色版（同じ数字の順子が2つの数牌色に）。神様麻雀（nizidara.com）。3色揃えば
  // 三色同順が上位で吸収（excludes）。DSL の sameSequenceSuits 述語で純データ実装。
  entry({ id: "nishoku-doujun", name: { ja: "\u4E8C\u8272\u540C\u9806", en: "Nishoku Doujun" }, description: { ja: "\u4E8C\u8272\u3067\u540C\u3058\u9806\u5B50\uFF08\u4E09\u8272\u540C\u9806\u306E\u4E00\u8272\u8DB3\u308A\u306A\u3044\u7248\uFF09", en: "Same sequence in two of the three suits" }, detector: dsl({ sameSequenceSuits: { atLeast: 2 } }), defaultHan: 1, tunable: "tunable", category: "localFamous", source: "nizidara.com kamisama-mahjong", exampleHand: ["m2", "m3", "m4", "p2", "p3", "p4", "s1", "s2", "s3", "s6", "s7", "s8", "z1", "z1"] }),
  // 途中まで通貫: 同色で連続2順子（123456 等）。一気通貫が上位で吸収（excludes）。神様麻雀。
  entry({ id: "tochuu-ittsuu", name: { ja: "\u9014\u4E2D\u307E\u3067\u901A\u8CAB", en: "Partial Ittsuu" }, description: { ja: "\u540C\u8272\u3067\u9023\u7D9A\u3057\u305F\u4E8C\u3064\u306E\u9806\u5B50\uFF08123456 \u306A\u3069\uFF09", en: "Two consecutive sequences in one suit (e.g. 123456)" }, detector: dsl({ consecutiveSequences: { atLeast: 2 } }), defaultHan: 1, tunable: "tunable", category: "localFamous", source: "nizidara.com kamisama-mahjong", exampleHand: ["m1", "m2", "m3", "m4", "m5", "m6", "p2", "p3", "p4", "s6", "s7", "s8", "z1", "z1"] }),
  // 役牌(春夏秋冬): 季節牌 f1-f4 を全て抜いて揃える（花牌ルール時のコレクション役）。条件はエンジン定義（ctx.flowers）。
  entry({ id: "flower-seasons", name: { ja: "\u5F79\u724C\uFF08\u6625\u590F\u79CB\u51AC\uFF09", en: "Four Seasons" }, description: { ja: "\u6625\u590F\u79CB\u51AC\uFF08\u82B1\u724C f1-f4\uFF09\u3092\u5168\u3066\u96C6\u3081\u308B", en: "Collect all four season flowers (f1-f4)" }, detector: builtin("flower-seasons"), defaultHan: 2, tunable: "tunable", category: "localFamous", exampleHand: EX.tanyao }),
  // 四君子: 梅蘭菊竹（花牌 f5-f8）を全て揃える。条件はエンジン定義（ctx.flowers）。
  entry({ id: "four-gentlemen", name: { ja: "\u56DB\u541B\u5B50", en: "Four Gentlemen" }, description: { ja: "\u6885\u862D\u83CA\u7AF9\uFF08\u82B1\u724C f5-f8\uFF09\u3092\u5168\u3066\u96C6\u3081\u308B", en: "Collect all four flowers (plum/orchid/chrysanthemum/bamboo, f5-f8)" }, detector: builtin("four-gentlemen"), defaultHan: 2, tunable: "tunable", category: "localFamous", exampleHand: EX.tanyao }),
  // 役牌(東西南北): 東西南北(z1-z4)を手牌に集めて異種4牌の特殊暗槓で宣言→成立。条件はエンジン定義。
  entry({ id: "wind-set", name: { ja: "\u5F79\u724C\uFF08\u6771\u897F\u5357\u5317\uFF09", en: "Four Winds Kan" }, description: { ja: "\u6771\u897F\u5357\u5317\u3092\u96C6\u3081\u3066\u7570\u7A2E4\u724C\u306E\u6697\u69D3\uFF08\u7279\u6B8A\u30AB\u30F3\uFF09", en: "Concealed kan of the four different winds (z1-z4)" }, detector: builtin("wind-set"), defaultHan: 2, tunable: "tunable", category: "localFamous", exampleHand: EX.tanyao }),
  // 不純全: 全面子が么九(1/9)入り＋中張牌(2-8)の雀頭・字牌なし（純全帯の雀頭違い）。神様麻雀。
  entry({ id: "fujunzen", name: { ja: "\u4E0D\u7D14\u5168", en: "Impure Junchan" }, description: { ja: "\u5168\u9762\u5B50\u306B1/9\u3092\u542B\u307F\u3001\u96C0\u982D\u304C\u4E2D\u5F35\u724C\uFF08\u5B57\u724C\u306A\u3057\uFF09\u3002\u9580\u524D2\u98DC\u30FB\u55B0\u30441\u98DC", en: "Every set contains a terminal; the pair is a simple (no honors). 2 han / open 1" }, detector: dsl({ all: [{ form: "standard" }, { everyGroupTouches: { terminal: true } }, { pair: { rankRange: [2, 8] } }, { suitsUsed: { honors: "forbidden" } }] }), defaultHan: 2, openHan: 1, tunable: "tunable", category: "localFamous", source: "nizidara.com kamisama-mahjong", exampleHand: ["m1", "m2", "m3", "m7", "m8", "m9", "p1", "p2", "p3", "s7", "s8", "s9", "p5", "p5"] }),
  // 役牌（東西南北）: 風牌（東西南北）をカンして成立（場風/自風でなくても1翻）。神様麻雀。
  entry({ id: "wind-kan", name: { ja: "\u5F79\u724C\uFF08\u6771\u897F\u5357\u5317\uFF09", en: "Yakuhai (winds, by kan)" }, description: { ja: "\u6771\u897F\u5357\u5317\u3044\u305A\u308C\u304B\u3092\u30AB\u30F3\u3057\u3066\u6210\u7ACB\uFF081\u7FFB\uFF09", en: "Kan of any wind (E/S/W/N) \u2014 1 han" }, detector: dsl({ groups: { kind: "quad", honor: "wind", atLeast: 1 } }), defaultHan: 1, tunable: "tunable", category: "localFamous", source: "nizidara.com kamisama-mahjong", exampleHand: ["z1", "z1", "z1", "z1", "m2", "m3", "m4", "p2", "p3", "p4", "s6", "s7", "s8", "p8", "p8"] }),
  entry({ id: "sanshoku-doukou", name: { ja: "\u4E09\u8272\u540C\u523B", en: "Sanshoku Doukou" }, description: { ja: "\u4E09\u8272\u3067\u540C\u3058\u523B\u5B50", en: "Same triplet in all three suits" }, detector: builtin("sanshoku-doukou"), defaultHan: 2, tunable: "tunable", exampleHand: ["m2", "m2", "m2", "p2", "p2", "p2", "s2", "s2", "s2", "s6", "s7", "s8", "z1", "z1"] }),
  entry({ id: "ittsuu", name: { ja: "\u4E00\u6C17\u901A\u8CAB", en: "Ittsuu" }, description: { ja: "\u4E00\u8272\u3067123-456-789", en: "123-456-789 in one suit" }, detector: builtin("ittsuu"), defaultHan: 2, tunable: "tunable", excludes: ["tochuu-ittsuu"], exampleHand: ["p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8", "p9", "s6", "s7", "s8", "z1", "z1"] }),
  entry({ id: "chanta", name: { ja: "\u6DF7\u5168\u5E2F\u4E48\u4E5D", en: "Chanta" }, description: { ja: "\u5168\u9762\u5B50\u306B\u4E48\u4E5D\u724C\uFF08\u9806\u5B50\u542B\u3080\uFF09", en: "Every set contains a terminal or honor" }, detector: builtin("chanta"), defaultHan: 2, tunable: "tunable", exampleHand: ["m1", "m2", "m3", "p7", "p8", "p9", "s1", "s2", "s3", "z5", "z5", "z5", "z1", "z1"] }),
  entry({ id: "junchan", name: { ja: "\u7D14\u5168\u5E2F\u4E48\u4E5D", en: "Junchan" }, description: { ja: "\u5168\u9762\u5B50\u306B\u8001\u982D\u724C\uFF08\u5B57\u724C\u306A\u3057\uFF09", en: "Every set contains a terminal, no honors" }, detector: builtin("junchan"), defaultHan: 3, tunable: "tunable", excludes: ["chanta"], exampleHand: ["m1", "m2", "m3", "p7", "p8", "p9", "s1", "s2", "s3", "s7", "s8", "s9", "p9", "p9"] }),
  entry({ id: "honroutou", name: { ja: "\u6DF7\u8001\u982D", en: "Honroutou" }, description: { ja: "\u8001\u982D\u724C\u3068\u5B57\u724C\u306E\u307F", en: "Only terminals and honors" }, detector: builtin("honroutou"), defaultHan: 2, tunable: "tunable", excludes: ["chanta"], exampleHand: ["m1", "m1", "m1", "p9", "p9", "p9", "s1", "s1", "s1", "z5", "z5", "z5", "z1", "z1"] }),
  entry({ id: "toitoi", name: { ja: "\u5BFE\u3005\u548C", en: "Toitoi" }, description: { ja: "\u5168\u9762\u5B50\u304C\u523B\u5B50", en: "All triplets" }, detector: builtin("toitoi"), defaultHan: 2, tunable: "tunable", exampleHand: ["m2", "m2", "m2", "p3", "p3", "p3", "s4", "s4", "s4", "z5", "z5", "z5", "p8", "p8"] }),
  entry({ id: "sanankou", name: { ja: "\u4E09\u6697\u523B", en: "Sanankou" }, description: { ja: "\u6697\u523B\u304C\u4E09\u7D44", en: "Three concealed triplets" }, detector: builtin("sanankou"), defaultHan: 2, tunable: "tunable", exampleHand: ["m2", "m2", "m2", "p3", "p3", "p3", "s4", "s4", "s4", "s6", "s7", "s8", "p8", "p8"] }),
  entry({ id: "sankantsu", name: { ja: "\u4E09\u69D3\u5B50", en: "Sankantsu" }, description: { ja: "\u69D3\u5B50\u304C\u4E09\u7D44", en: "Three kans" }, detector: builtin("sankantsu"), defaultHan: 2, tunable: "tunable", exampleHand: ["m2", "m2", "m2", "m2", "p3", "p3", "p3", "p3", "s4", "s4", "s4", "s4", "p8", "p8"] }),
  entry({ id: "shousangen", name: { ja: "\u5C0F\u4E09\u5143", en: "Shousangen" }, description: { ja: "\u4E09\u5143\u724C\u306E\u523B\u5B50\u4E8C\u7D44\u3068\u96C0\u982D", en: "Two dragon triplets + dragon pair" }, detector: builtin("shousangen"), defaultHan: 2, tunable: "tunable", exampleHand: ["z5", "z5", "z5", "z6", "z6", "z6", "s2", "s3", "s4", "s6", "s7", "s8", "z7", "z7"] }),
  entry({ id: "honitsu", name: { ja: "\u6DF7\u4E00\u8272", en: "Honitsu" }, description: { ja: "\u4E00\u8272\uFF0B\u5B57\u724C", en: "One suit plus honors" }, detector: builtin("honitsu"), defaultHan: 3, tunable: "tunable", exampleHand: ["p1", "p2", "p3", "p5", "p6", "p7", "p9", "p9", "p9", "z5", "z5", "z5", "z1", "z1"] }),
  entry({ id: "chinitsu", name: { ja: "\u6E05\u4E00\u8272", en: "Chinitsu" }, description: { ja: "\u4E00\u8272\u306E\u307F\uFF08\u5B57\u724C\u306A\u3057\uFF09", en: "One suit only, no honors" }, detector: builtin("chinitsu"), defaultHan: 6, tunable: "tunable", exampleHand: ["p1", "p2", "p3", "p5", "p6", "p7", "p9", "p9", "p9", "p4", "p4", "p4", "p8", "p8"] }),
  entry({ id: "chiitoitsu", name: { ja: "\u4E03\u5BFE\u5B50", en: "Chiitoitsu" }, description: { ja: "\u4E03\u3064\u306E\u5BFE\u5B50", en: "Seven pairs" }, detector: builtin("chiitoitsu"), defaultHan: 2, tunable: "structural", exampleHand: ["m1", "m1", "p2", "p2", "s3", "s3", "m9", "m9", "p7", "p7", "s5", "s5", "z1", "z1"] }),
  entry({ id: "haitei", name: { ja: "\u6D77\u5E95\u6478\u6708", en: "Haitei" }, description: { ja: "\u6700\u5F8C\u306E\u30C4\u30E2\u3067\u548C\u4E86", en: "Win on the last drawn tile" }, detector: builtin("haitei"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "houtei", name: { ja: "\u6CB3\u5E95\u6488\u9B5A", en: "Houtei" }, description: { ja: "\u6700\u5F8C\u306E\u6368\u3066\u724C\u3067\u548C\u4E86", en: "Win on the last discard" }, detector: builtin("houtei"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  entry({ id: "rinshan", name: { ja: "\u5DBA\u4E0A\u958B\u82B1", en: "Rinshan" }, description: { ja: "\u69D3\u306E\u5DBA\u4E0A\u724C\u3067\u548C\u4E86", en: "Win on the kan replacement tile" }, detector: builtin("rinshan"), defaultHan: 1, tunable: "tunable", exampleHand: ["s6", "s7", "s8", "s3", "s4", "s5", "m4", "m5", "m6", "z5", "z5", "z5", "z5", "p8", "p8"] }),
  entry({ id: "chankan", name: { ja: "\u69CD\u69D3", en: "Chankan" }, description: { ja: "\u52A0\u69D3\u3092\u596A\u3063\u3066\u548C\u4E86", en: "Rob a kan" }, detector: builtin("chankan"), defaultHan: 1, tunable: "tunable", exampleHand: EX.tanyao }),
  // ---------------------------------------------------------------------------
  // DORA rows (category:'dora'). NOT real yaku — they only contribute han and NEVER satisfy the
  // "must have >=1 yaku" gate (the engine's detectYaku skips category:'dora'). They appear in the
  // catalog so the owner can toggle them on/off and tune their per-tile han. `aka-dora` is consumed by
  // the scorer (score.ts) as: han-per-red-five = (enabled? hanOverride ?? defaultHan : 0) × akaCount.
  // The detector key is inert (never run); it exists only to satisfy the schema.
  // ---------------------------------------------------------------------------
  entry({ id: "aka-dora", name: { ja: "\u8D64\u30C9\u30E9", en: "Red Five" }, description: { ja: "\u8D64\u4E94\uFF08\u8D64\u30C9\u30E9\uFF091\u679A\u306B\u3064\u304D\u52A0\u7B97\u3059\u308B\u7FFB\u6570\u3002\u30C9\u30E9\u6271\u3044\u3067\u5F79\u306B\u306F\u6570\u3048\u306A\u3044", en: "Han added per red five (aka-dora). Counts as dora, not as a yaku" }, detector: builtin("aka-dora"), defaultHan: 1, tunable: "tunable", category: "dora", exampleHand: ["m4", "m5r", "m6", "p2", "p3", "p4", "s5", "s6", "s7", "s3", "s4", "s5", "p8", "p8"] }),
  // ---------------------------------------------------------------------------
  // YAKUMAN (builtin detectors). `kind:'yakuman'`, `tunable:'structural'` (han intrinsic; the
  // yakuman MULTIPLIER, not han, is what rooms tune later). `yakumanMult` is the default multiplier.
  // ---------------------------------------------------------------------------
  entry({ id: "kokushi", name: { ja: "\u56FD\u58EB\u7121\u53CC", en: "Kokushi Musou" }, description: { ja: "\u5341\u4E09\u4E48\u4E5D\uFF0813\u9762\u5F85\u3061\u306F\u30C0\u30D6\u30EB\uFF09", en: "Thirteen orphans (13-wait = double)" }, detector: builtin("kokushi"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", doubleName: { ja: "\u56FD\u58EB\u7121\u53CC\u5341\u4E09\u9762\u5F85\u3061", en: "Kokushi 13-wait" }, exampleHand: ["m1", "m9", "p1", "p9", "s1", "s9", "z1", "z2", "z3", "z4", "z5", "z6", "z7", "m1"] }),
  entry({ id: "suuankou", name: { ja: "\u56DB\u6697\u523B", en: "Suuankou" }, description: { ja: "\u56DB\u3064\u306E\u6697\u523B\uFF08\u5358\u9A0E\u306F\u30C0\u30D6\u30EB\uFF09", en: "Four concealed triplets (tanki = double)" }, detector: builtin("suuankou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", doubleName: { ja: "\u56DB\u6697\u523B\u5358\u9A0E", en: "Suuankou Tanki" }, exampleHand: ["m2", "m2", "m2", "p3", "p3", "p3", "s4", "s4", "s4", "z5", "z5", "z5", "p8", "p8"] }),
  entry({ id: "daisangen", name: { ja: "\u5927\u4E09\u5143", en: "Daisangen" }, description: { ja: "\u4E09\u5143\u724C\u306E\u523B\u5B50\u4E09\u7D44", en: "Triplets of all three dragons" }, detector: builtin("daisangen"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["z5", "z5", "z5", "z6", "z6", "z6", "z7", "z7", "z7", "s2", "s3", "s4", "p8", "p8"] }),
  entry({ id: "shousuushi", name: { ja: "\u5C0F\u56DB\u559C", en: "Shousuushi" }, description: { ja: "\u98A8\u724C\u306E\u523B\u5B50\u4E09\u7D44\u3068\u96C0\u982D", en: "Three wind triplets + wind pair" }, detector: builtin("shousuushi"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["z1", "z1", "z1", "z2", "z2", "z2", "z3", "z3", "z3", "s2", "s3", "s4", "z4", "z4"] }),
  entry({ id: "daisuushi", name: { ja: "\u5927\u56DB\u559C", en: "Daisuushi" }, description: { ja: "\u98A8\u724C\u306E\u523B\u5B50\u56DB\u7D44\uFF08\u30C0\u30D6\u30EB\u5F79\u6E80\uFF09", en: "Four wind triplets (double yakuman)" }, detector: builtin("daisuushi"), kind: "yakuman", category: "standard", defaultHan: 26, yakumanMult: 2, tunable: "structural", exampleHand: ["z1", "z1", "z1", "z2", "z2", "z2", "z3", "z3", "z3", "z4", "z4", "z4", "p8", "p8"] }),
  entry({ id: "tsuuiisou", name: { ja: "\u5B57\u4E00\u8272", en: "Tsuuiisou" }, description: { ja: "\u5B57\u724C\u306E\u307F", en: "All honors" }, detector: builtin("tsuuiisou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["z1", "z1", "z1", "z2", "z2", "z2", "z5", "z5", "z5", "z6", "z6", "z6", "z7", "z7"] }),
  entry({ id: "chinroutou", name: { ja: "\u6E05\u8001\u982D", en: "Chinroutou" }, description: { ja: "\u8001\u982D\u724C\u306E\u307F", en: "All terminals" }, detector: builtin("chinroutou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["m1", "m1", "m1", "m9", "m9", "m9", "p1", "p1", "p1", "s9", "s9", "s9", "p9", "p9"] }),
  entry({ id: "ryuuiisou", name: { ja: "\u7DD1\u4E00\u8272", en: "Ryuuiisou" }, description: { ja: "\u7DD1\u724C\u306E\u307F", en: "All green tiles" }, detector: builtin("ryuuiisou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["s2", "s3", "s4", "s2", "s3", "s4", "s6", "s6", "s6", "z6", "z6", "z6", "s8", "s8"] }),
  entry({ id: "chuuren", name: { ja: "\u4E5D\u84EE\u5B9D\u71C8", en: "Chuuren Poutou" }, description: { ja: "\u6E05\u4E00\u82721112345678999\u5F62\uFF08\u7D14\u6B63\u4E5D\u84EE\u306F\u30C0\u30D6\u30EB\uFF09", en: "Nine gates (pure 9-wait = double)" }, detector: builtin("chuuren"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", doubleName: { ja: "\u7D14\u6B63\u4E5D\u84EE\u5B9D\u71C8", en: "Junsei Chuuren" }, exampleHand: ["p1", "p1", "p1", "p2", "p3", "p4", "p5", "p5", "p6", "p7", "p8", "p9", "p9", "p9"] }),
  entry({ id: "suukantsu", name: { ja: "\u56DB\u69D3\u5B50", en: "Suukantsu" }, description: { ja: "\u69D3\u5B50\u56DB\u7D44", en: "Four kans" }, detector: builtin("suukantsu"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: ["m2", "m2", "m2", "m2", "p3", "p3", "p3", "p3", "s4", "s4", "s4", "s4", "z5", "z5", "z5", "z5", "p8", "p8"] }),
  entry({ id: "tenhou", name: { ja: "\u5929\u548C", en: "Tenhou" }, description: { ja: "\u89AA\u306E\u914D\u724C\u30C4\u30E2\u548C\u4E86", en: "Dealer wins on the first draw" }, detector: builtin("tenhou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: EX.tanyao }),
  entry({ id: "chiihou", name: { ja: "\u5730\u548C", en: "Chiihou" }, description: { ja: "\u5B50\u306E\u7B2C\u4E00\u30C4\u30E2\u548C\u4E86", en: "Non-dealer wins on the first draw" }, detector: builtin("chiihou"), kind: "yakuman", category: "standard", defaultHan: 13, yakumanMult: 1, tunable: "structural", exampleHand: EX.tanyao }),
  // ---------------------------------------------------------------------------
  // Famous-local entries. DSL where the shape is regular; builtin where it reads game state.
  // ---------------------------------------------------------------------------
  entry({ id: "daisharin", name: { ja: "\u5927\u8ECA\u8F2A", en: "Daisharin" }, description: { ja: "\u7B52\u5B502\u301C8\u306E\u4E03\u5BFE\u5B50", en: "Seven pairs of pinzu 2..8" }, detector: dsl({ all: [{ form: "chiitoi" }, { allTilesIn: { suit: "p", rankRange: [2, 8] } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia/majandofu local yaku", exampleHand: ["p2", "p2", "p3", "p3", "p4", "p4", "p5", "p5", "p6", "p6", "p7", "p7", "p8", "p8"] }),
  entry({ id: "daichikurin", name: { ja: "\u5927\u7AF9\u6797", en: "Daichikurin" }, description: { ja: "\u7D22\u5B502\u301C8\u306E\u4E03\u5BFE\u5B50", en: "Seven pairs of souzu 2..8" }, detector: dsl({ all: [{ form: "chiitoi" }, { allTilesIn: { suit: "s", rankRange: [2, 8] } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia/majandofu local yaku", exampleHand: ["s2", "s2", "s3", "s3", "s4", "s4", "s5", "s5", "s6", "s6", "s7", "s7", "s8", "s8"] }),
  entry({ id: "daisuurin", name: { ja: "\u5927\u6570\u96A3", en: "Daisuurin" }, description: { ja: "\u842C\u5B502\u301C8\u306E\u4E03\u5BFE\u5B50", en: "Seven pairs of manzu 2..8" }, detector: dsl({ all: [{ form: "chiitoi" }, { allTilesIn: { suit: "m", rankRange: [2, 8] } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia/majandofu local yaku", exampleHand: ["m2", "m2", "m3", "m3", "m4", "m4", "m5", "m5", "m6", "m6", "m7", "m7", "m8", "m8"] }),
  // 人和: 子の第一巡ロン和了。門前限定（途中で鳴いたら成立しない）— menzenOnly ガードを追加。
  entry({ id: "renhou", name: { ja: "\u4EBA\u548C", en: "Renhou" }, description: { ja: "\u5B50\u306E\u7B2C\u4E00\u5DE1\u30ED\u30F3\u548C\u4E86\uFF08\u9580\u524D\u9650\u5B9A\uFF09", en: "First-turn non-dealer ron (concealed only)" }, detector: dsl({ ctx: { firstTurn: true, ronOnly: true, nonDealer: true, menzenOnly: true } }), kind: "normal", category: "localFamous", defaultHan: 5, tunable: "tunable", source: "ja.wikipedia local yaku", exampleHand: EX.tanyao }),
  // 十三不塔: 第一ツモ・塔子なし・雀頭は和了牌の一組のみ。builtin で 14 牌を検査（旧 DSL は全第一ツモで
  // 役満を量産するバグだった）。example は塔子ゼロ（同色数牌は rank 距離3以上）の no-taatsu 配牌。
  entry({ id: "juusanbuttou", name: { ja: "\u5341\u4E09\u4E0D\u5854", en: "Juusan Buttou" }, description: { ja: "\u914D\u724C\u6642\u306B\u5854\u5B50\u306A\u3057\u306E\u7B2C\u4E00\u30C4\u30E2\u548C\u4E86\uFF08\u96C0\u982D\u306F\u548C\u4E86\u724C\u306E\u307F\uFF09", en: "First-turn tsumo with no taatsu in the deal (the only pair is the winning pair)" }, detector: builtin("juusanbuttou"), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku", exampleHand: ["m1", "m4", "m7", "p1", "p4", "p7", "s1", "s4", "s9", "z1", "z3", "z5", "z7", "z7"] }),
  entry({ id: "hachirenchan", name: { ja: "\u516B\u9023\u8358", en: "Hachirenchan" }, description: { ja: "\u89AA\u304C\u516B\u56DE\u9023\u7D9A\u548C\u4E86", en: "Dealer wins eight in a row" }, detector: builtin("hachirenchan"), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku", exampleHand: EX.tanyao }),
  // --- Famous-local NORMAL yaku (builtin: relational / tile-set / state shapes). Han is tunable. ---
  entry({ id: "sanrenkou", name: { ja: "\u4E09\u9023\u523B", en: "Sanrenkou" }, description: { ja: "\u540C\u8272\u3067\u9023\u7D9A\u3059\u308B\u4E09\u3064\u306E\u523B\u5B50", en: "Three consecutive triplets in one suit" }, detector: dsl({ all: [{ form: "standard" }, { consecutiveTriplets: { atLeast: 3 } }] }), defaultHan: 2, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", exampleHand: ["m2", "m2", "m2", "m3", "m3", "m3", "m4", "m4", "m4", "s6", "s7", "s8", "p8", "p8"] }),
  entry({ id: "isshoku-sanjun", name: { ja: "\u4E00\u8272\u4E09\u9806", en: "Isshoku Sanjun" }, description: { ja: "\u540C\u4E00\u9806\u5B50\u304C\u4E09\u7D44\uFF08\u9580\u524D3\u98DC\u30FB\u55B0\u30442\u98DC\uFF09", en: "Three identical sequences (closed 3 / open 2)" }, detector: dsl({ all: [{ form: "standard" }, { identicalSequences: { atLeast: 3 } }] }), defaultHan: 3, openHan: 2, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", excludes: ["iipeikou"], exampleHand: ["p2", "p3", "p4", "p2", "p3", "p4", "p2", "p3", "p4", "s6", "s7", "s8", "z1", "z1"] }),
  // 三風刻: 風牌の刻子(槓子含む)三組＋風牌でない雀頭。風牌雀頭なら小四喜(役満)なので `none: pair{honor wind}` で
  // 小四喜/大四喜と排他（風牌4組目があれば雀頭は非風なので groups atLeast 3 で大四喜とも干渉しない）。DSL 純データ。
  entry({ id: "sanfuukou", name: { ja: "\u4E09\u98A8\u523B", en: "San Fuukou" }, description: { ja: "\u98A8\u724C\u306E\u523B\u5B50\u4E09\u7D44\u3068\u975E\u98A8\u724C\u306E\u96C0\u982D\uFF08\u5C0F\u56DB\u559C\u306E\u4E00\u6B69\u624B\u524D\uFF09", en: "Three wind triplets + a non-wind pair (one short of shousuushi)" }, detector: dsl({ all: [{ form: "standard" }, { groups: { honor: "wind", kind: "set", atLeast: 3 } }, { none: [{ pair: { honor: "wind" } }] }] }), defaultHan: 2, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", exampleHand: ["z1", "z1", "z1", "z2", "z2", "z2", "z3", "z3", "z3", "s6", "s7", "s8", "p8", "p8"] }),
  entry({ id: "uumensai", name: { ja: "\u4E94\u9580\u6589", en: "Uumensai" }, description: { ja: "\u842C\u5B50\u30FB\u7B52\u5B50\u30FB\u7D22\u5B50\u30FB\u98A8\u724C\u30FB\u4E09\u5143\u724C\u306E\u4E94\u9580\u3059\u3079\u3066\u3092\u542B\u3080", en: "Uses manzu, pinzu, souzu, a wind and a dragon" }, detector: dsl({ suitsUsed: { gates: true } }), defaultHan: 2, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", exampleHand: ["m1", "m2", "m3", "p7", "p8", "p9", "s4", "s5", "s6", "z1", "z1", "z1", "z5", "z5"] }),
  entry({ id: "aka-sanshoku", name: { ja: "\u8D64\u4E09\u8272", en: "Aka Sanshoku" }, description: { ja: "\u8D64\u4E94\u3092\u4E09\u8272\u3059\u3079\u3066\uFF08\u8D645m\u30FB\u8D645p\u30FB\u8D645s\uFF09\u6240\u6301\uFF08\u4E00\u756A\u8857\uFF09\u3002\u8D64\u724C\u3092\u898B\u308B\u305F\u3081 builtin", en: "A red five in each suit (ichibangai); builtin \u2014 needs aka inspection" }, detector: builtin("aka-sanshoku"), defaultHan: 2, tunable: "tunable", category: "localFamous", source: "mahjong-ichibangai local yaku", exampleHand: ["m4", "m5r", "m6", "p4", "p5r", "p6", "s4", "s5r", "s6", "s7", "s8", "s9", "z1", "z1"] }),
  entry({ id: "iipin-moogetsu", name: { ja: "\u4E00\u7B52\u6478\u6708", en: "Iipin Moogetsu" }, description: { ja: "\u6D77\u5E95\u30C4\u30E2\u3067\u4E00\u7B52\u548C\u4E86\uFF08\u6E80\u8CAB\u6271\u3044\uFF09", en: "Haitei tsumo on the 1-pin (mangan-class)" }, detector: dsl({ all: [{ ctx: { lastTile: "haitei", tsumoOnly: true } }, { winTile: { in: ["p1"] } }] }), defaultHan: 5, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", exampleHand: ["p1", "p2", "p3", "m2", "m3", "m4", "s5", "s6", "s7", "s7", "s8", "s9", "z1", "z1"] }),
  entry({ id: "chuupin-raoyo", name: { ja: "\u4E5D\u7B52\u6488\u9B5A", en: "Chuupin Raoyo" }, description: { ja: "\u6CB3\u5E95\u30ED\u30F3\u3067\u4E5D\u7B52\u548C\u4E86\uFF08\u6E80\u8CAB\u6271\u3044\uFF09", en: "Houtei ron on the 9-pin (mangan-class)" }, detector: dsl({ all: [{ ctx: { lastTile: "houtei", ronOnly: true } }, { winTile: { in: ["p9"] } }] }), defaultHan: 5, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku", exampleHand: ["p9", "p8", "p7", "m2", "m3", "m4", "s5", "s6", "s7", "s1", "s2", "s3", "z1", "z1"] }),
  // --- Famous-local YAKUMAN (builtin; engine yakuman-exclusion prevents double-counting). ---
  entry({ id: "shirenkou", name: { ja: "\u56DB\u9023\u523B", en: "Shirenkou" }, description: { ja: "\u540C\u8272\u3067\u9023\u7D9A\u3059\u308B\u56DB\u3064\u306E\u523B\u5B50\uFF08\u5F79\u6E80\uFF09", en: "Four consecutive triplets in one suit (yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { consecutiveTriplets: { atLeast: 4 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku", excludes: ["suuankou"], exampleHand: ["m2", "m2", "m2", "m3", "m3", "m3", "m4", "m4", "m4", "m5", "m5", "m5", "p8", "p8"] }),
  // 大七星: DSL ×2 yakuman. Note: as a DSL yakuman it scores ×2 regardless of scoring.doubleYakuman
  // (the authored value is the truth); rooms wanting ×1 set hanOverride:13. (Owner-authored-value rule.)
  entry({ id: "dai-shichisei", name: { ja: "\u5927\u4E03\u661F", en: "Dai-shichisei" }, description: { ja: "\u5B57\u4E00\u8272\u306E\u4E03\u5BFE\u5B50\uFF08\u4E03\u7A2E\u306E\u5B57\u724C\u5BFE\u5B50\u30FB\u30C0\u30D6\u30EB\u5F79\u6E80\uFF09", en: "Seven pairs of all seven honors (double yakuman)" }, detector: dsl({ all: [{ form: "chiitoi" }, { allTilesIn: { honors: true } }] }), kind: "yakuman", category: "localFamous", defaultHan: 26, yakumanMult: 2, tunable: "structural", source: "ja.wikipedia local yaku", excludes: ["tsuuiisou"], exampleHand: ["z1", "z1", "z2", "z2", "z3", "z3", "z4", "z4", "z5", "z5", "z6", "z6", "z7", "z7"] }),
  // --- NEW tile-set yakuman authored as DATA (tilesSubsetOf). Tunable: definitions vary by source —
  // the owner edits the `tiles` set to taste. (Proof the rule language pays off: no builtin needed.) ---
  entry({ id: "benikujaku", name: { ja: "\u7D05\u5B54\u96C0", en: "Benikujaku" }, description: { ja: "\u7D22\u5B50\u306E1\u30FB5\u30FB7\u30FB9\u3068\u4E2D\u306E\u307F\uFF08\u7DD1\u4E00\u8272\u306E\u5BFE\uFF09", en: "Only s1/s5/s7/s9 and the Red dragon" }, detector: dsl({ tilesSubsetOf: { tiles: ["s1", "s5", "s7", "s9", "z7"] } }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku (editable; definitions vary)", exampleHand: ["s1", "s1", "s1", "s5", "s5", "s5", "s7", "s7", "s7", "z7", "z7", "z7", "s9", "s9"] }),
  entry({ id: "kuro-iisou", name: { ja: "\u9ED2\u4E00\u8272", en: "Kuro-iisou" }, description: { ja: "\u7B52\u5B50\u306E1\u30FB2\u30FB4\u30FB8\u30FB9\u3068\u98A8\u724C\u306E\u307F\uFF08\u9ED2\u3044\u724C\u306E\u307F\uFF09", en: "Only p1/p2/p4/p8/p9 and the four winds" }, detector: dsl({ tilesSubsetOf: { tiles: ["p1", "p2", "p4", "p8", "p9", "z1", "z2", "z3", "z4"] } }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku (editable; definitions vary)", exampleHand: ["p1", "p1", "p1", "p2", "p2", "p2", "p4", "p4", "p4", "z1", "z1", "z1", "p8", "p8"] }),
  // 推不倒: point-symmetric tiles only. The allowed set is broad (14 base types), so "a hand within this
  // set" is far easier than 清一色/緑一色 — as a YAKUMAN it was too strong (owner call). Demoted to a
  // NORMAL 2-han local yaku; the value is tunable in the Yaku tab (bump back to yakuman if a room wants).
  entry({ id: "suiputou", name: { ja: "\u63A8\u4E0D\u5012", en: "Suiputou" }, description: { ja: "\u70B9\u5BFE\u79F0\uFF08\u4E0A\u4E0B\u9006\u3067\u3082\u540C\u3058\uFF09\u306E\u724C\u306E\u307F\uFF1A\u7B521-5/8/9\u30FB\u7D222/4/5/6/8/9\u30FB\u767D\uFF08\u901A\u5E38\u5F79\u30FB\u7DE8\u96C6\u53EF\uFF09", en: "Only point-symmetric tiles: p1-5/8/9, s2/4/5/6/8/9, White (normal yaku; editable)" }, detector: dsl({ tilesSubsetOf: { tiles: ["p1", "p2", "p3", "p4", "p5", "p8", "p9", "s2", "s4", "s5", "s6", "s8", "s9", "z5"] } }), defaultHan: 2, tunable: "tunable", category: "localFamous", source: "ja.wikipedia local yaku (editable; definitions vary)", exampleHand: ["p1", "p1", "p1", "p2", "p2", "p2", "p3", "p3", "p3", "z5", "z5", "z5", "p9", "p9"] }),
  // --- ORIGINAL yakuman authored as DATA (no new engine predicates). Definitions are owner-editable. ---
  entry({ id: "happoukyou", name: { ja: "\u516B\u65B9\u93E1", en: "Octagonal Mirror" }, description: { ja: "\u6E05\u4E00\u8272\u306E\u4E8C\u76C3\u53E3\uFF08\u5B57\u724C\u306A\u3057\u30FB\u540C\u7A2E\u306E\u6570\u724C\u306E\u307F\u3067\u4E8C\u76C3\u53E3\uFF09", en: "Ryanpeikou in a single number suit, no honors" }, detector: dsl({ all: [{ form: "standard" }, { identicalSequences: { pairsAtLeast: 2 } }, { suitsUsed: { numberSuits: "one", honors: "forbidden" } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["ryanpeikou", "iipeikou", "chiitoitsu", "chinitsu"], exampleHand: ["m2", "m3", "m4", "m2", "m3", "m4", "m6", "m7", "m8", "m6", "m7", "m8", "m1", "m1"] }),
  entry({ id: "hyakkaryouran", name: { ja: "\u767E\u82B1\u7E5A\u4E71", en: "Hundred Flowers" }, description: { ja: "\u5076\u6570\u306E\u6570\u724C\u306E\u307F\uFF082\u30FB4\u30FB6\u30FB8\uFF09", en: "Only even-numbered simples (2/4/6/8)" }, detector: dsl({ tilesSubsetOf: { tiles: ["m2", "m4", "m6", "m8", "p2", "p4", "p6", "p8", "s2", "s4", "s6", "s8"] } }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", exampleHand: ["m2", "m2", "m2", "m4", "m4", "m4", "p6", "p6", "p6", "s8", "s8", "s8", "p2", "p2"] }),
  entry({ id: "isshoku-yonjun", name: { ja: "\u4E00\u8272\u56DB\u9806", en: "Four Identical Sequences" }, description: { ja: "\u540C\u7A2E\u30FB\u540C\u4E00\u306E\u9806\u5B50\u3092\u56DB\u7D44", en: "Four identical sequences in one suit" }, detector: dsl({ all: [{ form: "standard" }, { identicalSequences: { atLeast: 4 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "ja.wikipedia local yaku", excludes: ["isshoku-sanjun", "ryanpeikou", "iipeikou", "chiitoitsu"], exampleHand: ["m2", "m3", "m4", "m2", "m3", "m4", "m2", "m3", "m4", "m2", "m3", "m4", "p8", "p8"] }),
  // --- ORIGINAL flush-family yakuman authored as DATA (no new engine predicates; owner-editable). ---
  // These pair 清一色 (one number suit, no honors) / 混一色 (one suit + honors) with another structural
  // shape, raising the bar from a normal 6-han chinitsu to genuine yakuman rarity. Each lists the normal
  // yaku it subsumes in `excludes` and was checked against every catalog exampleHand to shadow none.
  // 清一対々和: 清一色（同種数牌のみ）かつ全面子刻子。鳴いても成立する点で四暗刻より広い（門前限定でない）。
  // 門前なら自然に四暗刻も複合（どちらも役満なので干渉しない）。
  entry({ id: "seiren-toukou", name: { ja: "\u6E05\u4E00\u5BFE\u3005\u548C", en: "Pure Flush Triplets" }, description: { ja: "\u6E05\u4E00\u8272\uFF08\u540C\u7A2E\u6570\u724C\u306E\u307F\u30FB\u5B57\u724C\u306A\u3057\uFF09\u304B\u3064\u5168\u9762\u5B50\u304C\u523B\u5B50\uFF08\u5BFE\u3005\u548C\u306E\u6E05\u4E00\u8272\u7248\u30FB\u5F79\u6E80\uFF09", en: "A single-number-suit flush in which every set is a triplet (chinitsu + toitoi; yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { suitsUsed: { numberSuits: "one", honors: "forbidden" } }, { groups: { kind: "set", atLeast: 4 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["chinitsu", "toitoi", "sanankou", "tanyao", "honroutou", "sanshoku-doukou"], exampleHand: ["p2", "p2", "p2", "p4", "p4", "p4", "p6", "p6", "p6", "p8", "p8", "p8", "p3", "p3"] }),
  // 清一気通貫: 清一色で 123-456-789 の三連順子（一気通貫の清一色版・役満）。
  entry({ id: "chin-ittsuu", name: { ja: "\u6E05\u4E00\u6C17\u901A\u8CAB", en: "Pure Straight Flush" }, description: { ja: "\u6E05\u4E00\u8272\uFF08\u540C\u7A2E\u6570\u724C\u306E\u307F\u30FB\u5B57\u724C\u306A\u3057\uFF09\u3067 123-456-789 \u306E\u4E00\u6C17\u901A\u8CAB\uFF08\u5F79\u6E80\uFF09", en: "A single-suit flush containing the 123-456-789 straight (chinitsu + ittsuu; yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { suitsUsed: { numberSuits: "one", honors: "forbidden" } }, { consecutiveSequences: { atLeast: 3 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["chinitsu", "ittsuu", "tochuu-ittsuu", "nishoku-doujun", "tanyao", "iipeikou"], exampleHand: ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8", "s9", "s2", "s3", "s4", "s5", "s5"] }),
  // 混一気通貫: 混一色（一つの数牌色＋字牌）で 123-456-789 の三連順子（一気通貫の混一色版・役満）。
  entry({ id: "hon-ittsuu", name: { ja: "\u6DF7\u4E00\u6C17\u901A\u8CAB", en: "Half-Flush Straight" }, description: { ja: "\u6DF7\u4E00\u8272\uFF08\u4E00\u3064\u306E\u6570\u724C\u8272\uFF0B\u5B57\u724C\uFF09\u3067 123-456-789 \u306E\u4E00\u6C17\u901A\u8CAB\uFF08\u5F79\u6E80\uFF09", en: "A half-flush (one number suit + honors) containing the 123-456-789 straight (yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { suitsUsed: { numberSuits: "one", honors: "required" } }, { consecutiveSequences: { atLeast: 3 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["honitsu", "ittsuu", "tochuu-ittsuu"], exampleHand: ["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8", "m9", "z5", "z5", "z5", "m1", "m1"] }),
  // 清純全帯么九: 清一色（同種数牌のみ・字牌なし）で全ての面子が端牌(1/9)を含む（純全帯么九の清一色版・役満）。
  entry({ id: "chin-junchan", name: { ja: "\u6E05\u7D14\u5168\u5E2F\u4E48\u4E5D", en: "Pure Flush Terminals" }, description: { ja: "\u6E05\u4E00\u8272\uFF08\u540C\u7A2E\u6570\u724C\u306E\u307F\u30FB\u5B57\u724C\u306A\u3057\uFF09\u3067\u5168\u9762\u5B50\u304C\u7AEF\u724C(1\u304B9)\u3092\u542B\u3080\uFF08\u7D14\u5168\u5E2F\u4E48\u4E5D\u306E\u6E05\u4E00\u8272\u7248\u30FB\u5F79\u6E80\uFF09", en: "A single-suit flush in which every set contains a terminal (chinitsu + junchan; yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { suitsUsed: { numberSuits: "one", honors: "forbidden" } }, { everyGroupTouches: { terminal: true } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["chinitsu", "junchan", "chanta", "ittsuu", "tochuu-ittsuu", "iipeikou", "honroutou"], exampleHand: ["m1", "m1", "m1", "m1", "m2", "m3", "m7", "m8", "m9", "m7", "m8", "m9", "m9", "m9"] }),
  // 清三連刻: 清一色（同種数牌のみ・字牌なし）で同色連続三刻（三連刻の清一色版・役満）。
  entry({ id: "chin-sanrenkou", name: { ja: "\u6E05\u4E09\u9023\u523B", en: "Pure Flush Triple Run" }, description: { ja: "\u6E05\u4E00\u8272\uFF08\u540C\u7A2E\u6570\u724C\u306E\u307F\u30FB\u5B57\u724C\u306A\u3057\uFF09\u3067\u9023\u7D9A\u3059\u308B\u4E09\u3064\u306E\u523B\u5B50\uFF08\u4E09\u9023\u523B\u306E\u6E05\u4E00\u8272\u7248\u30FB\u5F79\u6E80\uFF09", en: "A single-suit flush with three consecutive triplets (chinitsu + sanrenkou; yakuman)" }, detector: dsl({ all: [{ form: "standard" }, { suitsUsed: { numberSuits: "one", honors: "forbidden" } }, { consecutiveTriplets: { atLeast: 3 } }] }), kind: "yakuman", category: "localFamous", defaultHan: 13, yakumanMult: 1, tunable: "structural", source: "original (editable)", excludes: ["chinitsu", "sanrenkou", "sanankou", "tanyao"], exampleHand: ["p2", "p2", "p2", "p3", "p3", "p3", "p4", "p4", "p4", "p6", "p7", "p8", "p5", "p5"] })
];

// src/yaku/engine.ts
import { baseOfAka as baseOfAka9 } from "@mj/protocol";

// src/score/fu.ts
import { baseOfAka as baseOfAka8 } from "@mj/protocol";
function isSet3(g) {
  return g.kind === "triplet" || g.kind === "quad";
}
function setTile(g) {
  return baseOfAka8(g.tiles[0]);
}
function isTerminalOrHonor(t) {
  return isYaochuu(baseOfAka8(t));
}
var DRAGONS3 = /* @__PURE__ */ new Set(["z5", "z6", "z7"]);
function groupFu(g) {
  const th = isTerminalOrHonor(setTile(g));
  if (g.kind === "quad") {
    return g.open ? th ? 16 : 8 : th ? 32 : 16;
  }
  if (g.kind === "triplet") {
    return g.open ? th ? 4 : 2 : th ? 8 : 4;
  }
  return 0;
}
function roundUp(total, step) {
  if (step === 1) return total;
  return Math.ceil(total / 10) * 10;
}
function hasYaku(yr, id) {
  return yr.yaku.some((y) => y.id === id);
}
function computeFu(ph, yr, ctx, rs) {
  const step = rs.scoring.fuRounding ?? 10;
  if (ph.form === "chiitoi") {
    return { total: 25, items: [{ reason: "chiitoi", fu: 25 }] };
  }
  if (ph.form === "kokushi") {
    return { total: 0, items: [{ reason: "kokushi (yakuman; fu n/a)", fu: 0 }] };
  }
  const pinfu2 = hasYaku(yr, "pinfu");
  if (pinfu2) {
    if (ph.winBy === "tsumo") {
      return { total: 20, items: [{ reason: "pinfu tsumo (fixed 20)", fu: 20 }] };
    }
    return {
      total: 30,
      items: [
        { reason: "base", fu: 20 },
        { reason: "menzen ron", fu: 10 }
      ]
    };
  }
  const items = [];
  let total = 20;
  items.push({ reason: "base", fu: 20 });
  if (ph.concealed && ph.winBy === "ron") {
    total += 10;
    items.push({ reason: "menzen ron", fu: 10 });
  }
  if (ph.winBy === "tsumo") {
    total += 2;
    items.push({ reason: "tsumo", fu: 2 });
  }
  if (ph.wait === "kanchan" || ph.wait === "penchan" || ph.wait === "tanki") {
    total += 2;
    items.push({ reason: `${ph.wait} wait`, fu: 2 });
  }
  if (ph.pair) {
    const pt = baseOfAka8(ph.pair.tiles[0]);
    let pairFu = 0;
    if (DRAGONS3.has(pt)) pairFu += 2;
    const isRound = !!ctx.roundWind && pt === baseOfAka8(ctx.roundWind);
    const isSeat = !!ctx.seatWind && pt === baseOfAka8(ctx.seatWind);
    if (isRound && isSeat) {
      pairFu += rs.scoring.doubleWindPairFu === 2 ? 2 : 4;
    } else {
      if (isRound) pairFu += 2;
      if (isSeat) pairFu += 2;
    }
    if (pairFu > 0) {
      total += pairFu;
      items.push({ reason: "yakuhai pair", fu: pairFu });
    }
  }
  let ronShanponApplied = false;
  for (const g of ph.groups) {
    if (!isSet3(g)) continue;
    let effective = g;
    if (!ronShanponApplied && ph.winBy === "ron" && ph.wait === "shanpon" && g.kind === "triplet" && !g.open && setTile(g) === baseOfAka8(ph.winTile)) {
      effective = { ...g, open: true };
      ronShanponApplied = true;
    }
    const fu = groupFu(effective);
    if (fu > 0) {
      total += fu;
      items.push({ reason: `${effective.open ? "open" : "closed"} ${effective.kind} ${setTile(g)}`, fu });
    }
  }
  if (!ph.concealed && ph.winBy === "ron" && total === 20) {
    total = 30;
    items.push({ reason: "open pinfu-shape ron (floor 30)", fu: 10 });
  }
  return { total: roundUp(total, step), items };
}

// src/score/base.ts
var MANGAN_BASE = 2e3;
function effectiveLimitMode(rs) {
  return rs.scoring.limitMode ?? (rs.scoring.aotenjou ? "aotenjou" : "capped");
}
function ladderBaseFromHan(han) {
  if (han >= 13) return 8e3;
  if (han >= 11) return 6e3;
  if (han >= 8) return 4e3;
  if (han >= 6) return 3e3;
  return MANGAN_BASE;
}
function subManganBase(fu, han, rs) {
  let base = fu * 2 ** (2 + han);
  if (rs.scoring.kiriageMangan && (han === 4 && fu === 30 || han === 3 && fu === 60)) {
    base = MANGAN_BASE;
  }
  if (base > MANGAN_BASE) base = MANGAN_BASE;
  return base;
}
function steppedUnits(han) {
  if (han < 6) return 1;
  if (han < 8) return 1.5;
  if (han < 11) return 2;
  if (han < 13) return 3;
  return 4 + Math.floor((han - 13) / 3);
}
function normalBase(fu, han, rs) {
  const mode = effectiveLimitMode(rs);
  if (mode === "aotenjou") {
    const b = BigInt(fu) * 2n ** BigInt(2 + han);
    return Number(b);
  }
  if (mode === "stepped") {
    if (han < 5) return subManganBase(fu, han, rs);
    return MANGAN_BASE * steppedUnits(han);
  }
  if (han >= 5) return ladderBaseFromHan(han);
  return subManganBase(fu, han, rs);
}

// src/yaku/engine.ts
function tilesOf(ph) {
  const out = [];
  for (const g of ph.groups) for (const t of g.tiles) out.push(baseOfAka9(t));
  if (ph.pair) for (const t of ph.pair.tiles) out.push(baseOfAka9(t));
  for (const mm of ph.melds) for (const t of mm.tiles) out.push(baseOfAka9(t));
  return out;
}
function detectionCtx(ctx) {
  return {
    winBy: ctx.winBy,
    firstTurn: ctx.firstTurn,
    lastTile: ctx.lastTile,
    isDealer: ctx.isDealer,
    menzen: ctx.menzen,
    afterKan: ctx.afterKan
  };
}
function excludedIds(catalog, presentIds, overrides) {
  const drop = /* @__PURE__ */ new Set();
  for (const def of catalog) {
    if (!def.excludes || !presentIds.has(def.id)) continue;
    for (const d of def.excludes) {
      if (overrides?.some(([keeper, keptAlso]) => keeper === def.id && keptAlso === d)) continue;
      drop.add(d);
    }
  }
  return drop;
}
function dropFrom(list, drop) {
  if (drop.size === 0) return;
  for (let i = list.length - 1; i >= 0; i--) {
    if (drop.has(list[i].id)) list.splice(i, 1);
  }
}
function detectYaku(ph, ctx, rs, catalog, tiles) {
  const fullTiles = tiles ?? tilesOf(ph);
  const dctx = detectionCtx(ctx);
  const yaku = [];
  const yakuman = [];
  for (const def of catalog) {
    if (def.category === "dora") continue;
    const room = rs.enabledYaku[def.id];
    if (!room || !room.enabled) continue;
    if (def.kind === "yakuman") {
      const mult = runYakumanDetector(def, ph, ctx, rs, fullTiles, dctx, room.hanOverride);
      if (mult > 0) yakuman.push({ id: def.id, mult });
    } else {
      const han = runNormalDetector(def, ph, ctx, rs, dctx, room.hanOverride, fullTiles);
      if (han > 0) yaku.push({ id: def.id, han });
    }
  }
  const overrides = rs.scoring.exclusionOverrides;
  dropFrom(yaku, excludedIds(catalog, new Set(yaku.map((y) => y.id)), overrides));
  dropFrom(yakuman, excludedIds(catalog, new Set(yakuman.map((y) => y.id)), overrides));
  const hasYakuhaiOrMenzenYaku = yaku.length > 0 || yakuman.length > 0;
  if (yakuman.length > 0) {
    const keepNormal = rs.scoring.yakumanStacking === "withNormalYaku";
    return { yaku: keepNormal ? yaku : [], yakuman, isYakuman: true, hasYakuhaiOrMenzenYaku };
  }
  return { yaku, yakuman: [], isYakuman: false, hasYakuhaiOrMenzenYaku };
}
function runYakumanDetector(def, ph, ctx, rs, fullTiles, dctx, hanOverride) {
  let mult;
  if (def.detector.type === "builtin") {
    const d = YAKUMAN_DETECTORS[def.detector.key];
    mult = d ? d(ph, ctx, rs, fullTiles) : 0;
  } else {
    mult = evalConstraint(def.detector.constraint, ph, dctx, fullTiles) ? def.yakumanMult ?? 1 : 0;
  }
  if (mult <= 0) return 0;
  if (hanOverride !== void 0) return Math.max(1, Math.floor(hanOverride / 13));
  return mult;
}
function runNormalDetector(def, ph, ctx, rs, dctx, hanOverride, tiles) {
  let natural;
  if (def.detector.type === "builtin") {
    const d = BUILTIN_DETECTORS[def.detector.key];
    natural = d ? d(ph, ctx, rs, tiles) : 0;
  } else {
    natural = evalConstraint(def.detector.constraint, ph, dctx, tiles) ? naturalHanFor(def, ph) : 0;
  }
  if (natural <= 0) return 0;
  if (hanOverride !== void 0) return hanOverride;
  return natural;
}
function naturalHanFor(def, ph) {
  if (!ph.concealed && def.openHan !== void 0) return def.openHan;
  return def.defaultHan;
}
function variantPoints(ph, r, ctx, rs) {
  if (r.yakuman.length > 0) {
    const mult = r.yakuman.reduce((a, y) => a + y.mult, 0);
    return [1, mult * 1e6 + r.yakuman.length];
  }
  const yakuHan = r.yaku.reduce((a, y) => a + y.han, 0);
  const doraHan = ctx.doraCount + ctx.akaCount + (rs.scoring.uraDora === false ? 0 : ctx.uraCount) + ctx.kitaCount + (ctx.flowerCount ?? 0);
  const fu = computeFu(ph, r, ctx, rs).total;
  return [0, normalBase(fu, yakuHan + doraHan, rs) * 100 + r.yaku.length];
}
function variantTiebreak(r) {
  const ids = [...r.yakuman.map((y) => y.id), ...r.yaku.map((y) => y.id)].sort();
  return ids.join(",");
}
function detectBest(parses, ctx, rs, catalog, tiles) {
  if (parses.length === 0) {
    if (tiles && tiles.length === 14 && ctx.firstTurn && ctx.winBy === "tsumo") {
      const def = catalog.find((d) => d.id === "juusanbuttou" && d.kind === "yakuman");
      const room = rs.enabledYaku["juusanbuttou"];
      if (def && def.detector.type === "builtin" && room?.enabled) {
        const counts = /* @__PURE__ */ new Map();
        for (const t of tiles) {
          const b = baseOfAka9(t);
          counts.set(b, (counts.get(b) ?? 0) + 1);
        }
        let winTile;
        let pairs = 0;
        for (const [b, n] of counts) if (n === 2) {
          pairs++;
          winTile = b;
        }
        if (pairs === 1 && winTile) {
          const stub = { form: "standard", groups: [], pair: void 0, melds: [], winTile, winBy: "tsumo", concealed: true, wait: "tanki" };
          const d = YAKUMAN_DETECTORS[def.detector.key];
          if (d && d(stub, ctx, rs, tiles) > 0) {
            const ho = room.hanOverride;
            const mult = ho !== void 0 ? Math.max(1, Math.floor(ho / 13)) : def.yakumanMult ?? 1;
            return { yaku: [], yakuman: [{ id: "juusanbuttou", mult }], isYakuman: true, hasYakuhaiOrMenzenYaku: true };
          }
        }
      }
    }
    return { yaku: [], yakuman: [], isYakuman: false, hasYakuhaiOrMenzenYaku: false };
  }
  let best;
  let bestTier = -Infinity;
  let bestScore = -Infinity;
  let bestKey = "";
  for (const ph of parses) {
    const r = detectYaku(ph, ctx, rs, catalog, tiles);
    const [tier, s] = variantPoints(ph, r, ctx, rs);
    const key = variantTiebreak(r);
    if (tier > bestTier || tier === bestTier && s > bestScore || tier === bestTier && s === bestScore && key < bestKey) {
      best = r;
      bestTier = tier;
      bestScore = s;
      bestKey = key;
    }
  }
  return best;
}

// src/yaku/reachable.ts
import { baseOfAka as baseOfAka10, isHonor as isHonor6 } from "@mj/protocol";
var DRAGONS4 = ["z5", "z6", "z7"];
var suitOf = (t) => baseOfAka10(t)[0];
function violations(tiles, pred) {
  let n = 0;
  for (const t of tiles) if (!pred(t)) n++;
  return n;
}
function dominantNumberSuit(tiles) {
  const c = { m: 0, p: 0, s: 0 };
  for (const t of tiles) {
    const s = suitOf(t);
    if (s === "m" || s === "p" || s === "s") c[s]++;
  }
  return ["m", "p", "s"].reduce((a, b) => c[b] > c[a] ? b : a, "m");
}
function yaochuuAdjacent(t, allowHonors) {
  if (isHonor6(t)) return allowHonors;
  const r = tileKey(t).rank;
  return r <= 3 || r >= 7;
}
function tileSetPredicate(c) {
  if (!c) return null;
  if ("tilesSubsetOf" in c) {
    const allowed = new Set(c.tilesSubsetOf.tiles.map(baseOfAka10));
    return (t) => allowed.has(baseOfAka10(t));
  }
  if ("allTilesIn" in c) {
    const f = c.allTilesIn;
    return (t) => {
      const b = baseOfAka10(t);
      const honor = isHonor6(b);
      if (f.honors !== void 0 && honor !== f.honors) return false;
      if (f.isYaochuu !== void 0 && isYaochuu(b) !== f.isYaochuu) return false;
      if (f.suit !== void 0) {
        if (honor) return false;
        if (suitOf(b) !== f.suit) return false;
      }
      if (f.rankRange !== void 0) {
        if (honor) return false;
        const r = tileKey(b).rank;
        if (r < f.rankRange[0] || r > f.rankRange[1]) return false;
      }
      return true;
    };
  }
  return null;
}
function findSuitsUsed(c) {
  if (!c) return null;
  if ("suitsUsed" in c) return c.suitsUsed;
  for (const key of ["all", "any"]) {
    if (key in c) {
      for (const sub of c[key]) {
        const r = findSuitsUsed(sub);
        if (r) return r;
      }
    }
  }
  return null;
}
function findEveryGroupTouches(c) {
  if (!c) return null;
  if ("everyGroupTouches" in c) return c.everyGroupTouches;
  for (const key of ["all", "any"]) {
    if (key in c) {
      for (const sub of c[key]) {
        const r = findEveryGroupTouches(sub);
        if (r) return r;
      }
    }
  }
  return null;
}
function estimateBuiltin(id, tiles, ctx) {
  switch (id) {
    case "tanyao":
      return { distance: violations(tiles, (t) => !isYaochuu(t)), reason: "\u4E48\u4E5D\u724C\u3092\u5165\u308C\u66FF\u3048\u308B" };
    case "chinitsu": {
      const suit = dominantNumberSuit(tiles);
      return { distance: violations(tiles, (t) => suitOf(t) === suit), reason: `${suitName(suit)}\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B` };
    }
    case "honitsu": {
      const suit = dominantNumberSuit(tiles);
      return { distance: violations(tiles, (t) => suitOf(t) === suit || isHonor6(t)), reason: `${suitName(suit)}\uFF0B\u5B57\u724C\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B` };
    }
    case "honroutou":
      return { distance: violations(tiles, (t) => isYaochuu(t)), reason: "\u4E2D\u5F35\u724C\u3092\u5165\u308C\u66FF\u3048\u308B\uFF08\u4E48\u4E5D\u724C\u306E\u307F\uFF09" };
    case "chanta":
      return { distance: violations(tiles, (t) => yaochuuAdjacent(t, true)), reason: "\u5404\u9762\u5B50\u3092\u4E48\u4E5D\u7D61\u307F\u306B\u5BC4\u305B\u308B" };
    case "junchan":
      return { distance: violations(tiles, (t) => !isHonor6(t) && yaochuuAdjacent(t, false)), reason: "\u5404\u9762\u5B50\u3092\u8001\u982D\u7D61\u307F\u306B\u5BC4\u305B\u308B\uFF08\u5B57\u724C\u306A\u3057\uFF09" };
    case "toitoi":
      return { distance: lonelyTileCount(tiles), reason: "\u5BFE\u5B50\u30FB\u523B\u5B50\u306B\u5BC4\u305B\u308B\uFF08\u9806\u5B50\u306A\u3057\uFF09" };
    // Yakuhai: a triplet of a value honor. Distance = how many copies you still need toward 3.
    case "yakuhai-haku":
      return yakuhaiDistance(tiles, "z5", "\u767D");
    case "yakuhai-hatsu":
      return yakuhaiDistance(tiles, "z6", "\u767C");
    case "yakuhai-chun":
      return yakuhaiDistance(tiles, "z7", "\u4E2D");
    case "yakuhai-round":
      return ctx.roundWind ? yakuhaiDistance(tiles, baseOfAka10(ctx.roundWind), "\u5834\u98A8") : null;
    case "yakuhai-seat":
      return ctx.seatWind ? yakuhaiDistance(tiles, baseOfAka10(ctx.seatWind), "\u81EA\u98A8") : null;
    // Yakuman membership/shape ones we CAN judge as tile-sets.
    case "tsuuiisou":
      return { distance: violations(tiles, (t) => isHonor6(t)), reason: "\u6570\u724C\u3092\u5165\u308C\u66FF\u3048\u308B\uFF08\u5B57\u724C\u306E\u307F\uFF09" };
    case "chinroutou":
      return { distance: violations(tiles, (t) => isTerminal(t)), reason: "\u8001\u982D\u724C\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B" };
    case "ryuuiisou": {
      const green = /* @__PURE__ */ new Set(["s2", "s3", "s4", "s6", "s8", "z6"]);
      return { distance: violations(tiles, (t) => green.has(baseOfAka10(t))), reason: "\u7DD1\u724C\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B" };
    }
    case "daisangen":
      return tripletSetDistance(tiles, DRAGONS4, 3, "\u4E09\u5143\u724C\u306E\u523B\u5B50\u3092\u4E09\u3064\u63C3\u3048\u308B");
    case "shousuushi":
    case "daisuushi":
      return tripletSetDistance(tiles, ["z1", "z2", "z3", "z4"], id === "daisuushi" ? 4 : 3, "\u98A8\u724C\u306E\u523B\u5B50\u3092\u63C3\u3048\u308B");
    default:
      return null;
  }
}
var suitName = (s) => s === "m" ? "\u842C\u5B50" : s === "p" ? "\u7B52\u5B50" : "\u7D22\u5B50";
function yakuhaiDistance(tiles, honor, label) {
  let copies = 0;
  for (const t of tiles) if (baseOfAka10(t) === honor) copies++;
  return { distance: Math.max(0, 3 - copies), reason: `${label}\uFF08${honor}\uFF09\u306E\u523B\u5B50\u3092\u4F5C\u308B` };
}
function tripletSetDistance(tiles, set, needTriplets, reason) {
  const copies = /* @__PURE__ */ new Map();
  for (const t of tiles) {
    const b = baseOfAka10(t);
    if (set.includes(b)) copies.set(b, (copies.get(b) ?? 0) + 1);
  }
  const held = [...set].map((t) => Math.min(3, copies.get(t) ?? 0)).sort((a, b) => b - a);
  let dist = 0;
  for (let i = 0; i < needTriplets; i++) dist += 3 - (held[i] ?? 0);
  return { distance: dist, reason };
}
function lonelyTileCount(tiles) {
  const c = /* @__PURE__ */ new Map();
  for (const t of tiles) {
    const b = baseOfAka10(t);
    c.set(b, (c.get(b) ?? 0) + 1);
  }
  let lonely = 0;
  for (const n of c.values()) if (n === 1) lonely++;
  return lonely;
}
function hasUnjudgeableLeaf(c) {
  if ("identicalSequences" in c || "consecutiveTriplets" in c || "consecutiveSequences" in c || "sameSequenceSuits" in c || "groups" in c || "pair" in c || "winTile" in c || "wait" in c) return true;
  for (const key of ["all", "any", "none"]) {
    if (key in c) {
      for (const sub of c[key]) if (hasUnjudgeableLeaf(sub)) return true;
    }
  }
  return false;
}
function hasChiitoiForm(c) {
  if ("form" in c) return c.form === "chiitoi";
  for (const key of ["all", "any", "none"]) {
    if (key in c) {
      for (const sub of c[key]) if (hasChiitoiForm(sub)) return true;
    }
  }
  return false;
}
function chiitoiFloor(tiles) {
  const counts = /* @__PURE__ */ new Map();
  for (const t of tiles) {
    const b = baseOfAka10(t);
    counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  let pairs = 0;
  for (const n of counts.values()) if (n >= 2) pairs++;
  return Math.max(0, 7 - pairs);
}
function estimateDsl(constraint, tiles) {
  if (hasUnjudgeableLeaf(constraint)) return null;
  const chiitoi = hasChiitoiForm(constraint);
  const floor = chiitoi ? chiitoiFloor(tiles) : 0;
  const pred = tileSetPredicate(constraint) ?? unwrapTileSet(constraint);
  if (pred) return { distance: Math.max(violations(tiles, pred), floor), reason: "\u6307\u5B9A\u724C\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B" };
  const su = findSuitsUsed(constraint);
  if (su && su.numberSuits === "one") {
    const suit = dominantNumberSuit(tiles);
    const allowHonor = su.honors !== "forbidden";
    return {
      distance: violations(tiles, (t) => suitOf(t) === suit || allowHonor && isHonor6(t)),
      reason: `${suitName(suit)}${allowHonor ? "\uFF0B\u5B57\u724C" : ""}\u4EE5\u5916\u3092\u5165\u308C\u66FF\u3048\u308B`
    };
  }
  const eg = findEveryGroupTouches(constraint);
  if (eg) {
    const allowHonors = eg.honor !== false && !findHonorsForbidden(constraint);
    const pred2 = (t) => eg.terminal === true || eg.yaochuu === true ? yaochuuAdjacent(t, allowHonors) : isYaochuu(t) || allowHonors && isHonor6(t);
    return { distance: violations(tiles, pred2), reason: "\u5404\u9762\u5B50\u3092\u4E48\u4E5D\u7D61\u307F\u306B\u5BC4\u305B\u308B" };
  }
  return null;
}
function unwrapTileSet(c) {
  for (const key of ["all", "any"]) {
    if (key in c) {
      for (const sub of c[key]) {
        const r = tileSetPredicate(sub);
        if (r) return r;
      }
    }
  }
  return null;
}
function findHonorsForbidden(c) {
  const su = findSuitsUsed(c);
  return su?.honors === "forbidden";
}
function reachableYaku(concealed, melds, ctx, rs, catalog, opts) {
  const limit = opts?.limit ?? 6;
  const maxDistance = opts?.maxDistance ?? 6;
  const tiles = [...concealed];
  for (const m of melds) if (m.kind !== "kita") for (const t of m.tiles) tiles.push(t);
  const out = [];
  for (const def of catalog) {
    if (def.category === "dora") continue;
    const room = rs.enabledYaku[def.id];
    if (!room || !room.enabled) continue;
    let est = null;
    if (def.detector.type === "builtin") {
      est = estimateBuiltin(def.id, tiles, ctx);
    } else {
      est = estimateBuiltin(def.id, tiles, ctx) ?? estimateDsl(def.detector.constraint, tiles);
    }
    if (!est) continue;
    const isYakuman = def.kind === "yakuman";
    if (est.distance > maxDistance) continue;
    if (isYakuman && est.distance > Math.min(maxDistance, 4)) continue;
    const value = room.hanOverride ?? def.defaultHan;
    out.push({
      id: def.id,
      nameJa: def.name.ja,
      nameEn: def.name.en,
      value,
      isYakuman,
      distance: est.distance,
      reason: est.reason
    });
  }
  out.sort((a, b) => {
    if (a.distance !== b.distance) return a.distance - b.distance;
    if (a.isYakuman !== b.isYakuman) return a.isYakuman ? -1 : 1;
    if (a.value !== b.value) return b.value - a.value;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return out.slice(0, limit);
}

// src/score/payments.ts
function roundUp100(n) {
  return Math.ceil(n / 100) * 100;
}
function paymentMatrix(args) {
  const { base, winner, by, from, dealer, players, rs, honba, pao } = args;
  const pay = new Array(players).fill(0);
  const honbaTotal = honba * rs.points.honbaValue;
  const dealerWins = args.dealerWins ?? winner === dealer;
  if (by === "ron") {
    if (from === null) throw new Error("ron requires a discarder");
    const value = roundUp100(base * (dealerWins ? 6 : 4)) + honbaTotal;
    if (pao !== null && pao !== from) {
      const half = roundUp100((dealerWins ? 6 : 4) * base / 2);
      const otherHalf = roundUp100(base * (dealerWins ? 6 : 4)) - half;
      pay[pao] -= half;
      pay[from] -= otherHalf + honbaTotal;
      pay[winner] += value;
    } else {
      pay[from] -= value;
      pay[winner] += value;
    }
    return pay;
  }
  const honbaPer = players > 1 ? honbaTotal / (players - 1) : 0;
  if (pao !== null) {
    const total = (dealerWins ? roundUp100(base * 2) * (players - 1) : roundUp100(base * 2) + roundUp100(base) * (players - 2)) + honbaTotal;
    pay[pao] -= total;
    pay[winner] += total;
    return pay;
  }
  const tsumoLoss = rs.flags.tsumoLoss === true;
  let gain = 0;
  for (let s = 0; s < players; s++) {
    if (s === winner) continue;
    let share;
    if (dealerWins) {
      share = roundUp100(base * 2);
    } else if (tsumoLoss) {
      share = roundUp100(base);
    } else {
      share = s === dealer ? roundUp100(base * 2) : roundUp100(base);
    }
    share += honbaPer;
    pay[s] -= share;
    gain += share;
  }
  pay[winner] += gain;
  return pay;
}

// src/dora.ts
import { baseOfAka as baseOfAka11, isFlower } from "@mj/protocol";
function doraFromIndicator(indicator) {
  if (isFlower(indicator)) return indicator;
  const k = tileKey(indicator);
  if (k.suit < 3) {
    const nextRank2 = k.rank === 9 ? 1 : k.rank + 1;
    const s = k.suit === 0 ? "m" : k.suit === 1 ? "p" : "s";
    return `${s}${nextRank2}`;
  }
  if (k.rank <= 4) {
    const nextRank2 = k.rank === 4 ? 1 : k.rank + 1;
    return `z${nextRank2}`;
  }
  const nextRank = k.rank === 7 ? 5 : k.rank + 1;
  return `z${nextRank}`;
}
function countDora(tiles, indicators) {
  if (indicators.length === 0) return 0;
  const targets = indicators.map(doraFromIndicator);
  let n = 0;
  for (const t of tiles) {
    const b = baseOfAka11(t);
    for (const target of targets) if (b === target) n++;
  }
  return n;
}
function countDoraNeighbors(tiles, indicators) {
  if (indicators.length === 0) return 0;
  const targets = indicators.map(doraFromIndicator).map(baseOfAka11);
  const targetKeys = /* @__PURE__ */ new Set();
  const neighborKeys = /* @__PURE__ */ new Set();
  for (const tgt of targets) {
    const k = tileKey(tgt);
    if (k.suit === 3) continue;
    targetKeys.add(`${k.suit}:${k.rank}`);
    if (k.rank - 1 >= 1) neighborKeys.add(`${k.suit}:${k.rank - 1}`);
    if (k.rank + 1 <= 9) neighborKeys.add(`${k.suit}:${k.rank + 1}`);
  }
  if (neighborKeys.size === 0) return 0;
  let halves = 0;
  for (const t of tiles) {
    const k = tileKey(t);
    if (k.suit === 3) continue;
    const key = `${k.suit}:${k.rank}`;
    if (targetKeys.has(key)) continue;
    if (neighborKeys.has(key)) halves++;
  }
  return halves;
}

// src/score/score.ts
function gainOf(payments, winner) {
  return payments[winner] ?? 0;
}
function score(parses, ctx, rs, catalog = YAKU_CATALOG, tiles, opts = {}) {
  const players = opts.players ?? rs.players;
  const winner = ctx.winner;
  const dealer = opts.dealer ?? (ctx.isDealer ? winner : winner === 0 ? 1 : 0);
  const honba = opts.honba ?? 0;
  const pao = opts.pao ?? null;
  const neighborHan = (() => {
    if (rs.scoring.doraNeighbors !== true || !tiles) return 0;
    const inds = [...ctx.doraIndicators ?? []];
    if (ctx.riichi && rs.scoring.uraDora !== false) inds.push(...ctx.uraDoraIndicators ?? []);
    return Math.floor(countDoraNeighbors(tiles, inds) / 2);
  })();
  const akaRoom0 = rs.enabledYaku["aka-dora"];
  const akaDef0 = catalog.find((d) => d.id === "aka-dora");
  const akaHanPer0 = akaRoom0?.enabled === false ? 0 : akaRoom0?.hanOverride ?? akaDef0?.defaultHan ?? 1;
  const rankDoraHan = ctx.doraCount + akaHanPer0 * ctx.akaCount + (rs.scoring.uraDora === false ? 0 : ctx.uraCount) + ctx.kitaCount + (ctx.flowerCount ?? 0) + neighborHan;
  let best;
  for (const ph2 of parses) {
    const yr2 = detectYaku(ph2, ctx, rs, catalog, tiles);
    const fb2 = computeFu(ph2, yr2, ctx, rs);
    const han = yr2.yaku.reduce((a, y) => a + y.han, 0);
    const tier = yr2.yakuman.length > 0 ? 1 : 0;
    const rank = tier === 1 ? yr2.yakuman.reduce((a, y) => a + y.mult, 0) : normalBase(fb2.total, han + rankDoraHan, rs);
    if (!best || tier > best.tier || tier === best.tier && rank > best.rank) {
      best = { ph: ph2, yr: yr2, fu: fb2.total, han, tier, rank };
    }
  }
  if (!best) return { error: "no decomposition", code: "NO_HAND" };
  const { ph, yr, fu } = best;
  const mode = effectiveLimitMode(rs);
  const akaRoom = rs.enabledYaku["aka-dora"];
  const akaDef = catalog.find((d) => d.id === "aka-dora");
  const akaHanPer = akaRoom?.enabled === false ? 0 : akaRoom?.hanOverride ?? akaDef?.defaultHan ?? 1;
  const doraBreakdown = {
    dora: ctx.doraCount,
    aka: akaHanPer * ctx.akaCount,
    ura: rs.scoring.uraDora === false ? 0 : ctx.uraCount,
    kita: ctx.kitaCount,
    // 花牌 (flowers): flowerCount × 1 han, like kita — a dora source, NOT a yaku (so it never satisfies
    // the must-have-yaku gate below). 0 when flowers are OFF, so non-flower scoring is byte-identical.
    flower: ctx.flowerCount ?? 0,
    // ドラ隣 (dora-neighbor): floor(half-dora tiles / 2) han, a dora source (NOT a yaku — never satisfies
    // the gate). 0 when scoring.doraNeighbors is absent/OFF, so default scoring is byte-identical.
    neighbor: neighborHan
  };
  const doraHan = doraBreakdown.dora + doraBreakdown.aka + doraBreakdown.ura + doraBreakdown.kita + doraBreakdown.flower + doraBreakdown.neighbor;
  const yakuHan = yr.yaku.reduce((a, y) => a + y.han, 0);
  if (!yr.hasYakuhaiOrMenzenYaku && yr.yakuman.length === 0) {
    return { error: "no yaku (dora alone cannot win)", code: "NO_YAKU" };
  }
  const minHan = rs.scoring.hanRequirement ?? 1;
  if (yr.yakuman.length === 0 && yakuHan < minHan) {
    return { error: `han requirement not met (need ${minHan}, have ${yakuHan})`, code: "HAN_REQUIREMENT" };
  }
  let base;
  let yakuman;
  let displayHan;
  if (yr.yakuman.length > 0) {
    let mult = 0;
    for (const y of yr.yakuman) {
      let m = y.mult;
      if (m >= 3 && !rs.scoring.tripleYakuman) m = 2;
      if (m >= 2 && !rs.scoring.doubleYakuman) m = 1;
      mult += m;
    }
    if (mode === "stepped") {
      base = MANGAN_BASE * steppedUnits(13 * mult + doraHan);
    } else {
      base = 8e3 * mult;
    }
    yakuman = mult;
    displayHan = 0;
  } else {
    const totalHan = yakuHan + doraHan;
    base = normalBase(fu, totalHan, rs);
    displayHan = totalHan;
    yakuman = mode === "capped" && totalHan >= rs.scoring.kazoeYakumanCap ? 1 : 0;
  }
  const fb = computeFu(ph, yr, ctx, rs);
  const payments = paymentMatrix({
    base,
    winner,
    by: ctx.winBy,
    from: ctx.from,
    dealer,
    players,
    rs,
    honba,
    pao,
    dealerWins: ctx.isDealer
  });
  const honbaAdded = honba * rs.points.honbaValue;
  const points = gainOf(payments, winner);
  return {
    yaku: yr.yakuman.length > 0 ? yr.yakuman.map((y) => ({ id: y.id, han: 13 * y.mult })) : yr.yaku.slice(),
    doraBreakdown,
    fu: ph.form === "kokushi" ? 0 : fu,
    han: displayHan,
    yakuman,
    points,
    payments,
    ...pao !== null ? { pao: { seat: pao, share: 0.5 } } : {},
    honbaAdded,
    breakdown: fb
  };
}
function baseFromHanFu(han, fu, rs) {
  return normalBase(fu, han, rs);
}
var KANJI_NUM = ["", "\u4E00", "\u4E8C", "\u4E09", "\u56DB", "\u4E94", "\u516D", "\u4E03", "\u516B", "\u4E5D", "\u5341", "\u5341\u4E00", "\u5341\u4E8C", "\u5341\u4E09", "\u5341\u56DB", "\u5341\u4E94"];
function manganUnitLabel(n) {
  if (n <= 1) return "\u6E80\u8CAB";
  if (n < 2) return "\u8DF3\u6E80";
  if (n === 2) return "\u500D\u6E80";
  const k = KANJI_NUM[Math.floor(n)] ?? String(Math.floor(n));
  return `${k}\u500D\u6E80`;
}
function limitLabel(han, base, rs) {
  const mode = effectiveLimitMode(rs);
  if (mode === "aotenjou") return "";
  if (mode === "stepped") {
    if (han < 5) return base >= MANGAN_BASE ? "\u6E80\u8CAB" : "";
    return manganUnitLabel(steppedUnits(han));
  }
  if (han >= 13) return "kazoe yakuman";
  if (han >= 11) return "sanbaiman";
  if (han >= 8) return "baiman";
  if (han >= 6) return "haneman";
  if (han === 5) return "mangan";
  return base >= MANGAN_BASE ? "mangan" : "";
}
function scoreTableCell(han, fu, rs) {
  const base = baseFromHanFu(han, fu, rs);
  const players = rs.players;
  const ndRon = paymentMatrix({ base, winner: 1, by: "ron", from: 0, dealer: 0, players, rs, honba: 0, pao: null, dealerWins: false });
  const dRon = paymentMatrix({ base, winner: 0, by: "ron", from: 1, dealer: 0, players, rs, honba: 0, pao: null, dealerWins: true });
  const ndTsumo = paymentMatrix({ base, winner: 1, by: "tsumo", from: null, dealer: 0, players, rs, honba: 0, pao: null, dealerWins: false });
  const dTsumo = paymentMatrix({ base, winner: 0, by: "tsumo", from: null, dealer: 0, players, rs, honba: 0, pao: null, dealerWins: true });
  return {
    han,
    fu,
    base,
    nonDealerRon: ndRon[0] !== void 0 ? -ndRon[0] : 0,
    dealerRon: dRon[1] !== void 0 ? -dRon[1] : 0,
    // non-dealer tsumo: each non-dealer (a seat that is not winner and not dealer) pays |delta|;
    // dealer pays double. We read the dealer's (seat 0) and a plain non-dealer's payment.
    nonDealerTsumo: [ndTsumo[2] !== void 0 ? -ndTsumo[2] : 0, ndTsumo[0] !== void 0 ? -ndTsumo[0] : 0],
    dealerTsumo: dTsumo[1] !== void 0 ? -dTsumo[1] : 0,
    limit: limitLabel(han, base, rs)
  };
}
function scoreTable(rs, hans, fus) {
  return hans.map((han) => fus.map((fu) => scoreTableCell(han, fu, rs)));
}

// src/deal.ts
var ROUND_WINDS = ["z1", "z2", "z3", "z4"];
var SEAT_WINDS = ["z1", "z2", "z3", "z4"];
function numberCopies(players) {
  return players <= 5 ? 4 : 5;
}
function deadWallSize(players) {
  return players <= 4 ? 14 : players <= 6 ? 10 : 8;
}
function buildWall(rs) {
  const out = [];
  const akaLeft = {
    m: rs.tiles.akaCount?.m ?? 0,
    p: rs.tiles.akaCount?.p ?? 0,
    s: rs.tiles.akaCount?.s ?? 0
  };
  const copies = numberCopies(rs.players);
  for (const suit of rs.tiles.suits) {
    for (let rank = 1; rank <= 9; rank++) {
      if (rs.tiles.removeManzu2to8 && suit === "m" && rank >= 2 && rank <= 8) continue;
      for (let copy = 0; copy < copies; copy++) {
        if (rank === 5 && (akaLeft[suit] ?? 0) > 0) {
          out.push(`${suit}${rank}r`);
          akaLeft[suit]--;
        } else {
          out.push(`${suit}${rank}`);
        }
      }
    }
  }
  const HONOR_TO_Z = {
    E: "z1",
    S: "z2",
    W: "z3",
    N: "z4",
    P: "z5",
    F: "z6",
    C: "z7"
  };
  const WIND_SEAT_INDEX = { E: 0, S: 1, W: 2, N: 3 };
  const ALL_WINDS_Z = ["z1", "z2", "z3", "z4"];
  const nukiKeep = rs.flags.sanmaKitaMode === "nukiDora" ? new Set(rs.flags.nukiTiles ?? ALL_WINDS_Z.slice(rs.players)) : /* @__PURE__ */ new Set();
  for (const h of rs.tiles.honors) {
    const z = HONOR_TO_Z[h];
    if (!z) continue;
    if (rs.tiles.dropGuestWinds && h in WIND_SEAT_INDEX && WIND_SEAT_INDEX[h] >= rs.players && !nukiKeep.has(z)) {
      continue;
    }
    for (let copy = 0; copy < 4; copy++) out.push(z);
  }
  if (rs.tiles.flowers) {
    for (let i = 1; i <= 8; i++) out.push(`f${i}`);
  }
  return out;
}
function seatWindFor(rel, players, rs) {
  if (rel < 4) return SEAT_WINDS[rel] ?? null;
  const mode = rs.tiles.extraWindMode ?? (players > 4 ? "repeatNorth" : "standard4");
  if (mode === "repeatNorth") return SEAT_WINDS[3] ?? null;
  if (mode === "extendWinds") return rel === 4 ? "z8" : rel === 5 ? "z9" : null;
  return null;
}
var SEAT_DRAGONS = ["z5", "z6", "z7"];
function seatDragonFor(rel, rs) {
  if (!rs.flags.seatDragon) return null;
  if (rel < 4) return null;
  return SEAT_DRAGONS[rel - 4] ?? null;
}
function makePlayer(seat, dealer, players, start, rs) {
  const rel = ((seat - dealer) % players + players) % players;
  const seatWind = seatWindFor(rel, players, rs);
  const seatDragon = seatDragonFor(rel, rs);
  return {
    seat,
    hand: [],
    melds: [],
    discards: [],
    riichi: null,
    furiten: { temporary: false, permanent: false, byRiichi: false },
    score: start,
    seatWind,
    seatDragon,
    kitaCount: 0,
    flowerCount: 0,
    flowers: [],
    nagashiAlive: true,
    drawnThisTurn: null
  };
}
function initialState(rs, seed) {
  const players = rs.players;
  const rng = makeRng(seed);
  const tiles = shuffle(buildWall(rs), rng);
  const dw = deadWallSize(players);
  const deadWall = tiles.slice(tiles.length - dw);
  const live = tiles.slice(0, tiles.length - dw);
  const dealer = 0;
  const roundWind = ROUND_WINDS[0];
  const playerStates = [];
  for (let s = 0; s < players; s++) playerStates.push(makePlayer(s, dealer, players, rs.points.start, rs));
  for (let n = 0; n < 13; n++) {
    for (let s = 0; s < players; s++) {
      const t = live.pop();
      if (t === void 0) throw new Error("wall exhausted during deal");
      playerStates[s].hand.push(t);
    }
  }
  for (const p of playerStates) p.hand.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
  const doraIndicators = [rs.tiles.fixedDoraIndicators?.[0] ?? deadWall[4]];
  return {
    ruleSet: rs,
    rng: rng.state(),
    wall: live,
    deadWall,
    rinshan: [],
    doraIndicators,
    uraDoraIndicators: [],
    revealedDora: 1,
    players: playerStates,
    roundWind,
    dealer,
    turn: dealer,
    phase: "draw",
    lastDiscard: null,
    honba: 0,
    riichiPot: 0,
    roundCounter: 0,
    dealerWinStreak: 0,
    kanCount: 0,
    firstGoAround: true
  };
}

// src/furiten.ts
import { baseOfAka as baseOfAka12 } from "@mj/protocol";
function waitsForSeat(s, seat) {
  const p = s.players[seat];
  if (shanten(p.hand, p.melds) !== 0) return [];
  return tenpaiWaits(p.hand, p.melds);
}
function selfDiscardFuriten(p, waits) {
  for (const d of p.discards) if (waits.has(baseOfAka12(d.tile))) return true;
  return false;
}
function recomputeFuriten(s, seat) {
  const p = s.players[seat];
  const waitList = waitsForSeat(s, seat);
  const waits = new Set(waitList.map(baseOfAka12));
  if (waits.size === 0) {
    if (!p.furiten.permanent) {
      p.furiten.temporary = false;
    }
    return;
  }
  if (selfDiscardFuriten(p, waits)) {
    p.furiten.temporary = true;
  }
}
function applyMissedRonFuriten(s, passer) {
  const p = s.players[passer];
  const waits = new Set(waitsForSeat(s, passer).map(baseOfAka12));
  if (waits.size === 0) return;
  if (s.lastDiscard === null) return;
  const discardTile = baseOfAka12(
    s.players[s.lastDiscard.seat].discards[s.lastDiscard.index].tile
  );
  if (!waits.has(discardTile)) return;
  p.furiten.temporary = true;
  if (p.riichi?.declared) {
    p.furiten.permanent = true;
    p.furiten.byRiichi = true;
  }
}
function isFuriten(p) {
  return p.furiten.temporary || p.furiten.permanent;
}
function clearTemporaryOnDraw(p) {
  if (!p.furiten.permanent) p.furiten.temporary = false;
}

// src/calls.ts
import { baseOfAka as baseOfAka13 } from "@mj/protocol";
var err = (error, code) => ({ error, code });
function lastDiscardTile(s) {
  if (s.lastDiscard === null) return null;
  const d = s.players[s.lastDiscard.seat].discards[s.lastDiscard.index];
  return d ? baseOfAka13(d.tile) : null;
}
function ponCallable(s, seat, rs) {
  if (rs.flags.noCalls) return null;
  const t = lastDiscardTile(s);
  if (t === null || s.lastDiscard === null || s.lastDiscard.seat === seat) return null;
  const p = s.players[seat];
  if (p.riichi?.declared) return null;
  const have = p.hand.filter((h) => baseOfAka13(h) === t);
  if (have.length < 2) return null;
  return { kind: "pon", tiles: [have[0], have[1], t], calledFrom: s.lastDiscard.seat, calledTileIndex: 2 };
}
function minkanCallable(s, seat, rs) {
  if (rs.flags.noCalls) return null;
  const t = lastDiscardTile(s);
  if (t === null || s.lastDiscard === null || s.lastDiscard.seat === seat) return null;
  const p = s.players[seat];
  if (p.riichi?.declared) return null;
  const have = p.hand.filter((h) => baseOfAka13(h) === t);
  if (have.length < 3) return null;
  return {
    kind: "minkan",
    tiles: [have[0], have[1], have[2], t],
    calledFrom: s.lastDiscard.seat,
    calledTileIndex: 3
  };
}
function chiiOptions(s, seat, rs) {
  if (rs.flags.noCalls) return [];
  if (rs.flags.noChii) return [];
  const t = lastDiscardTile(s);
  if (t === null || s.lastDiscard === null) return [];
  const from = s.lastDiscard.seat;
  if ((from + 1) % s.players.length !== seat) return [];
  const p = s.players[seat];
  if (p.riichi?.declared) return [];
  const k = tileKey(t);
  if (k.suit === 3) return [];
  const rank = k.rank;
  const out = [];
  const has = (r) => {
    if (r < 1 || r > 9) return null;
    const want = tile34From(k.suit, r);
    const h = p.hand.find((x) => baseOfAka13(x) === want);
    return h ?? null;
  };
  const patterns = [
    [rank - 2, rank - 1],
    [rank - 1, rank + 1],
    [rank + 1, rank + 2]
  ];
  for (const [a, b] of patterns) {
    const ta = has(a);
    const tb = has(b);
    if (ta && tb) {
      const tiles = sortMeld([ta, tb, t]);
      const meld = {
        kind: "chii",
        tiles,
        calledFrom: from,
        // The called (discarded) tile `t` is at its SORTED position — index 0 only for a low-end chii;
        // a kanchan puts it at 1, a high-end at 2. applyMeld uses this to know which copy came from the
        // discarder (NOT removed from hand); a wrong index left the hand +1 oversized → a "complete-
        // looking" hand that parseAgari can't decompose (can't ツモ/立直). t's rank is unique in the run.
        calledTileIndex: tiles.indexOf(t)
      };
      out.push(meld);
    }
  }
  void rs;
  return out;
}
function tile34From(suit, rank) {
  const s = suit === 0 ? "m" : suit === 1 ? "p" : "s";
  return `${s}${rank}`;
}
function sortMeld(tiles) {
  return [...tiles].sort((a, b) => tile34(baseOfAka13(a)) - tile34(baseOfAka13(b)));
}
function ronCallable(s, seat, rs, catalog = YAKU_CATALOG) {
  const t = lastDiscardTile(s);
  if (t === null || s.lastDiscard === null || s.lastDiscard.seat === seat) return false;
  const p = s.players[seat];
  if (isFuriten(p)) return false;
  const waits = new Set(waitsForSeat(s, seat).map(baseOfAka13));
  if (!waits.has(t)) return false;
  const probe = buildWinContext(s, seat, "ron", rs);
  const concealed = [...p.hand, t];
  const parses = parseAgari(concealed, p.melds, t, "ron");
  if (parses.length === 0) return false;
  const sr = score(parses, probe, rs, catalog, [...concealed, ...p.melds.flatMap((m) => m.tiles)], {
    players: s.players.length,
    dealer: s.dealer,
    honba: s.honba
  });
  return !("error" in sr);
}
function buildWinContext(s, seat, by, _rs) {
  const p = s.players[seat];
  const from = by === "ron" && s.lastDiscard ? s.lastDiscard.seat : null;
  const menzen = !p.melds.some((m) => m.kind !== "ankan" && m.kind !== "kita");
  const isDealer = seat === s.dealer;
  const lastTile = by === "tsumo" && s.wall.length === 0 ? "haitei" : by === "ron" && s.wall.length === 0 ? "houtei" : "none";
  const winTile = by === "ron" ? lastDiscardTile(s) : p.hand[p.hand.length - 1];
  const fullTiles = [...p.hand, ...by === "ron" ? [winTile] : [], ...p.melds.flatMap((m) => m.tiles)];
  const dora = countDora(fullTiles, s.doraIndicators);
  const aka = fullTiles.filter((t) => t.endsWith("r")).length;
  const ura = p.riichi?.declared ? countDora(fullTiles, s.uraDoraIndicators) : 0;
  return {
    winBy: by,
    firstTurn: s.firstGoAround,
    lastTile,
    isDealer,
    menzen,
    afterKan: false,
    winner: seat,
    from,
    seatWind: p.seatWind,
    seatDragon: p.seatDragon ?? null,
    roundWind: s.roundWind,
    riichi: p.riichi?.declared ?? false,
    doubleRiichi: p.riichi?.double ?? false,
    ippatsu: p.riichi?.ippatsu ?? false,
    openRiichi: p.riichi?.open ?? false,
    doraCount: dora,
    akaCount: aka,
    uraCount: ura,
    kitaCount: p.kitaCount,
    flowerCount: p.flowerCount,
    flowers: p.flowers,
    // Revealed indicators (incl. kan-dora). ura indicators only on a riichi win — same gate as uraCount
    // above — so the ドラ隣 source resolves the same dora targets normal dora counting does.
    doraIndicators: s.doraIndicators,
    uraDoraIndicators: p.riichi?.declared ? s.uraDoraIndicators : [],
    dealerWinStreak: s.dealerWinStreak
  };
}
function resolveWin(s, seat, by, rs, catalog = YAKU_CATALOG) {
  const p = s.players[seat];
  const winTile = by === "ron" ? lastDiscardTile(s) : p.hand[p.hand.length - 1];
  if (winTile === null || winTile === void 0) return err("no win tile", "NO_WIN_TILE");
  const concealed = by === "ron" ? [...p.hand, winTile] : p.hand.slice();
  if (shanten(concealed, p.melds) !== -1) return err("not a winning hand", "NOT_COMPLETE");
  const ctx = buildWinContext(s, seat, by, rs);
  const parses = parseAgari(concealed, p.melds, winTile, by);
  const fullTiles = [...concealed, ...p.melds.flatMap((m) => m.tiles)];
  const sr = score(parses, ctx, rs, catalog, fullTiles, {
    players: s.players.length,
    dealer: s.dealer,
    honba: s.honba,
    riichiPot: s.riichiPot
  });
  if ("error" in sr) return sr;
  for (let i = 0; i < s.players.length; i++) {
    s.players[i].score += sr.payments[i] ?? 0;
  }
  s.players[seat].score += s.riichiPot;
  const potTaken = s.riichiPot;
  s.riichiPot = 0;
  void potTaken;
  s.phase = "resolve";
  const events = [
    {
      seq: 0,
      event: "win",
      winners: [seat],
      results: [sr],
      hands: [concealed.slice()],
      ura: s.uraDoraIndicators.slice()
    }
  ];
  return { events };
}
function distFromDiscarder(s, seat) {
  const N = s.players.length;
  const d = s.lastDiscard.seat;
  return ((seat - d) % N + N) % N;
}
function resolveCallWindow(s0, submitted, rs, catalog = YAKU_CATALOG) {
  if (s0.lastDiscard === null) return err("no discard to resolve", "NO_DISCARD");
  const discarder = s0.lastDiscard.seat;
  const rons = submitted.filter((c) => c.intent.kind === "ron" && c.seat !== discarder && ronCallable(s0, c.seat, rs, catalog)).map((c) => c.seat).sort((a, b) => distFromDiscarder(s0, a) - distFromDiscarder(s0, b));
  const pons = submitted.filter((c) => c.intent.kind === "pon" || c.intent.kind === "minkan");
  const chiis = submitted.filter((c) => c.intent.kind === "chii");
  if (rons.length > 0) {
    let winners = rons;
    if (rs.flags.multiWinPolicy === "headBump") winners = [rons[0]];
    else if (rs.flags.multiWinPolicy === "tripleRon") winners = rons.slice(0, 3);
    else winners = rons.slice(0, 2);
    return resolveMultiRon(s0, winners, rs, catalog);
  }
  if (pons.length > 0) {
    const c = pons[0];
    const meld = c.intent.kind === "pon" ? ponCallable(s0, c.seat, rs) : minkanCallable(s0, c.seat, rs);
    if (!meld) return err("illegal pon/kan", "NO_PON");
    applyMeldInline(s0, c.seat, meld, discarder);
    const ev = [{ seq: 0, event: "call", seat: c.seat, kind: meld.kind, tiles: meld.tiles, from: discarder }];
    if (meld.kind === "minkan") {
      s0.kanCount++;
      const r = pullRinshan(s0, c.seat);
      if (r) ev.push({ seq: ev.length, event: "draw", seat: c.seat, tile: r });
      const ind = pushKanDora(s0, rs);
      if (ind) ev.push({ seq: ev.length, event: "doraFlip", indicator: ind });
    }
    s0.turn = c.seat;
    s0.phase = "discard";
    s0.firstGoAround = false;
    for (const p of s0.players) if (p.riichi) p.riichi.ippatsu = false;
    return { state: s0, events: ev };
  }
  if (chiis.length > 0) {
    const c = chiis[0];
    const opts = chiiOptions(s0, c.seat, rs);
    const want = c.intent.tiles.map(baseOfAka13).slice().sort();
    const meld = opts.find((m) => {
      const bases = m.tiles.map(baseOfAka13).slice().sort();
      return want.every((w) => bases.includes(w));
    });
    if (!meld) return err("illegal chii", "NO_CHII");
    applyMeldInline(s0, c.seat, meld, discarder);
    s0.turn = c.seat;
    s0.phase = "discard";
    s0.firstGoAround = false;
    for (const p of s0.players) if (p.riichi) p.riichi.ippatsu = false;
    return { state: s0, events: [{ seq: 0, event: "call", seat: c.seat, kind: "chii", tiles: meld.tiles, from: discarder }] };
  }
  for (let seat = 0; seat < s0.players.length; seat++) {
    if (seat === discarder) continue;
  }
  s0.turn = (discarder + 1) % s0.players.length;
  s0.phase = "draw";
  return { state: s0, events: [] };
}
function resolveMultiRon(s, winners, rs, catalog = YAKU_CATALOG) {
  const winTile = lastDiscardTile(s);
  const results = [];
  const hands = [];
  const nearest = winners[0];
  let stickTaken = false;
  for (const seat of winners) {
    const p = s.players[seat];
    const concealed = [...p.hand, winTile];
    const ctx = buildWinContext(s, seat, "ron", rs);
    const parses = parseAgari(concealed, p.melds, winTile, "ron");
    const fullTiles = [...concealed, ...p.melds.flatMap((m) => m.tiles)];
    const sr = score(parses, ctx, rs, catalog, fullTiles, {
      players: s.players.length,
      dealer: s.dealer,
      honba: s.honba
    });
    if ("error" in sr) return sr;
    for (let i = 0; i < s.players.length; i++) s.players[i].score += sr.payments[i] ?? 0;
    if (seat === nearest && !stickTaken) {
      s.players[seat].score += s.riichiPot;
      s.riichiPot = 0;
      stickTaken = true;
    }
    results.push(sr);
    hands.push(concealed.slice());
  }
  s.phase = "resolve";
  return {
    state: s,
    events: [{ seq: 0, event: "win", winners: winners.slice(), results, hands, ura: s.uraDoraIndicators.slice() }]
  };
}
function applyMeldInline(s, seat, meld, from) {
  const player = s.players[seat];
  const called = baseOfAka13(meld.tiles[meld.calledTileIndex] ?? meld.tiles[0]);
  let calledRemoved = false;
  for (const want of meld.tiles.map(baseOfAka13)) {
    if (!calledRemoved && want === called && from !== seat) {
      calledRemoved = true;
      continue;
    }
    const i = player.hand.findIndex((h) => baseOfAka13(h) === want);
    if (i >= 0) player.hand.splice(i, 1);
  }
  player.hand = [...player.hand].sort((a, b) => tile34(baseOfAka13(a)) - tile34(baseOfAka13(b)));
  player.melds.push(meld);
  if (s.lastDiscard) {
    const d = s.players[from].discards[s.lastDiscard.index];
    if (d) d.calledAway = true;
  }
}
function pullRinshan(s, seat) {
  const r = s.rinshan.length > 0 ? s.rinshan.pop() : s.deadWall.shift();
  if (r === void 0) return null;
  s.players[seat].hand.push(r);
  s.players[seat].hand = [...s.players[seat].hand].sort((a, b) => tile34(baseOfAka13(a)) - tile34(baseOfAka13(b)));
  return r;
}
function pushKanDora(s, rs) {
  if (rs.scoring.kanDora === false) return null;
  const idx = 4 + s.revealedDora;
  const ind = s.deadWall[idx];
  if (ind === void 0) return null;
  s.doraIndicators.push(ind);
  s.revealedDora++;
  return ind;
}

// src/reducer.ts
import { baseOfAka as baseOfAka14 } from "@mj/protocol";
var err2 = (error, code) => ({ error, code });
function drawOneInfinite(s) {
  const pool = buildWall(s.ruleSet);
  const rng = makeRngFromLanes(s.rng);
  const t = pool[rng.nextBelow(pool.length)];
  s.rng = rng.state();
  return t;
}
function clone(s) {
  return {
    ...s,
    rng: [...s.rng],
    wall: s.wall.slice(),
    deadWall: s.deadWall.slice(),
    rinshan: s.rinshan.slice(),
    doraIndicators: s.doraIndicators.slice(),
    uraDoraIndicators: s.uraDoraIndicators.slice(),
    players: s.players.map((p) => ({
      ...p,
      hand: p.hand.slice(),
      melds: p.melds.map((m) => ({ ...m, tiles: m.tiles.slice() })),
      discards: p.discards.map((d) => ({ ...d })),
      riichi: p.riichi ? { ...p.riichi } : null,
      furiten: { ...p.furiten },
      flowers: p.flowers.slice()
    })),
    lastDiscard: s.lastDiscard ? { ...s.lastDiscard } : null
  };
}
function nextSeat(s, seat) {
  return (seat + 1) % s.players.length;
}
function popLiveTile(s) {
  if (s.ruleSet.tiles.infinite) {
    const t = s.wall.pop() ?? drawOneInfinite(s);
    s.wall.unshift(drawOneInfinite(s));
    return t;
  }
  return s.wall.pop();
}
function drawReplacementTile(s) {
  if (s.rinshan.length > 0) return s.rinshan.pop();
  const dead = s.deadWall.pop();
  if (dead !== void 0) return dead;
  return popLiveTile(s);
}
function drawTile(s, seat) {
  const fixed = s.ruleSet.tiles.fixedDraws;
  if (seat === 0 && fixed) {
    const cursor = s.fixedDrawCursor ?? 0;
    const forced = fixed[cursor];
    if (forced !== void 0) {
      s.fixedDrawCursor = cursor + 1;
      s.players[seat].hand.push(forced);
      return forced;
    }
  }
  const t = popLiveTile(s);
  if (t === void 0) return null;
  s.players[seat].hand.push(t);
  return t;
}
function reduce(state, seat, intent, rs, catalog = YAKU_CATALOG) {
  if (state.phase === "end" || state.phase === "resolve") {
    return err2("hand already over", "HAND_OVER");
  }
  if (state.phase === "callWindow") {
    return reduceCallWindow(state, seat, intent, rs, catalog);
  }
  if (seat !== state.turn) return err2("not your turn", "NOT_TURN");
  const s = clone(state);
  const events = [];
  let seq = 0;
  const player = s.players[seat];
  let drawnTile = null;
  const needsDraw = s.phase === "draw";
  if (needsDraw) {
    if (s.wall.length === 0) {
      return err2("wall empty", "WALL_EMPTY");
    }
    const drawn = drawTile(s, seat);
    if (drawn === null) return err2("wall empty", "WALL_EMPTY");
    drawnTile = drawn;
    player.drawnThisTurn = drawn;
    events.push({ seq: seq++, event: "draw", seat, tile: drawn });
    s.phase = "discard";
    clearTemporaryOnDraw(player);
    if (player.riichi?.declared && player.riichi.infinite) {
      player.score -= rs.points.riichiStick;
      s.riichiPot += rs.points.riichiStick;
    }
  }
  switch (intent.kind) {
    case "discard": {
      const tile = intent.tile;
      const tsumogiri = player.drawnThisTurn ?? null;
      if (player.riichi?.declared && !player.riichi.infinite && tsumogiri !== null && tile !== tsumogiri) {
        return err2("riichi: must discard the drawn tile (tsumogiri)", "RIICHI_LOCKED");
      }
      const idx = player.hand.indexOf(tile);
      if (idx < 0) return err2("tile not in hand", "NO_TILE");
      player.hand.splice(idx, 1);
      player.drawnThisTurn = null;
      const isRiichiDeclare = intent.riichi === true;
      if (isRiichiDeclare) {
        const declareResult = declareRiichi(s, seat, intent.openRiichi === true, intent.infiniteRiichi === true, rs);
        if (declareResult) return declareResult;
      }
      player.hand = sortTiles(player.hand);
      player.discards.push({
        tile,
        tsumogiri: intent.tsumogiri === true,
        riichiTile: isRiichiDeclare,
        calledAway: false
      });
      s.lastDiscard = { seat, index: player.discards.length - 1 };
      recomputeFuriten(s, seat);
      s.phase = "callWindow";
      events.push({ seq: seq++, event: "discard", seat, tile, tsumogiri: intent.tsumogiri === true });
      if (isRiichiDeclare) events.push({ seq: seq++, event: "riichi", seat, tile });
      const auto = autoResolveCallWindowIfNone(s, rs, events, seq, catalog);
      if (auto) seq = auto;
      return { state: s, events };
    }
    case "tsumo": {
      const win = resolveWin(s, seat, "tsumo", rs, catalog);
      if ("error" in win) return win;
      for (const e of win.events) e.seq = seq++;
      events.push(...win.events);
      return { state: s, events };
    }
    case "ankan": {
      const k = doAnkan(s, seat, intent.tile, rs, events, seq);
      if ("error" in k) return k;
      return { state: k.state, events: k.events };
    }
    case "windkan": {
      const k = doWindKan(s, seat, rs, events, seq);
      if ("error" in k) return k;
      return { state: k.state, events: k.events };
    }
    case "shouminkan": {
      const k = doShouminkan(s, seat, intent.tile, rs, events, seq);
      if ("error" in k) return k;
      return { state: k.state, events: k.events };
    }
    case "kita": {
      const kitaMode = rs.flags.sanmaKitaMode;
      if (kitaMode !== "nukiDora") {
        return err2(
          kitaMode === "normalHonor" ? "kita disabled: north is a normal honor" : "kita not allowed",
          "NO_KITA"
        );
      }
      const nukiTiles = nukiTileSet(rs);
      const pull = intent.tile ?? "z4";
      if (!nukiTiles.includes(pull)) return err2("tile is not a nuki tile", "NO_KITA");
      const idx = player.hand.indexOf(pull);
      if (idx < 0) return err2("no nuki tile in hand", "NO_TILE");
      player.hand.splice(idx, 1);
      player.kitaCount++;
      const r = drawReplacementTile(s);
      if (r !== void 0) {
        player.hand.push(r);
        player.drawnThisTurn = r;
      }
      player.hand = sortTiles(player.hand);
      s.phase = "discard";
      return { state: s, events };
    }
    case "flowerpull": {
      if (rs.tiles.flowers !== true) return err2("flowers not enabled", "NO_FLOWER");
      const pull = intent.tile;
      if (pull[0] !== "f") return err2("tile is not a flower", "NO_FLOWER");
      const idx = player.hand.indexOf(pull);
      if (idx < 0) return err2("no flower in hand", "NO_TILE");
      player.hand.splice(idx, 1);
      player.flowerCount++;
      player.flowers.push(pull);
      events.push({ seq: seq++, event: "flower", seat, tile: pull });
      const r = drawReplacementTile(s);
      if (r !== void 0) {
        player.hand.push(r);
        player.drawnThisTurn = r;
        events.push({ seq: seq++, event: "draw", seat, tile: r });
      }
      player.hand = sortTiles(player.hand);
      return { state: s, events };
    }
    case "kyuushuu": {
      return doKyuushuu(s, seat, rs, events, seq);
    }
    default:
      return err2("illegal intent for turn", "ILLEGAL");
  }
}
function reduceCallWindow(state, seat, intent, rs, catalog = YAKU_CATALOG) {
  const s = clone(state);
  const events = [];
  let seq = 0;
  if (state.lastDiscard === null) return err2("no discard to call", "NO_DISCARD");
  const discarder = state.lastDiscard.seat;
  if (seat === discarder) return err2("cannot call own discard", "SELF_CALL");
  switch (intent.kind) {
    case "pass": {
      return passCall(s, seat, rs, events, seq);
    }
    case "ron": {
      if (!ronCallable(s, seat, rs, catalog)) return err2("cannot ron", "NO_RON");
      const win = resolveWin(s, seat, "ron", rs, catalog);
      if ("error" in win) return win;
      for (const e of win.events) e.seq = seq++;
      events.push(...win.events);
      return { state: s, events };
    }
    case "pon": {
      const m = ponCallable(s, seat, rs);
      if (!m) return err2("cannot pon", "NO_PON");
      applyOpenMeld(s, seat, m, discarder);
      events.push({ seq: seq++, event: "call", seat, kind: "pon", tiles: m.tiles, from: discarder });
      s.turn = seat;
      s.phase = "discard";
      s.firstGoAround = false;
      breakIppatsuAll(s);
      return { state: s, events };
    }
    case "minkan": {
      const m = minkanCallable(s, seat, rs);
      if (!m) return err2("cannot kan", "NO_KAN");
      applyOpenMeld(s, seat, m, discarder);
      s.kanCount++;
      events.push({ seq: seq++, event: "call", seat, kind: "minkan", tiles: m.tiles, from: discarder });
      const minkanSeqRef = { v: seq };
      const r = drawRinshan(s, seat, events, minkanSeqRef);
      seq = minkanSeqRef.v;
      if (r) events.push({ seq: seq++, event: "draw", seat, tile: r });
      const ind = flipKanDora(s, rs);
      if (ind) events.push({ seq: seq++, event: "doraFlip", indicator: ind });
      s.turn = seat;
      s.phase = "discard";
      s.firstGoAround = false;
      breakIppatsuAll(s);
      return { state: s, events };
    }
    case "chii": {
      const opts = chiiOptions(s, seat, rs);
      const want = intent.tiles.map(baseOfAka14).slice().sort();
      const chosen = opts.find((m) => {
        const meldBases = m.tiles.map(baseOfAka14).slice().sort();
        return want.every((w) => meldBases.includes(w));
      });
      if (!chosen) return err2("cannot chii", "NO_CHII");
      applyOpenMeld(s, seat, chosen, discarder);
      events.push({ seq: seq++, event: "call", seat, kind: "chii", tiles: chosen.tiles, from: discarder });
      s.turn = seat;
      s.phase = "discard";
      s.firstGoAround = false;
      breakIppatsuAll(s);
      return { state: s, events };
    }
    default:
      return err2("illegal call", "ILLEGAL");
  }
}
function passCall(s, seat, _rs, events, _seq) {
  applyMissedRonFuriten(s, seat);
  advanceAfterDiscard(s);
  return { state: s, events };
}
function advanceAfterDiscard(s) {
  if (s.lastDiscard === null) return;
  const discarder = s.lastDiscard.seat;
  s.turn = nextSeat(s, discarder);
  s.phase = "draw";
  if (s.turn === s.dealer) s.firstGoAround = false;
}
function autoResolveCallWindowIfNone(s, rs, _events, seq, catalog = YAKU_CATALOG) {
  if (s.phase !== "callWindow" || s.lastDiscard === null) return seq;
  const discarder = s.lastDiscard.seat;
  for (let seat = 0; seat < s.players.length; seat++) {
    if (seat === discarder) continue;
    if (ronCallable(s, seat, rs, catalog)) return seq;
    if (ponCallable(s, seat, rs)) return seq;
    if (minkanCallable(s, seat, rs)) return seq;
    if (chiiOptions(s, seat, rs).length > 0) return seq;
  }
  advanceAfterDiscard(s);
  return seq;
}
function applyOpenMeld(s, seat, meld, from) {
  const player = s.players[seat];
  const called = baseOfAka14(meld.tiles[meld.calledTileIndex] ?? meld.tiles[0]);
  const toRemove = meld.tiles.map(baseOfAka14);
  let calledRemoved = false;
  for (const want of toRemove) {
    if (!calledRemoved && want === called && from !== seat) {
      calledRemoved = true;
      continue;
    }
    const i = player.hand.findIndex((h) => baseOfAka14(h) === want);
    if (i >= 0) player.hand.splice(i, 1);
  }
  player.hand = sortTiles(player.hand);
  player.melds.push(meld);
  if (s.lastDiscard) {
    const d = s.players[from].discards[s.lastDiscard.index];
    if (d) d.calledAway = true;
  }
}
function popRinshanTile(s) {
  if (s.ruleSet.tiles.infinite) return drawOneInfinite(s);
  return s.rinshan.length > 0 ? s.rinshan.pop() : s.deadWall.shift();
}
function drawRinshan(s, seat, _events, _seqRef) {
  const r = popRinshanTile(s);
  if (r === void 0) return null;
  s.players[seat].hand.push(r);
  s.players[seat].drawnThisTurn = r;
  s.players[seat].hand = sortTiles(s.players[seat].hand);
  return r;
}
function flipKanDora(s, rs) {
  if (rs.scoring.kanDora === false) return null;
  const fixed = rs.tiles.fixedDoraIndicators?.[s.revealedDora];
  if (fixed !== void 0) {
    s.doraIndicators.push(fixed);
    s.revealedDora++;
    return fixed;
  }
  if (rs.tiles.infinite) {
    const ind2 = drawOneInfinite(s);
    s.doraIndicators.push(ind2);
    s.revealedDora++;
    return ind2;
  }
  const idx = 4 + s.revealedDora;
  const ind = s.deadWall[idx];
  if (ind === void 0) return null;
  s.doraIndicators.push(ind);
  s.revealedDora++;
  return ind;
}
function breakIppatsuAll(s) {
  for (const p of s.players) if (p.riichi) p.riichi.ippatsu = false;
}
function declareRiichi(s, seat, open, infinite, rs) {
  const p = s.players[seat];
  const reDeclare = p.riichi?.declared === true;
  if (reDeclare && rs.flags.reRiichi !== true) return err2("already riichi", "ALREADY_RIICHI");
  if (infinite && rs.flags.infiniteRiichi !== true) return err2("infinite riichi not allowed", "NO_INFINITE_RIICHI");
  if (p.melds.some((m) => m.kind !== "ankan" && m.kind !== "kita")) {
    return err2("cannot riichi with open melds", "OPEN_HAND");
  }
  if (rs.flags.riichiBelowStick !== true && !infinite && p.score < rs.points.riichiStick) {
    return err2("not enough points to riichi", "NO_POINTS");
  }
  if (shanten(p.hand, p.melds) !== 0) return err2("not tenpai for riichi", "NOT_TENPAI");
  const isDouble = !reDeclare && s.firstGoAround;
  const open2 = (p.riichi?.open ?? false) || open;
  const infinite2 = (p.riichi?.infinite ?? false) || infinite;
  p.riichi = { declared: true, ippatsu: true, double: isDouble, open: open2, turn: p.discards.length, infinite: infinite2 };
  p.score -= rs.points.riichiStick;
  s.riichiPot += rs.points.riichiStick;
  if (open && !rs.flags.openRiichi) return err2("open riichi not allowed", "NO_OPEN_RIICHI");
  return null;
}
function riichiAnkanPreservesWait(hand, melds, base) {
  const four = hand.filter((t) => baseOfAka14(t) === base);
  if (four.length < 4) return false;
  const oneIdx = hand.findIndex((t) => baseOfAka14(t) === base);
  const waitsDiscard = tenpaiWaits(hand.filter((_, i) => i !== oneIdx), melds);
  const rest = hand.filter((t) => baseOfAka14(t) !== base);
  const ankanMeld = { kind: "ankan", tiles: four.slice(0, 4), calledFrom: null, calledTileIndex: 0 };
  const waitsKan = tenpaiWaits(rest, [...melds, ankanMeld]);
  if (waitsKan.length === 0 || waitsDiscard.length !== waitsKan.length) return false;
  const setK = new Set(waitsKan.map(baseOfAka14));
  return waitsDiscard.every((w) => setK.has(baseOfAka14(w)));
}
function doAnkan(s, seat, tile, rs, events, seq) {
  const p = s.players[seat];
  const base = baseOfAka14(tile);
  const have = p.hand.filter((t) => baseOfAka14(t) === base);
  if (have.length < 4) return err2("no ankan", "NO_KAN");
  if (p.riichi?.declared && !p.riichi.infinite && !riichiAnkanPreservesWait(p.hand, p.melds, base)) {
    return err2("riichi: ankan would change the wait", "RIICHI_KAN_CHANGES_WAIT");
  }
  for (let i = 0; i < 4; i++) {
    const idx = p.hand.findIndex((t) => baseOfAka14(t) === base);
    p.hand.splice(idx, 1);
  }
  const meld = { kind: "ankan", tiles: have.slice(0, 4), calledFrom: null, calledTileIndex: 0 };
  p.melds.push(meld);
  p.hand = sortTiles(p.hand);
  s.kanCount++;
  events.push({ seq: seq++, event: "call", seat, kind: "ankan", tiles: meld.tiles, from: null });
  const ankanSeqRef = { v: seq };
  const r = drawRinshan(s, seat, events, ankanSeqRef);
  seq = ankanSeqRef.v;
  if (r) events.push({ seq: seq++, event: "draw", seat, tile: r });
  const ind = flipKanDora(s, rs);
  if (ind) events.push({ seq: seq++, event: "doraFlip", indicator: ind });
  breakIppatsuAll(s);
  s.phase = "discard";
  return { state: s, events };
}
var WIND_KAN_TILES = ["z1", "z2", "z3", "z4"];
function doWindKan(s, seat, rs, events, seq) {
  if (rs.enabledYaku["wind-set"]?.enabled !== true) return err2("wind-set kan not enabled", "NO_WIND_KAN");
  const p = s.players[seat];
  for (const w of WIND_KAN_TILES) if (!p.hand.some((t) => baseOfAka14(t) === w)) return err2("need all four winds", "NO_WIND_KAN");
  const taken = [];
  for (const w of WIND_KAN_TILES) {
    const idx = p.hand.findIndex((t) => baseOfAka14(t) === w);
    taken.push(p.hand[idx]);
    p.hand.splice(idx, 1);
  }
  const meld = { kind: "ankan", tiles: taken, calledFrom: null, calledTileIndex: 0 };
  p.melds.push(meld);
  p.hand = sortTiles(p.hand);
  s.kanCount++;
  events.push({ seq: seq++, event: "call", seat, kind: "ankan", tiles: meld.tiles, from: null });
  const seqRef = { v: seq };
  const r = drawRinshan(s, seat, events, seqRef);
  seq = seqRef.v;
  if (r) events.push({ seq: seq++, event: "draw", seat, tile: r });
  const ind = flipKanDora(s, rs);
  if (ind) events.push({ seq: seq++, event: "doraFlip", indicator: ind });
  breakIppatsuAll(s);
  s.phase = "discard";
  return { state: s, events };
}
function doShouminkan(s, seat, tile, rs, events, seq) {
  const p = s.players[seat];
  const base = baseOfAka14(tile);
  const pon = p.melds.find((m) => m.kind === "pon" && baseOfAka14(m.tiles[0]) === base);
  if (!pon) return err2("no pon to upgrade", "NO_KAN");
  const idx = p.hand.findIndex((t) => baseOfAka14(t) === base);
  if (idx < 0) return err2("no tile to add", "NO_TILE");
  const added = p.hand.splice(idx, 1)[0];
  pon.kind = "shouminkan";
  pon.tiles.push(added);
  p.hand = sortTiles(p.hand);
  s.kanCount++;
  events.push({ seq: seq++, event: "call", seat, kind: "shouminkan", tiles: pon.tiles, from: pon.calledFrom });
  const shouminkanSeqRef = { v: seq };
  const r = drawRinshan(s, seat, events, shouminkanSeqRef);
  seq = shouminkanSeqRef.v;
  if (r) events.push({ seq: seq++, event: "draw", seat, tile: r });
  const ind = flipKanDora(s, rs);
  if (ind) events.push({ seq: seq++, event: "doraFlip", indicator: ind });
  breakIppatsuAll(s);
  s.phase = "discard";
  return { state: s, events };
}
function doKyuushuu(s, seat, rs, events, seq) {
  if (!rs.flags) return err2("no flags", "NO_FLAGS");
  const p = s.players[seat];
  const distinct = new Set(p.hand.map(baseOfAka14).filter((t) => isYaochuuTile(t)));
  if (!s.firstGoAround || distinct.size < 9) return err2("not kyuushuu", "NO_KYUUSHUU");
  s.phase = "resolve";
  events.push({ seq: seq++, event: "ryuukyoku", kind: "kyuushuu", tenpai: [], payments: new Array(s.players.length).fill(0) });
  return { state: s, events };
}
function isYaochuuTile(t) {
  const b = baseOfAka14(t);
  if (b[0] === "z") return true;
  const r = Number(b.slice(1));
  return r === 1 || r === 9;
}
function nukiTileSet(rs) {
  if (rs.flags.nukiTiles) return rs.flags.nukiTiles;
  const WINDS2 = ["z1", "z2", "z3", "z4"];
  return WINDS2.slice(rs.players);
}
function legalMoves(s, seat) {
  const out = [];
  const rs = s.ruleSet;
  if (s.phase === "callWindow") {
    if (s.lastDiscard && s.lastDiscard.seat !== seat) {
      if (ronCallable(s, seat, rs)) out.push({ kind: "ron" });
      if (ponCallable(s, seat, rs)) out.push({ kind: "pon" });
      if (minkanCallable(s, seat, rs)) out.push({ kind: "minkan" });
      for (const m of chiiOptions(s, seat, rs)) {
        out.push({ kind: "chii", tiles: [m.tiles[0], m.tiles[1]] });
      }
      out.push({ kind: "pass" });
    }
    return out;
  }
  if (seat !== s.turn) return out;
  const p = s.players[seat];
  const concealedTiles = new Set(p.hand.map(baseOfAka14));
  const riichiLocked = p.riichi?.declared === true && !p.riichi.infinite && p.drawnThisTurn != null;
  for (const t of p.hand) {
    if (riichiLocked && t !== p.drawnThisTurn) continue;
    out.push({ kind: "discard", tile: t });
  }
  if (shanten(p.hand, p.melds) === -1) out.push({ kind: "tsumo" });
  for (const b of concealedTiles) {
    if (p.hand.filter((t) => baseOfAka14(t) === b).length >= 4) {
      if (p.riichi?.declared && !p.riichi.infinite && !riichiAnkanPreservesWait(p.hand, p.melds, b)) continue;
      out.push({ kind: "ankan", tile: b });
    }
  }
  if (s.ruleSet.enabledYaku["wind-set"]?.enabled === true && WIND_KAN_TILES.every((w) => p.hand.some((t) => baseOfAka14(t) === w))) {
    out.push({ kind: "windkan" });
  }
  for (const m of p.melds) {
    if (m.kind === "pon") {
      const base = baseOfAka14(m.tiles[0]);
      if (p.hand.some((t) => baseOfAka14(t) === base)) out.push({ kind: "shouminkan", tile: base });
    }
  }
  if (s.firstGoAround) {
    const distinct = new Set(p.hand.map(baseOfAka14).filter((t) => isYaochuuTile(t)));
    if (distinct.size >= 9) out.push({ kind: "kyuushuu" });
  }
  if (rs.flags.sanmaKitaMode === "nukiDora") {
    for (const t of nukiTileSet(rs)) {
      if (p.hand.some((h) => baseOfAka14(h) === t)) out.push({ kind: "kita", tile: t });
    }
  }
  if (rs.tiles.flowers === true) {
    for (const t of p.hand) {
      if (t[0] === "f") out.push({ kind: "flowerpull", tile: t });
    }
  }
  return out;
}

// src/edges.ts
import { baseOfAka as baseOfAka15 } from "@mj/protocol";
function isTenpai(s, seat) {
  const p = s.players[seat];
  return shanten(p.hand, p.melds) === 0;
}
function nagashiMangan(s, seat) {
  const p = s.players[seat];
  if (p.discards.length === 0) return false;
  for (const d of p.discards) {
    if (d.calledAway) return false;
    if (!isYaochuu2(baseOfAka15(d.tile))) return false;
  }
  return true;
}
function isYaochuu2(t) {
  const k = tileKey(t);
  return k.suit === 3 || k.rank === 1 || k.rank === 9;
}
function exhaustiveDraw(s0, rs) {
  const s = s0;
  const N = s.players.length;
  const payments = new Array(N).fill(0);
  const nagashiSeats = [];
  if (rs.flags.nagashiMangan) {
    for (let seat = 0; seat < N; seat++) if (nagashiMangan(s, seat)) nagashiSeats.push(seat);
  }
  if (nagashiSeats.length > 0) {
    const MANGAN_BASE2 = 2e3;
    for (const seat of nagashiSeats) {
      const isDealer = seat === s.dealer;
      for (let i = 0; i < N; i++) {
        if (i === seat) continue;
        const owe = isDealer ? MANGAN_BASE2 * 2 : i === s.dealer ? MANGAN_BASE2 * 2 : MANGAN_BASE2;
        payments[i] -= owe;
        payments[seat] += owe;
      }
    }
    for (let i = 0; i < N; i++) s.players[i].score += payments[i];
    s.phase = "resolve";
    return {
      state: s,
      events: [{ seq: 0, event: "ryuukyoku", kind: "nagashi-mangan", tenpai: nagashiSeats, payments }]
    };
  }
  const tenpai = [];
  for (let seat = 0; seat < N; seat++) if (isTenpai(s, seat)) tenpai.push(seat);
  const noten = N - tenpai.length;
  const POT = 3e3;
  if (tenpai.length > 0 && noten > 0) {
    const loss = Math.floor(POT / noten);
    const collected = loss * noten;
    const baseGain = Math.floor(collected / tenpai.length);
    let remainder = collected - baseGain * tenpai.length;
    for (let seat = 0; seat < N; seat++) {
      if (isTenpai(s, seat)) {
        payments[seat] = baseGain + (remainder > 0 ? (remainder--, 1) : 0);
      } else {
        payments[seat] = -loss;
      }
    }
  }
  for (let i = 0; i < N; i++) s.players[i].score += payments[i];
  s.phase = "resolve";
  return {
    state: s,
    events: [{ seq: 0, event: "ryuukyoku", kind: "exhaustive", tenpai, payments }]
  };
}
function abortiveEnabled(rs, kind) {
  const bag = rs.flags.abortive;
  if (!bag) return true;
  return bag[kind] !== false;
}
function suufonRenda(s, rs) {
  if (!abortiveEnabled(rs, "suufon")) return false;
  if (!s.firstGoAround) return false;
  const firstDiscards = [];
  for (const p of s.players) {
    if (p.discards.length !== 1) return false;
    if (p.melds.length > 0) return false;
    firstDiscards.push(baseOfAka15(p.discards[0].tile));
  }
  if (firstDiscards.length < 4) return false;
  const t = firstDiscards[0];
  const k = tileKey(t);
  if (!(k.suit === 3 && k.rank <= 4)) return false;
  return firstDiscards.every((d) => d === t);
}
function suukaikan(s, rs) {
  if (!abortiveEnabled(rs, "suukaikan")) return false;
  if (s.kanCount < 4) return false;
  const kanOwners = /* @__PURE__ */ new Set();
  for (let i = 0; i < s.players.length; i++) {
    const kans = s.players[i].melds.filter((m) => m.kind === "ankan" || m.kind === "minkan" || m.kind === "shouminkan").length;
    if (kans > 0) kanOwners.add(i);
  }
  return kanOwners.size >= 2;
}
function suuchaRiichi(s, rs) {
  if (!abortiveEnabled(rs, "suucha-riichi")) return false;
  return s.players.every((p) => p.riichi?.declared);
}
function decideRenchan(s, rs, outcome) {
  const dealer = s.dealer;
  if (outcome.kind === "win") {
    const dealerWon = outcome.winners.includes(dealer);
    return {
      dealerKeeps: dealerWon,
      honbaNext: dealerWon ? s.honba + 1 : 0,
      potCarried: 0
      // pot already taken by winner
    };
  }
  if (outcome.kind === "exhaustive") {
    const dealerTenpai = outcome.tenpai.includes(dealer);
    const keep = rs.flags.renchan === "none" ? false : rs.flags.renchan === "tenpai" ? dealerTenpai : dealerTenpai;
    return { dealerKeeps: keep, honbaNext: s.honba + 1, potCarried: s.riichiPot };
  }
  if (outcome.kind === "abortive") {
    return { dealerKeeps: true, honbaNext: s.honba + 1, potCarried: s.riichiPot };
  }
  return { dealerKeeps: true, honbaNext: s.honba, potCarried: s.riichiPot };
}
function bustedSeats(s) {
  const out = [];
  for (let i = 0; i < s.players.length; i++) if (s.players[i].score < 0) out.push(i);
  return out;
}
function isGameOverByTobi(s, rs) {
  return rs.flags.tobi !== false && bustedSeats(s).length > 0;
}
function chomboPayments(s, offender) {
  const N = s.players.length;
  const pay = new Array(N).fill(0);
  const offenderIsDealer = offender === s.dealer;
  for (let i = 0; i < N; i++) {
    if (i === offender) continue;
    const amt = offenderIsDealer ? 4e3 : i === s.dealer ? 4e3 : 2e3;
    pay[i] += amt;
    pay[offender] -= amt;
  }
  return pay;
}

// src/match.ts
import { seedFromNumber } from "@mj/protocol";
function dealHand(p) {
  const players = p.rs.players;
  const s = initialState(p.rs, seedFromNumber(p.baseSeed + p.kyoku));
  s.dealer = p.dealer;
  s.turn = p.dealer;
  s.honba = p.honba;
  s.riichiPot = p.riichiPot;
  s.roundWind = p.roundWind;
  for (let i = 0; i < players; i++) s.players[i].score = p.scores[i];
  return s;
}
function finishHandOutcome(s, events, rs) {
  const players = s.players.length;
  const ev = events.find((e) => e.event === "win" || e.event === "ryuukyoku") ?? null;
  const winners = ev && ev.event === "win" ? ev.winners : [];
  const payments = ev && (ev.event === "win" ? ev.results.length > 0 : true) ? ev.event === "win" ? ev.results[0].payments : ev.event === "ryuukyoku" ? ev.payments : new Array(players).fill(0) : new Array(players).fill(0);
  const scores = s.players.map((p) => p.score);
  const outcome = ev && ev.event === "win" ? { kind: "win", winners } : ev && ev.event === "ryuukyoku" ? { kind: "exhaustive", tenpai: ev.tenpai } : { kind: "abortive" };
  const renchan = decideRenchan(s, rs, outcome);
  const gameOver = rs.flags.infiniteRiichi !== true && isGameOverByTobi(s, rs);
  return {
    kind: ev?.event === "ryuukyoku" ? "ryuukyoku" : "win",
    winners,
    payments,
    detail: ev,
    scores,
    renchan,
    gameOver
  };
}
function nextHandTransition(cur, outcome) {
  const dealer = outcome.dealerKeeps ? cur.dealer : (cur.dealer + 1) % cur.players;
  return { dealer, honba: outcome.honbaNext, potCarry: outcome.potCarried, kyoku: cur.kyoku + 1 };
}

// src/redact.ts
function redactFor(s, viewer) {
  return {
    wallCount: s.wall.length,
    deadWallCount: s.deadWall.length,
    doraIndicators: s.doraIndicators.slice(),
    // ura dora is hidden until a win reveals it; expose only when the engine has populated it AND
    // the hand is resolved. Until then send null (never the concealed indicators).
    uraDoraIndicators: s.phase === "resolve" && s.uraDoraIndicators.length > 0 ? s.uraDoraIndicators.slice() : null,
    roundWind: s.roundWind,
    dealer: s.dealer,
    turn: s.turn,
    phase: s.phase,
    honba: s.honba,
    riichiPot: s.riichiPot,
    players: s.players.map((p) => {
      const isSelf = p.seat === viewer;
      return {
        seat: p.seat,
        melds: p.melds.map((m) => ({ ...m, tiles: m.tiles.slice() })),
        discards: p.discards.map((d) => ({ ...d })),
        riichi: p.riichi ? { ...p.riichi } : null,
        score: p.score,
        seatWind: p.seatWind,
        kitaCount: p.kitaCount,
        // 花牌 is OPT-IN: only surface count + tiles when nonempty so flowers-OFF views are byte-identical
        // (both fields are .optional() in GameStateViewSchema). Flowers are public (set aside face-up).
        ...p.flowerCount > 0 ? { flowerCount: p.flowerCount } : {},
        ...p.flowers.length > 0 ? { flowers: p.flowers.slice() } : {},
        ...isSelf ? { furitenSelf: isFuriten(p), hand: p.hand.slice() } : { handCount: p.hand.length }
      };
    })
  };
}

// src/replay.ts
import { fnv1a32 as fnv1a322, stableStringify as stableStringify2 } from "@mj/protocol";
function reconstruct(replay) {
  let s = initialState(replay.ruleSet, replay.seed);
  for (const m of replay.moves) {
    const r = reduce(s, m.seat, m.intent, replay.ruleSet);
    if ("error" in r) return r;
    s = r.state;
  }
  return s;
}
function gameStateHash(s) {
  const canonical = {
    rng: s.rng,
    wall: s.wall,
    deadWall: s.deadWall,
    rinshan: s.rinshan,
    doraIndicators: s.doraIndicators,
    uraDoraIndicators: s.uraDoraIndicators,
    revealedDora: s.revealedDora,
    roundWind: s.roundWind,
    dealer: s.dealer,
    turn: s.turn,
    phase: s.phase,
    honba: s.honba,
    riichiPot: s.riichiPot,
    roundCounter: s.roundCounter,
    dealerWinStreak: s.dealerWinStreak,
    kanCount: s.kanCount,
    firstGoAround: s.firstGoAround,
    lastDiscard: s.lastDiscard,
    players: s.players.map((p) => ({
      seat: p.seat,
      hand: p.hand,
      melds: p.melds,
      discards: p.discards,
      riichi: p.riichi,
      furiten: p.furiten,
      score: p.score,
      seatWind: p.seatWind,
      kitaCount: p.kitaCount,
      nagashiAlive: p.nagashiAlive
    }))
  };
  return fnv1a322(stableStringify2(canonical));
}

// src/progression.ts
var DAN_TRAVERSAL_PLATES = {
  jakuho: { id: "plate:jakuho-traversal", kind: "plate", name: "\u96C0\u6B69\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  jakudo: { id: "plate:jakudo-traversal", kind: "plate", name: "\u96C0\u9053\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  jakusho: { id: "plate:jakusho-traversal", kind: "plate", name: "\u96C0\u5320\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  jakuryu: { id: "plate:jakuryu-traversal", kind: "plate", name: "\u96C0\u9F8D\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  jakuou: { id: "plate:jakuou-traversal", kind: "plate", name: "\u96C0\u738B\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  tenjaku: { id: "plate:tenjaku-traversal", kind: "plate", name: "\u5929\u96C0\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" }
};
var DAN_TRAVERSAL_TITLES = {
  jakuho: { id: "title:jakuho-cleared", kind: "title", name: "\u96C0\u6B69\u5352\u696D", axis: "dan" },
  jakudo: { id: "title:jakudo-cleared", kind: "title", name: "\u96C0\u9053\u5352\u696D", axis: "dan" },
  jakusho: { id: "title:jakusho-cleared", kind: "title", name: "\u96C0\u5320\u5352\u696D", axis: "dan" },
  jakuryu: { id: "title:jakuryu-cleared", kind: "title", name: "\u96C0\u9F8D\u5352\u696D", axis: "dan" },
  jakuou: { id: "title:jakuou-cleared", kind: "title", name: "\u96C0\u738B\u5352\u696D", axis: "dan" },
  tenjaku: { id: "title:tenjaku-cleared", kind: "title", name: "\u5929\u96C0\u5165\u9580", axis: "dan" }
};
var LADDER_COMPLETE_REWARDS = [
  { id: "plate:ladder-complete", kind: "plate", name: "\u5B8C\u5168\u8E0F\u7834\u30D7\u30EC\u30FC\u30C8", axis: "dan" },
  { id: "title:ladder-complete", kind: "title", name: "\u5168\u6BB5\u5236\u8987", axis: "dan" }
];
var DAN_TRAVERSAL_REWARDS = [
  ...Object.values(DAN_TRAVERSAL_PLATES),
  ...Object.values(DAN_TRAVERSAL_TITLES),
  ...LADDER_COMPLETE_REWARDS
];
var FUKUROKU_TITLES = {
  "fukuroku:first-5player-clear": { id: "title:fukuroku-first-5player", kind: "title", name: "\u4E94\u4EBA\u306E\u76C3", axis: "fukuroku" },
  "fukuroku:haru-houra": { id: "title:fukuroku-haru-houra", kind: "title", name: "\u6625\u306E\u548C\u4E86\u5E2B", axis: "fukuroku" },
  "fukuroku:shikkunshi-complete": { id: "title:fukuroku-shikkunshi", kind: "title", name: "\u56DB\u541B\u5B50\u5168\u63C3", axis: "fukuroku" },
  "fukuroku:yakuman-10kinds": { id: "title:fukuroku-yakuman-10", kind: "title", name: "\u5F79\u6E80\u5341\u7A2E\u9054\u6210", axis: "fukuroku" },
  "fukuroku:7player-1st": { id: "title:fukuroku-7player-1st", kind: "title", name: "\u5927\u5353\u306E\u9802\u70B9", axis: "fukuroku" },
  "fukuroku:jigen-all4": { id: "title:fukuroku-jigen-all4", kind: "title", name: "\u6885\u862D\u83CA\u7AF9\u306E\u4E3B", axis: "fukuroku" }
};
var TENTEN_EMBLEMS = [
  { id: "emblem:tenten-rank1", kind: "emblem", name: "\u5929\u70B91\u4F4D\u30A8\u30F3\u30D6\u30EC\u30E0", axis: "tenten", maxRank: 1 },
  // TUNABLE
  { id: "emblem:tenten-top10", kind: "emblem", name: "\u5929\u70B9\u4E0A\u4F4D10\u30A8\u30F3\u30D6\u30EC\u30E0", axis: "tenten", maxRank: 10 },
  // TUNABLE
  { id: "emblem:tenten-top100", kind: "emblem", name: "\u5929\u70B9\u4E0A\u4F4D100\u30A8\u30F3\u30D6\u30EC\u30E0", axis: "tenten", maxRank: 100 }
  // TUNABLE
];
var DAN_TIERS_ORDERED = [
  "jakuho",
  "jakudo",
  "jakusho",
  "jakuryu",
  "jakuou",
  "tenjaku"
];
function earnedDanRewards(reached, ladderComplete) {
  const ids = [];
  for (const tier of DAN_TIERS_ORDERED) {
    ids.push(DAN_TRAVERSAL_PLATES[tier].id);
    ids.push(DAN_TRAVERSAL_TITLES[tier].id);
    if (tier === reached) break;
  }
  if (ladderComplete) {
    for (const r of LADDER_COMPLETE_REWARDS) ids.push(r.id);
  }
  return ids;
}
function earnedFukurokuTitles(completedIds) {
  const ids = [];
  for (const fId of completedIds) {
    const entry2 = FUKUROKU_TITLES[fId];
    if (entry2) ids.push(entry2.id);
  }
  return ids;
}
function earnedTentenEmblem(rank) {
  if (rank === null) return null;
  for (const entry2 of TENTEN_EMBLEMS) {
    if (rank <= entry2.maxRank) return entry2.id;
  }
  return null;
}
function earnedRewards(snap) {
  const plates = [];
  const titles = [];
  const emblems = [];
  if (snap.reachedDan !== null) {
    for (const id of earnedDanRewards(snap.reachedDan, snap.ladderComplete)) {
      const isPlate = id.startsWith("plate:");
      (isPlate ? plates : titles).push(id);
    }
  }
  for (const id of earnedFukurokuTitles(snap.completedFukuroku)) {
    titles.push(id);
  }
  const emblem = earnedTentenEmblem(snap.tentenRank);
  if (emblem !== null) emblems.push(emblem);
  return { plates, titles, emblems };
}
function isRewardOfKind(kind, expected) {
  return kind === expected;
}

// src/daily.ts
var DAILY_REWARD = {
  /** 圓 grant for the FIRST match of a new day. */
  enFirst: 10,
  /** Progress-pt grant for the first match of a new day. */
  ptFirst: 5,
  /** 圓 trickle per subsequent (same-day) match. */
  enRepeat: 2,
  /** Progress-pt trickle per subsequent match. */
  ptRepeat: 1,
  /** Max additional same-day matches that yield a trickle (cap per day beyond the first). */
  repeatCapPerDay: 4,
  /** Progress-pt needed to advance one 立ち絵 step. */
  ptPerFaceStep: 100
};
function dayKeyUTC(nowMs) {
  return new Date(nowMs).toISOString().slice(0, 10);
}
function dailyPlayReward(progress, nowMs, opts) {
  const reward = opts?.reward ?? DAILY_REWARD;
  const today = dayKeyUTC(nowMs);
  const firstOfDay = progress.dayKey !== today || progress.matchesToday === 0;
  if (firstOfDay) {
    return { en: reward.enFirst, progressPt: reward.ptFirst, firstOfDay: true };
  }
  const repeatCount = progress.matchesToday - 1;
  if (repeatCount >= reward.repeatCapPerDay) {
    return { en: 0, progressPt: 0, firstOfDay: false };
  }
  return { en: reward.enRepeat, progressPt: reward.ptRepeat, firstOfDay: false };
}
function applyDailyReward(progress, nowMs, opts) {
  const reward = opts?.reward ?? DAILY_REWARD;
  const grant = dailyPlayReward(progress, nowMs, opts);
  const today = dayKeyUTC(nowMs);
  const isNewDay = progress.dayKey !== today || progress.matchesToday === 0;
  return {
    dayKey: today,
    matchesToday: isNewDay ? 1 : progress.matchesToday + 1,
    lifetimeProgressPt: progress.lifetimeProgressPt + grant.progressPt,
    en: progress.en + grant.en,
    claimedDays: isNewDay ? progress.claimedDays + 1 : progress.claimedDays
  };
}
function progressToFace(lifetimeProgressPt, opts) {
  const ptPerStep = opts?.ptPerFaceStep ?? DAILY_REWARD.ptPerFaceStep;
  const safeStep = Math.max(1, ptPerStep);
  const faceStep = Math.floor(lifetimeProgressPt / safeStep);
  const ptIntoStep = lifetimeProgressPt % safeStep;
  return { faceStep, ptIntoStep, ptForNextStep: safeStep };
}
export {
  BUILTIN_DETECTORS,
  DAILY_REWARD,
  DAN_FLOOR,
  DAN_LADDER,
  DAN_PT_4,
  DAN_TRAVERSAL_PLATES,
  DAN_TRAVERSAL_REWARDS,
  DAN_TRAVERSAL_TITLES,
  DETERMINISM_SEED_HEX,
  FUKUROKU_RANK_INDEPENDENT,
  FUKUROKU_TITLES,
  HOUNOU_COST_MULT,
  HOUNOU_MAX,
  LADDER_COMPLETE_REWARDS,
  TENTEN_EMBLEMS,
  YAKUMAN_DETECTORS,
  YAKU_CATALOG,
  YAOCHUU,
  applyDailyReward,
  applyEventFudaGrant,
  applyMissedRonFuriten,
  baseFromHanFu,
  buildWall,
  buildWinContext,
  bustedSeats,
  chiiOptions,
  chomboPayments,
  clampEventFudaGrant,
  classifySafety,
  clearTemporaryOnDraw,
  computeFu,
  countDora,
  countDoraNeighbors,
  dailyPlayReward,
  danPointDelta,
  dayKeyUTC,
  deadWallSize,
  dealHand,
  decideRenchan,
  defaultEventFudaCaps,
  defaultReliefBounds,
  detectBest,
  detectYaku,
  determinismDigest,
  doraFromIndicator,
  earnedDanRewards,
  earnedFukurokuTitles,
  earnedRewards,
  earnedTentenEmblem,
  effectiveGameLength,
  effectiveLimitMode,
  evalConstraint,
  eventFudaGrantedInWindow,
  exhaustiveDraw,
  expectedSatsuToPortrait,
  finishHandOutcome,
  finishOrder,
  formatDeltaSatsu,
  formatSatsuToPortrait,
  from34,
  gameStateHash,
  handCounts,
  hounouCost,
  hounouCostCumulative,
  hounouPlan,
  houseHold,
  initialState,
  isAllLast,
  isFuriten,
  isGameOverByTobi,
  isGenbutsu,
  isKabe,
  isRewardOfKind,
  isSuji,
  isTenpai,
  isTerminal,
  isYaochuu,
  kyokuRemaining,
  lastDiscardTile,
  legalMoves,
  makeRng,
  makeRngFromLanes,
  manganUnitLabel,
  minkanCallable,
  nagashiMangan,
  nextHandTransition,
  nukiTileSet,
  numberCopies,
  parseAgari,
  paymentMatrix,
  placementRanks,
  ponCallable,
  practiceSettleMode,
  progressToFace,
  pushScalar,
  rankedEntryPayout,
  rankedPayoutMultipliers,
  reachableYaku,
  recomputeFuriten,
  reconstruct,
  redactFor,
  reduce,
  reliefCostFor,
  reliefEligibility,
  resolveCallWindow,
  resolveWin,
  ronCallable,
  roundWindForKyoku,
  safeTilesVs,
  scheduledKyoku,
  score,
  scoreTable,
  scoreTableCell,
  settleMatch,
  settleUma,
  shanten,
  shuffle,
  sortTiles,
  steppedUnits,
  suuchaRiichi,
  suufonRenda,
  suukaikan,
  tenpaiWaits,
  tile34,
  tileKey,
  ukeire,
  visibleCounts,
  waitsForSeat
};
